import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { debounce } from 'lodash';
import { FaTrash, FaCheck, FaPlus, FaHeading } from "react-icons/fa";

import {
  Button,
  Modal,
  Typography,
  TextareaAutosize,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Accordion, AccordionDetails, AccordionSummary,
  CircularProgress
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./HseSavedReportModal.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import MultiLineCreatableSelect from "./MultiLineCreatableSelect";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import CancelIcon from "@mui/icons-material/Cancel";
import { config } from "../../config";
import axios from "../../APIs/axios";
import { toast, ToastContainer } from "react-toastify";
import ExportSavedReportPDF from "../ExportSavedReportPDF/ExportSavedReportPDF";
import ExportWordDoc from "../ExportWordDoc/ExportWordDoc";
import { getAccountDetails } from "../Services/localStorage";
import { groupReferences, orderReferences } from "../../utils/annexureGrouping";
import ExportExcel from "../ExportExcel/ExportExcel";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ImageViewerModal from "../ImageViewerModal/ImageViewerModal";
import Loader from "../Loader/Loader";
import VariantsModal from "../VariantsModal/VariantsModal";
import InfoIcon from "@mui/icons-material/Info";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CheckIcon from "@mui/icons-material/Check";
import ObservationsDrawer from "../ObservationsDrawer/ObservationsDrawer";
import DeleteIcon from "@mui/icons-material/Delete";
import JoditEditor from 'jodit-react';
import HTMLReactParser from 'html-react-parser';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Autocomplete from '@mui/material/Autocomplete';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { IoAddCircle } from "react-icons/io5";
import { compressionOptions } from "html-docx-js/dist/html-docx";
import DialogBox from "../DialogBox/DialogBox";
import Tooltips from "@mui/material/Tooltip";
import HseObservationRow from "./HseObservationRow";
import ImageCropModal from "./ImageCropModal";
import ViewHseReportSectionBasedDetails from "../ViewHseReportDetails/ViewHseReportSectionBasedDetails";

const removeDuplicates = (observations) => {
  const uniqueObservations = Array.from(
    new Map(observations.map((item) => [item.observation, item])).values()
  );
  return uniqueObservations;
};

// Number of columns rendered in the screen-10 observation table. The two
// spacer rows below span this many columns so the table grid stays aligned.
const HSE_OBS_COLUMN_COUNT = 14;

// Virtualized table for one observation group (one `tableType`). Only the rows
// in (and just around) the viewport are mounted, so a 300-1000+ row report stays
// responsive instead of mounting every row at once. The row component, its
// handlers and the data all live in the parent, so unmounting a row off-screen
// never loses edits.
//
// One useVirtualizer must run per group, and hooks can't run inside a .map(), so
// each group is its own component. The scroll element is this group's own
// `.table-scroll` TableContainer (`max-height:65vh; overflow-y:auto`). The list
// (<tbody>) sits below the column header inside that same scroll box, so the
// virtualizer is told the header's height via `scrollMargin`; getting that value
// right (and stable) is what keeps the rendered window aligned with what's on
// screen — see the measurement note below.
function HseObservationGroup({
  tableType,
  rows,
  headingId,
  tableRef,
  renderRow,
  renderTrailing,
}) {
  const tbodyRef = useRef(null);
  // The element that actually scrolls is THIS group's TableContainer
  // (`.table-scroll` is `max-height:65vh; overflow-y:auto`), not the outer
  // modal body. The virtualizer must watch it, or only the first window of
  // rows renders and the rest is reserved-but-empty space.
  const scrollContainerRef = useRef(null);
  const setScrollContainerRef = useCallback(
    (node) => {
      scrollContainerRef.current = node;
      // Keep feeding the shared tableRef so the existing close-dropdowns-on
      // -scroll handler still works.
      if (tableRef) tableRef.current = node;
    },
    [tableRef]
  );
  const [scrollMargin, setScrollMargin] = useState(0);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    // Read-only rows are lightweight now, so a low estimate is closer to reality.
    // Underestimating is the safe direction: it renders a few extra rows (cheap)
    // and avoids a blank strip at the bottom edge before real heights are
    // measured. measureElement refines each row's true height after it mounts.
    estimateSize: () => 64,
    overscan: 12,
    scrollMargin,
  });

  // The virtualizer positions rows relative to the top of the scroll container,
  // but the list (<tbody>) actually starts BELOW the column header. `scrollMargin`
  // tells it about that header offset; if it's wrong or unstable, the rendered
  // window is shifted from what's actually on screen — which is exactly what made
  // the first row vanish and rows blank out while scrolling. We read the header
  // height directly from the <thead> (this is scroll-position independent, so it
  // can't drift the way the old getBoundingClientRect math did) and keep it
  // current with a ResizeObserver (column titles can re-wrap on resize).
  useLayoutEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;
    const headEl = scrollEl.querySelector("thead");
    if (!headEl) return;
    const measure = () => {
      const h = headEl.offsetHeight;
      setScrollMargin((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headEl);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [rows.length]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop =
    virtualItems.length > 0 ? Math.max(0, virtualItems[0].start - scrollMargin) : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? Math.max(
        0,
        rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      )
      : 0;

  return (
    <TableContainer component={Paper} className="table-scroll" ref={setScrollContainerRef}>
      <Table
        aria-labelledby={headingId}
        aria-rowcount={rows.length + 1 + (renderTrailing ? 1 : 0)}
      >
        <TableHead>
          <TableRow aria-rowindex={1}>
            <TableCell scope="col">Sr. No.</TableCell>
            <TableCell scope="col">Area / Process</TableCell>
            <TableCell scope="col">Equipment</TableCell>
            <TableCell scope="col">Category</TableCell>
            <TableCell scope="col">Check Point</TableCell>
            <TableCell scope="col">Criticality</TableCell>
            <TableCell scope="col">Status</TableCell>
            <TableCell scope="col">Observation</TableCell>
            <TableCell scope="col">Recommendation</TableCell>
            <TableCell scope="col">Legal Reference (if any)</TableCell>
            <TableCell scope="col">Max Points</TableCell>
            <TableCell scope="col">Score</TableCell>
            <TableCell scope="col">Objective Evidence</TableCell>
            <TableCell scope="col">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody ref={tbodyRef}>
          {paddingTop > 0 && (
            <TableRow aria-hidden="true" style={{ height: paddingTop }}>
              <TableCell
                colSpan={HSE_OBS_COLUMN_COUNT}
                style={{ padding: 0, border: 0, height: paddingTop }}
              />
            </TableRow>
          )}
          {virtualItems.map((virtualRow) =>
            renderRow(
              rows[virtualRow.index],
              virtualRow.index,
              rowVirtualizer.measureElement
            )
          )}
          {paddingBottom > 0 && (
            <TableRow aria-hidden="true" style={{ height: paddingBottom }}>
              <TableCell
                colSpan={HSE_OBS_COLUMN_COUNT}
                style={{ padding: 0, border: 0, height: paddingBottom }}
              />
            </TableRow>
          )}
          {renderTrailing && renderTrailing(rows.length + 2)}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const HseSavedReportModal = ({
  selectedReportData,
  setOpenSavedReport,
  allData,
  reportHeader,
  getAllHseData,
  module,
  setOpenReportList,
  getAllHseReports,
  exp,
}) => {

  const safeString = (value) => {
    if (typeof value === "string") return value;
    if (value === null || value === undefined) return "";
    return String(value);
  };

  const safeReplace = (value, search, replacement = "") => {
    return safeString(value).replace(search, replacement);
  };

  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [screenNumber, setScreenNumber] = useState(exp === true ? 12 : 1);
  const [selectedDateTime, setSelectedDateTime] = useState(
    selectedReportData.date_time
  );
  const [orgList, setOrgList] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState({
    label: selectedReportData.organization,
    value: selectedReportData.org_id,
  });
  // console.log("brief",selectedReportData)
  const [selectedSite, setSelectedSite] = useState({
    label: selectedReportData.site,
    value: selectedReportData.site,
  });
  const [selectedState, setSelectedState] = useState({
    label: selectedReportData.state,
    value: selectedReportData.state,
  });
  
  const [criticalityOptionList, setCriticalityOptionList] = useState([]);
  const [statusOptionList, setStatusOptionList] = useState([]);
  const [allScores, setAllScores] = useState([]);

  const [startDate, setStartDate] = useState(selectedReportData.start_date);
  const [endDate, setEndDate] = useState(selectedReportData.end_date);
  const [siteOptions, setSiteOptions] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [hseCategoryList, setHseCategoryList] = useState([]);
  const [checkpointList, setCheckpointList] = useState([]);
  const [criticalityList, setCriticalityList] = useState([]);
  const [observationList, setObservationList] = useState([]);
  const [recommendationList, setRecommendationList] = useState([]);
  const [legalReferenceList, setLegalReferenceList] = useState([]);
  const [statusList, setStatusList] = useState([]);

  // Separate state variables for new row dropdown lists
  const [newRowAreaList, setNewRowAreaList] = useState([]);
  const [newRowEquipmentList, setNewRowEquipmentList] = useState([]);
  const [newRowHseCategoryList, setNewRowHseCategoryList] = useState([]);
  const [newRowCheckpointList, setNewRowCheckpointList] = useState([]);
  const [newRowCriticalityList, setNewRowCriticalityList] = useState([]);
  const [newRowObservationList, setNewRowObservationList] = useState([]);
  const [newRowRecommendationList, setNewRowRecommendationList] = useState([]);
  const [newRowLegalReferenceList, setNewRowLegalReferenceList] = useState([]);
  const [newRowStatusList, setNewRowStatusList] = useState([]);
  const [newObservationAdded, setNewObservationAdded] = useState(false);
  const [bulkUploadedImages, setBulkUploadedImages] = useState([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const bulkUploadedImagesRef = useRef([]);
  const [openGallery, setOpenGallery] = useState(false);
  const[currentTableType,setCurrentTableType] = useState("");
  const [viewBulkImageModal,setViewBulkImageModal] = useState(false);
  const [bulkImageUrl, setBulkImageUrl] = useState('');
  const [emptyInput, setEmptyInput] = useState([]);
  const [emptyFieldFound, setEmptyFieldFound] = useState(false)

  const handleViewBulkImage = (imgUrl) => {
    console.log('-- handleViewBulkImage imgUrl',imgUrl)
    setBulkImageUrl(imgUrl);
    setViewBulkImageModal(true);
  }

  const handleCloseViewBulkImageModal = () => {
    setViewBulkImageModal(false);
    setBulkImageUrl('');
  }

  const deleteBulkImage = () => {
    console.log('Delete from Bulk image stack', bulkImageUrl);
    let updatedBulkSet = bulkUploadedImages.filter(ftr => ftr.url != bulkImageUrl);
    console.log('updatedBulkSet: ',updatedBulkSet);
    setBulkUploadedImages(updatedBulkSet);
    handleCloseViewBulkImageModal();
  }

  const [selectedArea, setSelectedArea] = useState(
    Array.isArray(selectedReportData?.Areas) && selectedReportData.Areas.length > 0
      ? selectedReportData.Areas.map((e) => e.area)
      : ["NA"]
  );
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    selectedReportData.Categories
      ? selectedReportData.Categories.map((e) => e.category)
      : []
  );
  const [selectedSector, setSelectedSector] = useState({
    label: selectedReportData.sector_type || "",
    value: selectedReportData.sector_type || "",
  });
  // const [selectedParam, setSelectedParam] = useState(
  //   Array.from(
  //     new Set(selectedReportData.AllObservations.map((e) => e.table_type) .filter((e) => e !== null && e !== undefined && e !== ""))
  //   ).map((table_type) => ({ label: table_type, value: table_type }))
  // );
  const [selectedParam, setSelectedParam] = useState(() =>
    Array.from(
      new Set(
        (selectedReportData?.AllObservations || [])
          .map((e) => e.table_type)
          .filter((e) => e !== null && e !== undefined && e !== "")
      )
    ).map((table_type) => ({ label: table_type, value: table_type }))
  );

  // const [AllObservations, setAllObservations] = useState(
  //   selectedReportData.AllObservations
  // );
  const [AllObservations, setAllObservations] = useState(
    // selectedReportData.AllObservations
    selectedReportData?.AllObservations || []
  );
  // const [selectedObservations, setSelectedObservations] = useState(
  //   selectedReportData.AllObservations.filter((e) => e.isNew = true)
  // );
  const [selectedObservations, setSelectedObservations] = useState(() =>
    (selectedReportData?.AllObservations || []).filter((e) => e.isNew === true)
  );

  const [recommendations, setRecommendations] = useState(
    selectedReportData.Recommendations
  );
  const [criticalObservations, setCriticalObservations] = useState([]);
  const [otherDetails, setOtherDetails] = useState(
    selectedReportData.other_details
  );
  const [backgroundBrief, setBackgroundBrief] = useState(
    selectedReportData.background_brief
      ? selectedReportData.background_brief
      : reportHeader.background_brief
  );
  const [improvementOpportunityAreas, setImprovementOpportunityAreas,] = useState(
    selectedReportData.improvement_opportunity_areas
      ? selectedReportData.improvement_opportunity_areas
      : reportHeader.improvement_opportunity_areas
  );
  const [classificationOfAuditObservations, setClassificationOfAuditObservations] = useState(
    selectedReportData.classification_of_audit_observations
      ? selectedReportData.classification_of_audit_observations
      : reportHeader.classification_of_audit_observations
  );
  const [auditScoreAnalysis, setAuditScoreAnalysis] = useState(
    selectedReportData.audit_score_analysis
      ? selectedReportData.audit_score_analysis
      : reportHeader.audit_score_analysis
  );
  const [overallAssessmentIndicator, setOverallAssessmentIndicator] = useState(
    selectedReportData.overall_assessment_indicator
      ? selectedReportData.overall_assessment_indicator
      : reportHeader.overall_assessment_indicator
  );
  const [introduction, setIntroduction] = useState(
    selectedReportData.introduction
      ? selectedReportData.introduction
      : reportHeader.introduction
  );
  // const [contents, setContents] = useState(
  //   selectedReportData.contents
  //     ? selectedReportData.contents.replace("Scoring Table", "Charts")
  //     : reportHeader.contents.replace("Scoring Table", "Charts")
  // );
  const [contents, setContents] = useState(
    safeReplace(
      selectedReportData?.contents || reportHeader?.contents || "",
      "Scoring Table",
      "Charts"
    )
  );
  const [exeSummary, setExeSummary] = useState(
    selectedReportData.exe_summary
      ? selectedReportData.exe_summary
      : reportHeader.exe_summary
  );
  const [exeSummaryFindigs, setExeSummaryFindigs] = useState(
    selectedReportData.findings
      ? selectedReportData.findings
      : "<p>Findings were categorised under System, Infrastructure, Capability, and Legal to prioritise corrective actions based on criticality.</p><p>Overall, the facility demonstrates a developing HSE framework with certain compliant practices in place; however, multiple high and extreme risk gaps require immediate management intervention to ensure legal compliance, hazard control, and operational resilience.</p><ul><li>Legal: </li><li>System:</li> <li>Infrastructure:</li><li>Capability:</li></ul>"
  );
  const [exeSummaryKeyObservations, setExeSummaryKeyObservations] = useState(
    selectedReportData.key_observations
      ? selectedReportData.key_observations
      : "<p>No observation added</p>"
  );
  const [keyObservationDisplay,setKeyObservationDisplay] = useState(null);
  function convertToHtml(text) {
    // Convert inline numbered and bullet lists into separate lines
    text = text
      .replace(/\s{2,}(\d+\.\s+)/g, "\n$1")      //  1. item
      .replace(/\s{2,}([•*-]\s+)/g, "\n$1")      //  - item, * item, • item
      .replace(/\s{2,}(\.\s+)/g, "\n$1");        //  . item

    const lines = text.split(/\r?\n/);

    let html = "";
    let listType = null;

    for (let line of lines) {
      line = line.trim();

      const ordered = line.match(/^\d+\.\s+(.*)$/);
      const unordered = line.match(/^(?:\.|-|\*|•)\s+(.*)$/);

      if (ordered) {
        if (listType !== "ol") {
          if (listType) html += `</${listType}>`;
          html += "<ol>";
          listType = "ol";
        }
        html += `<li>${ordered[1]}</li>`;
      } else if (unordered) {
        if (listType !== "ul") {
          if (listType) html += `</${listType}>`;
          html += "<ul>";
          listType = "ul";
        }
        html += `<li>${unordered[1]}</li>`;
      } else {
        if (listType) {
          html += `</${listType}>`;
          listType = null;
        }

        if (line) {
          html += `<p>${line}</p>`;
        }
      }
    }

    if (listType) html += `</${listType}>`;

    return html;
  }

  const [conclusion, setConclusion] = useState(
    selectedReportData.conclusion
      ? selectedReportData.conclusion
      : reportHeader.conclusion
  );
  const [bestPractice, setbestPractice] = useState(
    selectedReportData.best_practice
      ? selectedReportData.best_practice
      : reportHeader.best_practice
  );
  const [theWayForward, setTheWayForward] = useState(
    selectedReportData.the_way_forward
      ? selectedReportData.the_way_forward
      : reportHeader.the_way_forward
  );
  const [imageUrlsByRow, setImageUrlsByRow] = useState(
    JSON.parse(selectedReportData.image_urls)
      ? JSON.parse(selectedReportData.image_urls)
      : {}
  );
  const { userId, name } = getAccountDetails();
  const [editedObservations, setEditedObservations] = useState([]);
  // const [editedRecommendations, setEditedRecommendations] = useState([]);
  // const [editedAreas, setEditedAreas] = useState([]);
  // const [editedCategories, setEditedCategories] = useState([]);
  // const [editedCriticalities, setEditedCriticalities] = useState([]);
  // const [editedIsReference, setEditedIsReference] = useState([]);
  const [isReportEdited, setIsReportEdited] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentEditedRow, setCurrentEditedRow] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState([]);
  const [scorePercent, setScorePercent] = useState(0);

  const [chartImage, setChartImage] = useState(null);
  // Warns when the Overall Assessment Indicator text exceeds the 4000-char PDF limit.
  const [charWarning, setCharWarning] = useState(false);
  const chartContainerRef = useRef(null);
  const [disableSaveNext, setDisableSaveNext] = useState(false);
  const [waitForCharts, setWaitForCharts] = useState(true);
  const regexForBackground = /\bon.*?by\b/;
  const regexForExeSummary = /\bon.*?and\b/;
  const regexForAreas = /\bentire[\s\S]*?and\b/;
  const [loading, setLoading] = useState(false);
  const [area, setArea] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [openVairantModal, setOpenVariantModal] = useState(false);
  const [observationVariants, setObservationVariants] = useState([]);
  const [editedFields, setEditedFields] = useState([]);
  const [confirmationShown, setConfirmationShown] = useState(false);
  const [facilityInfo, setFacilityInfo] = useState({});
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [paramList, setParamList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [newRowInputs, setNewRowInputs] = useState({});
  const [newRowInputValues, setNewRowInputValues] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const editor = useRef(null);
  const classificationEditor = useRef();
  const contentEditor = useRef(null);
  const introductionEditor = useRef(null);
  const exeSummaryEditor = useRef(null);
  const exeSummaryFindigsEditor = useRef(null);
  const exeSummaryKeyObservationsEditor = useRef(null);
  const bestPracticeEditor = useRef(null);
  const wayForwardPlanEditor = useRef(null);
  const conclusionEditor = useRef(null);
  const auditScoreEditor = useRef(null);
  const improvementEditor = useState(null);
  const overallAssessmentEditor = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [hasEditedCriticalObservations, setHasEditedCriticalObservations] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState({ image: null, index: null });
  // Holds the images a user just picked while they crop them, plus the row they
  // belong to. Upload only fires once the crop modal hands back the final blobs.
  const [cropState, setCropState] = useState({ open: false, files: [], target: null });
  // const [timeFrom, setTimeFrom] = useState(selectedReportData.time_of_audit_from);
  // const [timeTo, setTimeTo] = useState(selectedReportData.time_of_audit_to);
  // const [briefPropertyDescription, setBriefPropertyDescription] = useState(selectedReportData.brief_property_description);
  // const [numOfFloors, setNumOfFloors] = useState(selectedReportData.num_of_floors);
  // const [avgStaffFootfall, setAvgStaffFootfall] = useState(selectedReportData.average_staff_footfall);
  // const [noObjectionCertificate, setNoObjectionCertificate] = useState(selectedReportData.no_objection_certificate);
  // const [nationalBuildingCodeCategory, setNationalBuildingCodeCategory] = useState(selectedReportData.national_building_code_category);
  // const [coordinationgPersonClientside, setCoordinationgPersonClientside] = useState(selectedReportData.coordinating_person_clientSide);
  // const [reportPreparedBy, setReportPreparedBy] = useState(selectedReportData.report_prepared_by);
  // const [reportReviewedBy, setReportReviewedBy] = useState(selectedReportData.report_reviewed_by);
  const [timeFrom, setTimeFrom] = useState(selectedReportData?.time_of_audit_from ?? "");
  const [timeTo, setTimeTo] = useState(selectedReportData?.time_of_audit_to ?? "");
  const [briefPropertyDescription, setBriefPropertyDescription] = useState(selectedReportData?.brief_property_description ?? "");
  const [numOfFloors, setNumOfFloors] = useState(selectedReportData?.num_of_floors ?? "");
  const [avgStaffFootfall, setAvgStaffFootfall] = useState(selectedReportData?.average_staff_footfall ?? "");
  const [noObjectionCertificate, setNoObjectionCertificate] = useState(selectedReportData?.no_objection_certificate ?? "");
  const [nationalBuildingCodeCategory, setNationalBuildingCodeCategory] = useState(selectedReportData?.national_building_code_category ?? "");
  const [coordinationgPersonClientside, setCoordinationgPersonClientside] = useState(selectedReportData?.coordinating_person_clientSide ?? "");
  const [reportPreparedBy, setReportPreparedBy] = useState(selectedReportData?.report_prepared_by ?? "");
  const [reportReviewedBy, setReportReviewedBy] = useState(selectedReportData?.report_reviewed_by ?? "");
  const [buildingName, setBuildingName] = useState(selectedReportData?.building_name ?? "")
  const areaSelectRef = useRef(null);
  const criticalitySelectRef = useRef(null);
  const equipmentSelectRef = useRef(null);
  const categorySelectRef = useRef(null);
  const checkpointSelectRef = useRef(null);
  const statusSelectRef = useRef(null);
  const tableRef = useRef(null);
  const observationLookupMapsRef = useRef({
    selectedObservationMap: new Map(),
    groupedObservationIndexMap: {},
  });
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [isCriticalityDropdownOpen, setIsCriticalityDropdownOpen] = useState(false);
  const [isEquipmentDropdownOpen, setIsEquipmentDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isCheckpointDropdownOpen, setIsCheckpointDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  const editAreaSelectRef = useRef(null);
  const editCriticalitySelectRef = useRef(null);
  const editEquipmentSelectRef = useRef(null);
  const editCategorySelectRef = useRef(null);
  const editCheckpointSelectRef = useRef(null);
  const editStatusSelectRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [isEditAreaDropdownOpen, setIsEditAreaDropdownOpen] = useState(false);
  const [isEditCriticalityDropdownOpen, setIsEditCriticalityDropdownOpen] = useState(false);
  const [isEditEquipmentDropdownOpen, setIsEditEquipmentDropdownOpen] = useState(false);
  const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false);
  const [isEditCheckpointDropdownOpen, setIsEditCheckpointDropdownOpen] = useState(false);
  const [isEditStatusDropdownOpen, setIsEditStatusDropdownOpen] = useState(false);
  const [isEditObservationDropdownOpen, setIsEditObservationDropdownOpen] = useState(false);
  const [isEditRecommendationsDropdownOpen, setIsEditRecommendationsDropdownOpen] = useState(false);

  //
  const [selectedReportDataView, setSelectedReportDataView] = useState({});
  const [openViewReport, setOpenViewReport] = useState(false);
  const [openViewReport2, setOpenViewReport2] = useState(false);
  //

  // get existing observation from backend
  const [existingObservations,setExistingObservations] = useState([]);

    // Keep the ref in sync so the unmount cleanup always sees the latest list
  useEffect(() => {
    bulkUploadedImagesRef.current = bulkUploadedImages;
  }, [bulkUploadedImages]);

  // On unmount: revoke any remaining preview blobs (handles parent-driven unmount)
  useEffect(() => {
    return () => {
      bulkUploadedImagesRef.current.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, []);

   const unassignedBulkImages = useMemo(
    () => bulkUploadedImages.filter((i) => !i.assigned),
    [bulkUploadedImages]
  );


  useEffect(() => {
    const closeDropdownOnScroll = () => {
      if (isAreaDropdownOpen && areaSelectRef.current) {
        areaSelectRef.current.blur();
        setIsAreaDropdownOpen(false);
      }
      if (isCriticalityDropdownOpen && criticalitySelectRef.current) {
        criticalitySelectRef.current.blur();
        setIsCriticalityDropdownOpen(false);
      }
      if (isEquipmentDropdownOpen && equipmentSelectRef.current) {
        equipmentSelectRef.current.blur();
        setIsEquipmentDropdownOpen(false);
      }
      if (isCategoryDropdownOpen && categorySelectRef.current) {
        categorySelectRef.current.blur();
        setIsCategoryDropdownOpen(false);
      }
      if (isStatusDropdownOpen && statusSelectRef.current) {
        statusSelectRef.current.blur();
        setIsStatusDropdownOpen(false);
      }
      if (isEditAreaDropdownOpen && editAreaSelectRef.current) {
        editAreaSelectRef.current.blur();
        setIsEditAreaDropdownOpen(false);
      }
      if (isCriticalityDropdownOpen && criticalitySelectRef.current) {
        criticalitySelectRef.current.blur();
        setIsCriticalityDropdownOpen(false);
      }
      if (isEditEquipmentDropdownOpen && editEquipmentSelectRef.current) {
        editEquipmentSelectRef.current.blur();
        setIsEditEquipmentDropdownOpen(false);
      }
      if (isEditCategoryDropdownOpen && editCategorySelectRef.current) {
        editCategorySelectRef.current.blur();
        setIsEditCategoryDropdownOpen(false);
      }
      if (isEditStatusDropdownOpen && editStatusSelectRef.current) {
        editStatusSelectRef.current.blur();
        setIsEditStatusDropdownOpen(false);
      }
      if (isEditCheckpointDropdownOpen && editCheckpointSelectRef.current) {
        editCheckpointSelectRef.current.blur();
        setIsEditCheckpointDropdownOpen(false);
      }
    };

    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener("scroll", closeDropdownOnScroll);
      tableElement.addEventListener("wheel", closeDropdownOnScroll, { passive: true });
      tableElement.addEventListener("touchmove", closeDropdownOnScroll, { passive: true });
    }

    return () => {
      if (tableElement) {
        tableElement.removeEventListener("scroll", closeDropdownOnScroll);
        tableElement.removeEventListener("wheel", closeDropdownOnScroll);
        tableElement.removeEventListener("touchmove", closeDropdownOnScroll);
      }
    };
  }, [
    isAreaDropdownOpen,
    isCriticalityDropdownOpen,
    isCheckpointDropdownOpen,
    isEquipmentDropdownOpen,
    isCategoryDropdownOpen,
    isStatusDropdownOpen,
    isEditAreaDropdownOpen,
    isEditEquipmentDropdownOpen,
    isEditCategoryDropdownOpen,
    isEditStatusDropdownOpen,
    isEditCheckpointDropdownOpen,
  ]);


  useEffect(() => {
    getCriticalityStatusOption();
    getAllScoreListBySectorType();
    
    if(selectedReportData.state == null){
      setSelectedState([]);
    }

  }, [])

  const getAllScoreListBySectorType = async () => {
    try {
      const response = await axios.get(
        `${config.PATH}/api/get-score-by-sector-type?sector_type=${selectedReportData.sector_type || ''}`
      );
      if (response.data?.success && response.data?.data) {
        setAllScores(response.data.data || []);
      }

    } catch (error) {
      if (error.response?.status === 404) {


      } else {
        console.error('Error fetching Scores:', error);
      }
    }
  }

  const getCriticalityStatusOption = async () => {
    try {
      const response = await axios.get(
        `${config.PATH}/api/get-hse-criticality-status-select-options?sector_type=${selectedReportData.sector_type || ''}`
      );

      if (response.data?.success && response.data?.data) {
        setCriticalityOptionList(response.data.data.criticality_options || []);
        setStatusOptionList(response.data.data.status_options || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.warn(`Sector type "${selectedReportData.sector_type}" not found in HSE options - using empty lists`);
        setCriticalityOptionList([]);
        setStatusOptionList([]);
      } else {
        console.error('Error fetching criticality status options:', error);
      }
    }
  };

  const handleOpenImageDialog = (index, observation) => {
    setSelectedObservation({ image: observation, index });
    setOpenDialog(true);
  };

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };
  const handleCloseImageDialog = () => {
    setSelectedObservation({ image: null, index: null });
    setOpenDialog(false);
  };
  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleViewReport = async (report_id) => {
  
      try {
        const endpoint =
          module === "cmv"
            ? `${config.PATH}/api/hse-cmv-report/${report_id}`
            : `${config.PATH}/api/hse-report/${report_id}`;
        const report = await axios.get(endpoint);
        setSelectedReportDataView(report.data);
        setOpenViewReport(true);
      } catch (err) {
        console.log(err);
      }
    };

  const initializeNewRowInputs = (tableType, sectorType) => {
    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        table_type: tableType, // Fixed key for table type
        sector_type: sectorType, // Include sector type
        is_selected: 1, // Fixed value for is_selected
        area: "",
        equipment: "",
        category: "",
        check_points: "",
        observation: "",
        criticality: "",
        status: "",
        recommendations: "",
        is_reference: "",
        max_pts: "0",
        score: "0",
        system_implementation: "",
        legal_reference: "",
        // compliance_check: "",
        imageUrls: [],
      },
    }));
    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        area: "",
        equipment: "",
        category: "",
        check_points: "",
        observation: "",
        criticality: "",
        status: "",
        recommendations: "",
        legal_reference: "",
      },
    }));
  };

  useEffect(() => {
    updateBackgroundBrief();
    updateExecSummary();
    getFacilityInfo();
  }, [
    selectedOrganization,
    selectedSite,
    startDate,
    endDate,
    selectedArea,
    selectedSector,
  ]);


  // useEffect(() => {
  //   const initialSelected = selectedReportData.AllObservations.filter((e) => e.is_selected === 1);
  //   setSelectedObservations(initialSelected);

  //   const initialManual = selectedReportData.manualCriticalObservations || [];
  //   setManualCriticalObservations(initialManual);

  //   const selectedHigh = initialSelected.filter((e) => e.criticality === "High");
  //   setCriticalObservations([...selectedHigh, ...initialManual]);
  // }, [selectedReportData]);

  useEffect(() => {
    if (!selectedReportData) return;

    const allObs = selectedReportData.AllObservations || [];
    const initialSelected = allObs.filter((e) => e.is_selected === 1);


    setSelectedObservations(initialSelected);

    const initialManual = selectedReportData.manualCriticalObservations || [];
    setManualCriticalObservations(initialManual);

    const selectedHigh = initialSelected.filter((e) => e.criticality === "High");
    setCriticalObservations([...selectedHigh, ...initialManual]);
  }, [selectedReportData]);

  useEffect(() => {
    getParamList();
  }, [selectedSector]);

  const getFacilityInfo = async () => {
    const response = await axios.get(
      `${config.PATH}/api/get-facility-info/${selectedReportData.report_id}`
    );
    const data = response.data;
    // Update each field individually
    setFacilityInfo(data);
  };

  const updateBackgroundBrief = () => {
    // const updatedData = selectedReportData.background_brief
    //   .replace(
    //     selectedReportData.organization,
    //     `${selectedOrganization
    //       ? selectedOrganization.label
    //       : selectedReportData.organization
    //     }`
    //   )
    //   .replace(
    //     selectedReportData.site,
    //     `${selectedSite ? selectedSite.label : selectedReportData.site}`
    //   )
    //   .replace(
    //     regexForBackground,
    //     `on ${new Date(startDate).getDate()}-${new Date(startDate).getMonth() + 1
    //     }-${new Date(startDate).getFullYear()} and ${new Date(
    //       endDate
    //     ).getDate()}-${new Date(endDate).getMonth() + 1}-${new Date(
    //       endDate
    //     ).getFullYear()} by`
    //   );
    const updatedData = safeString(selectedReportData?.background_brief)
      .replace(
        safeString(selectedReportData?.organization),
        `${selectedOrganization
          ? selectedOrganization.label
          : selectedReportData.organization || ""
        }`
      )
      .replace(
        safeString(selectedReportData?.site),
        `${selectedSite ? selectedSite.label : selectedReportData.site || ""}`
      )
      .replace(
        regexForBackground,
        `on ${new Date(startDate).getDate()}-${new Date(startDate).getMonth() + 1
        }-${new Date(startDate).getFullYear()} and ${new Date(
          endDate
        ).getDate()}-${new Date(endDate).getMonth() + 1}-${new Date(
          endDate
        ).getFullYear()} by`
      );
    setBackgroundBrief(updatedData);
  };

  const updateExecSummary = () => {
    // const updatedData = selectedReportData.exe_summary
    //   .replace(
    //     `${selectedReportData.organization}(${selectedReportData.site})`,
    //     `${selectedOrganization
    //       ? selectedOrganization.label
    //       : selectedReportData.organization
    //     }(${selectedSite ? selectedSite.label : selectedReportData.site})`
    //   )
    //   .replace(
    //     regexForExeSummary,
    //     `on ${new Date(startDate).getDate()}-${new Date(startDate).getMonth() + 1
    //     }-${new Date(startDate).getFullYear()} till ${new Date(
    //       endDate
    //     ).getDate()}-${new Date(endDate).getMonth() + 1}-${new Date(
    //       endDate
    //     ).getFullYear()} and`
    //   )
    //   .replace(
    //     regexForAreas,
    //     `entire ${selectedArea.map((e) => e).join(", ")} and`
    //   );
    const updatedData = safeString(selectedReportData?.exe_summary)
      .replace(
        `${safeString(selectedReportData?.organization)}(${safeString(selectedReportData?.site)})`,
        `${selectedOrganization
          ? selectedOrganization.label
          : selectedReportData.organization || ""
        }(${selectedSite ? selectedSite.label : selectedReportData.site || ""})`
      )
      .replace(
        regexForExeSummary,
        `on ${new Date(startDate).getDate()}-${new Date(startDate).getMonth() + 1
        }-${new Date(startDate).getFullYear()} till ${new Date(
          endDate
        ).getDate()}-${new Date(endDate).getMonth() + 1}-${new Date(
          endDate
        ).getFullYear()} and`
      )
      .replace(
        regexForAreas,
        `entire ${selectedArea.map((e) => e).join(", ")} and`
      );
    setExeSummary(updatedData);
  };

  const fetchDataForCharts = () => {

    const observations = selectedObservations || [];

    // const totalScore =
    //   observations.length > 0
    //     ? observations
    //       .map((e) => e.score || 0)
    //       .reduce((acc, score) => acc + score, 0)
    //     : 0;

    const totalScore = observations.reduce((acc, item) => {
      return acc + parseFloat(item.score || 0);
    }, 0);
    const totalMaxPoints = observations.reduce((acc, item) => {
      return acc + parseFloat(item.max_pts || 0);
    }, 0);

    const percentage =
      totalMaxPoints > 0
        ? Math.floor((totalScore / totalMaxPoints) * 100)
        : 0;

    // const percentage = Math.floor(
    //   (totalScore / (observations.length * 5)) * 100
    // );

    setScorePercent(isNaN(percentage) ? 0 : percentage);
    setData(observations);
  };

  // const convertChartToImage = async () => {
  //   try {
  //     if (!chartContainerRef.current) {
  //       console.error("chartContainerRef is null or undefined");
  //       return;
  //     }

  //     const contentHeight = chartContainerRef.current.scrollHeight;
  //     chartContainerRef.current.style.height = `${contentHeight}px`;

  //     const canvas = await html2canvas(chartContainerRef.current, {
  //       scrollY: -window.scrollY,
  //     });

  //     chartContainerRef.current.style.height = null;

  //     const image = canvas.toDataURL("image/png");
  //     setChartImage(image);
  //   } catch (error) {
  //     console.log("Error converting div to image:", error);
  //   }
  // };

  const captureInProgressRef = useRef(false);

  const convertChartToImage = useCallback(async () => {
    if (captureInProgressRef.current) return null;

    try {
      captureInProgressRef.current = true;

      const chartElement = chartContainerRef.current;

      if (!chartElement) {
        console.log("chart ref missing");
        return null;
      }

      // Wait for ApexCharts to finish painting before snapshotting. A single
      // animation frame isn't enough — the charts mount and animate
      // asynchronously, so capturing too early produces a heading-only blank
      // Dashboard page in the PDF/Word export. Two frames plus a short settle
      // give the chart SVGs time to render.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Capture the container's FULL height/width, not just the slice visible
      // inside the scrollable modal body. Without this the charts below the
      // fold get clipped out of the snapshot, leaving only the "Dashboard"
      // heading on the exported page (the blank-page bug).
      const fullHeight = chartElement.scrollHeight;
      const fullWidth = chartElement.scrollWidth;

      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY,
        height: fullHeight,
        width: fullWidth,
        windowHeight: fullHeight,
        logging: false,
      });

      const image = canvas.toDataURL("image/png");

      console.log("chart generated", image?.length);

      return image;
    } catch (error) {
      console.log("Error converting chart:", error);
      return null;
    } finally {
      captureInProgressRef.current = false;
    }
  }, []);

  useEffect(() => {

    console.log("chartImage updated", chartImage?.length);

  }, [chartImage]);

  // useEffect(() => {
  //   if (screenNumber === 11) {
  //     fetchDataForCharts();
  //     const timeoutId = setTimeout(() => {
  //       convertChartToImage();
  //     }, 1000); // Adjust the delay as needed (e.g., 1000 milliseconds)

  //     // Clear the timeout if the component unmounts or screenNumber changes
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [screenNumber, selectedObservations, convertChartToImage]);

  // useEffect(() => {
  //   if (screenNumber !== 11) {
  //     return;
  //   }

  //   let isCancelled = false;

  //   fetchDataForCharts();

  //   const timeoutId = setTimeout(async () => {
  //     if (
  //       isCancelled ||
  //       !isMountedRef.current ||
  //       !chartContainerRef.current
  //     ) {
  //       return;
  //     }

  //     await convertChartToImage();
  //   }, 1200);

  //   return () => {
  //     isCancelled = true;
  //     clearTimeout(timeoutId);
  //   };
  // }, [screenNumber]);
  useEffect(() => {
    if (screenNumber !== 10) return;
    fetchDataForCharts();
  }, [screenNumber, selectedObservations]);
  
  // we can use copyToClipboard to copy anything currently its used in executive summary copy observation section
  useEffect(() => {
    window.copyToClipboard = async (text) => {
      try {
        if (!document.hasFocus()) {
          window.focus();
        }
        await navigator.clipboard.writeText(text);
        toast.success("Copied!");
      } catch (err) {
        console.warn("Clipboard API failed, using fallback", err);

        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";

        document.body.appendChild(textarea);
        textarea.select();

        try {
          document.execCommand("copy");
          toast.success("Copied!");
        } catch {
          toast.error("Failed to copy");
        }

        document.body.removeChild(textarea);
        toast.error("Failed to copy");
      }
    };

    return () => {
      delete window.copyToClipboard;
    };
  }, []);

  // Invalidate cached chart snapshot whenever observations change
  // (add/edit/delete) so the Dashboard chart embedded in PDF/Word
  // exports is regenerated from the latest data instead of a stale image.
  // Only invalidate while at/before the Dashboard screen (<= 11). Once the
  // user moves past it — with a fresh capture taken on leave (see handleNext) —
  // keep the snapshot so the export still has an image even when there are no
  // observations.
  useEffect(() => {
    // console.log('screen no check1', screenNumber);
    if(screenNumber === 11){
      // display all selected observations to auditor so that if he wants to add any comment for particular observation he can select from list
      let HighCriticalityObservations = AllObservations.filter((e) => e.criticality === "High").map((obs, i) =>
      `<tr>
        <td valign="top" align="center" style="padding-top:20px;border:1px dashed;">${i+1}</td>
        <td valign="top" style="border:1px dashed;">${convertToHtml(obs.observation)}</td>
        <td valign="middle" align="center" style="border:1px dashed;">
          <button
              data-copy="${encodeURIComponent(obs.observation)}" 
              onclick="window.copyToClipboard(decodeURIComponent(this.dataset.copy))"
              style="border: none; outline: none;" title="Copy Observation">
                <svg xmlns="http://w3.org" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style="cursor: pointer;">
                  <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
                </svg>
            </button>
        </td></tr>`
    )
      let keyCriticalObservation = '<p>High Severity Observations</p><table cellspacing="2" cellpadding="5" width="85%">'+HighCriticalityObservations.join('')+'</table>';
      setKeyObservationDisplay(keyCriticalObservation);
    }
    if (screenNumber <= 9) setChartImage(null);
  }, [selectedObservations, screenNumber]);

  useEffect(() => {
    if (
      screenNumber !== 9 ||
      chartImage
    ) {
      return;
    }

    const generateChart = async () => {
      const image = await convertChartToImage();

      if (image) {
        console.log("setting chart image");

        setChartImage(image);
      }
    };

    const timeout = setTimeout(generateChart, 2500);

    return () => clearTimeout(timeout);
  }, [
    screenNumber,
    data,
    scorePercent,
    convertChartToImage,
    chartImage,
  ]);

  useEffect(() => {
    setWaitForCharts(true);
    if (screenNumber === 10) {
      // Simulate loading for 3 seconds
      const loaderTimeout = setTimeout(() => {
        setWaitForCharts(false);
      }, 5000);

      // Cleanup timeout if the component unmounts or when loading is complete
      return () => clearTimeout(loaderTimeout);
    }
  }, [screenNumber]);
  const [dialog, setDialog] = useState({ open: false, message: "", title: "", accept: "", reject: "", onConfirm: null });
  const handleDialogBoxObservation = (action) => {
    if (action) {
      dialog.onConfirm(); // Execute the confirmed action (allow editing)
    }
    // setDialog((prevDialog) => ({ ...prevDialog, open: false }));
    setDialog((prevDialog) => ({
      ...prevDialog,
      open: false,
    }));
  };
  // const handleCellEdit = (e, field, originalContent, observationObj) => {
  //   // Handle the confirmation dialog
  //   const handleConfirmation = () => {
  //     if (!confirmationShown) {
  //       const confirmEdit = window.confirm(
  //         "Editing the observation field will make this a new observation set and the variant list will be updated accordingly. Do you want to continue?"
  //       );
  //       if (!confirmEdit) {
  //         e.target.textContent = originalContent; // Revert content
  //         return false;
  //       } else {
  //         setConfirmationShown(true); // User confirmed
  //         return true;
  //       }
  //     }
  //     return true;
  //   };

  //   // Show confirmation for "observation" field
  //   if (field === "observation" && !handleConfirmation()) {
  //     setIsEditing(false);
  //     return; // Exit if the user cancels the confirmation
  //   }

  //   setIsEditing(true);

  //   const updateEditedFields = (newField) => {
  //     if (!editedFields.includes(newField)) {
  //       setEditedFields((prevFields) => [...prevFields, newField]);
  //     }
  //   };

  //   let currentContent;
  //   if (field === "area" || field === "category" || field === "criticality") {
  //     currentContent = e.value;
  //   } else {
  //     currentContent = e.target.textContent;
  //   }

  //   // Validate character limits
  //   const charLimitExceeded = (limit) => {
  //     if (currentContent.length > limit) {
  //       toast.warning(`Only ${limit} characters are allowed in this field.`);
  //       setIsEditing(false);
  //       return true;
  //     }
  //     return false;
  //   };

  //   if (field === "area" || field === "category") {
  //     if (charLimitExceeded(50)) return;
  //   } else if (field === "observation" || field === "recommendations") {
  //     if (charLimitExceeded(7000)) {
  //       e.target.textContent = currentContent.substring(0, 7000);
  //       const range = document.createRange();
  //       const sel = window.getSelection();
  //       range.setStart(e.target.childNodes[0], 7000);
  //       range.collapse(true);
  //       sel.removeAllRanges();
  //       sel.addRange(range);
  //       e.preventDefault();
  //       return;
  //     }
  //   }

  //   setIsReportEdited(true);

  //   // Find the observation using either sr_no or id
  //   const observationIndex = selectedObservations.findIndex(
  //     (obs) => obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
  //   );

  //   // Check if a different row is being edited without saving the current one
  //   if (currentEditedRow !== -1 && currentEditedRow !== observationObj.sr_no && currentEditedRow !== observationObj.id) {
  //     toast.warning(
  //       "Please save changes in the currently edited row before editing another row."
  //     );
  //     return;
  //   }

  //   // Update the currentEditedRow state with the new sr_no or id
  //   setCurrentEditedRow(observationObj.sr_no || observationObj.id);

  //   let value = currentContent;

  //   const updatedObservations = [...editedObservations];
  //   // let i = updatedObservations.length ? updatedObservations.length - 1 : 0;
  //   // updatedObservations[i] = {
  //   //   ...observationObj,
  //   //   ...updatedObservations[i],
  //   //   [field]: value,
  //   //   selectedRefIndex: observationIndex,
  //   // };
  //   const existingIndex = updatedObservations.findIndex(
  //     (obs) => obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
  //   );

  //   const refIndex = AllObservations.findIndex(
  //     (obs) => obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
  //   );

  //   const newEditedObj = {
  //     ...(existingIndex !== -1 ? updatedObservations[existingIndex] : observationObj),
  //     [field]: value,
  //     selectedRefIndex: observationIndex,
  //     refIndex: refIndex,
  //   };

  //   if (existingIndex !== -1) {
  //     updatedObservations[existingIndex] = newEditedObj;
  //   } else {
  //     updatedObservations.push(newEditedObj);
  //   }


  //   setEditedObservations(updatedObservations);
  //   // console.log("editedobservations", setEditedObservations);
  //   // Check if the content has been reverted to the original
  //   const isReverted = (() => {
  //     switch (field) {
  //       case "observation":
  //       case "recommendations":
  //       case "is_reference":
  //         return e.target.textContent === originalContent;
  //       case "area":
  //       case "category":
  //       case "criticality":
  //         return e.value === originalContent;
  //       default:
  //         return false;
  //     }
  //   })();

  //   // If content is reverted, reset editing state
  //   if (isReverted) {
  //     setCurrentEditedRow(-1); // Reset currentEditedRow
  //     setIsEditing(false);
  //     setEditedObservations([]);
  //     setConfirmationShown(false); // Reset confirmation flag
  //   }

  //   // Update edited fields array based on the field edited
  //   const fieldMap = {
  //     category: "CA",
  //     check_points: "CH",
  //     observation: "O",
  //     criticality: "CR",
  //     recommendations: "R",
  //     is_reference: "I",
  //   };

  //   if (fieldMap[field]) {
  //     updateEditedFields(fieldMap[field]);
  //   }
  // };

  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    return false; // numbers (incl. 0) and booleans (incl. false) are NOT "empty"
  };

  const EMPTY_CHECK_FIELD_LABELS = {
    area: "Area",
    equipment: "Equipment",
    category: "Category",
    check_points: "Checkpoint",
    criticality: "Criticality",
    status: "Status",
    observation: "Observation",
    recommendations: "Recommendations",
  };

  // Given a row's existing emptyFields and the field(s) a single edit just
  // changed, returns the updated emptyFields list — adding a label for a
  // field that is now empty, removing it if the field now has a value. Only
  // the field(s) actually touched by this edit are considered (including
  // fields cascade-reset to "" by the same edit, e.g. clearing Area also
  // clears Equipment/Category/etc.), so editing one field never clears — or
  // sets — the highlight on an untouched sibling field.
  const withUpdatedEmptyFields = (existingEmptyFields, changedFields) => {
    const next = new Set(existingEmptyFields || []);
    Object.entries(changedFields).forEach(([key, val]) => {
      const label = EMPTY_CHECK_FIELD_LABELS[key];
      if (!label) return;
      if (isEmptyValue(val)) next.add(label);
      else next.delete(label);
    });
    return [...next];
  };

  const handleCellEdit = useCallback((e, index, field, originalContent, observationObj) => {
    console.log('-- handleCellEdit')
    const { selectedObservationMap, groupedObservationIndexMap } =
      observationLookupMapsRef.current;

    if (currentEditedRow !== -1 && currentEditedRow !== observationObj.sr_no) {
      const unsavedRow = selectedObservationMap.get(currentEditedRow);

      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex =
        groupedObservationIndexMap?.[unsavedTableType]?.[currentEditedRow] ?? -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    const updateEditedFields = (newField) => {
      if (!editedFields.includes(newField)) {
        setEditedFields((prevFields) => [...prevFields, newField]);
      }
    };

    let value = "";

    if (["area", "equipment", "category", "check_points", "criticality", "status", "observation", "recommendations", "legal_reference"].includes(field)) {
      value = e?.value ?? "";
      // Clear the input value when an option is selected
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationObj.sr_no}-${field}`]: "",
      }));
    } else if (e?.target?.value !== undefined) {
      value = e.target.value;
    } else if (e?.target?.textContent !== undefined) {
      value = e.target.textContent;
    }
    const maxLength = field === "check_points" ? 1000 : 100;
    // Skip character validation for observation, recommendations, and legal_reference fields
    if (
      field !== "observation" &&
      field !== "recommendations" &&
      field !== "legal_reference" &&
      value.length > maxLength
    ) {
      alert(`${field} cannot exceed ${maxLength} characters.`);
      return;
    }

    if (field === "area" && e) {
      setArea([e.value]);
      updateEditedFields("A");
    }

    setIsEditing(true);
    setCurrentEditedRow(observationObj.sr_no);

    setIsReportEdited(true);

    // Dependent selection logic - clear dependent fields and populate lists
    if (field === "area") {
      let calculatedScore = 0;
      let calculatedMaxPoints = 0;

      // Clear dependent fields when area changes
      const areaChangedFields = {
        area: value,
        equipment: "",
        category: "",
        check_points: "",
        criticality: "",
        status: "",
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          area: value,
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, areaChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              area: value,
              equipment: "",
              category: "",
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, areaChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateEquipmentList(value, field);
      } else {
        setEquipmentList([]);
      }
      // Reset dependent dropdown lists when area changes
      setHseCategoryList([]);
      setCheckpointList([]);
      setCriticalityList([]);
      resetDependentLists();
      return;
    }

    if (field === "equipment") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      // Clear dependent fields when equipment changes
      const currentArea = observationObj.area || "";
      const equipmentChangedFields = {
        equipment: value,
        category: "",
        check_points: "",
        criticality: "",
        status: "",
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          equipment: value,
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, equipmentChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              equipment: value,
              category: "",
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, equipmentChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateHseCategoryList(value, field, currentArea);
      } else {
        setHseCategoryList([]);
      }
      // Reset dependent dropdown lists when equipment changes
      setCheckpointList([]);
      setCriticalityList([]);
      resetDependentLists();
      return;
    }

    if (field === "category") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      // Clear dependent fields when category changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const categoryChangedFields = {
        category: value,
        check_points: "",
        criticality: "",
        status: "",
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          category: value,
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, categoryChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              category: value,
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, categoryChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateCheckpointList(value, field, currentArea, currentEquipment);
      } else {
        setCheckpointList([]);
      }
      // Reset dependent dropdown lists when category changes
      setCriticalityList([]);
      resetDependentLists();
      return;
    }

    if (field === "check_points") {
      // Clear dependent fields when checkpoint changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";

      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      const checkPointsChangedFields = {
        check_points: value,
        criticality: "",
        status: "",
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          check_points: value,
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, checkPointsChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              check_points: value,
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, checkPointsChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateCriticalityList(value, field, currentArea, currentEquipment, currentCategory);
        populateLegalReferenceList(
          currentArea,
          currentEquipment,
          currentCategory,
          value,
          null,
          null
        );
      } else {
        setCriticalityList([]);
        setLegalReferenceList([]);
      }
      setStatusList([]);
      setObservationList([]);
      setRecommendationList([]);
      return;
    }

    if (field === "criticality") {
      // Clear dependent fields when criticality changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";

      // Calculate score based on criticality and current status (if exists)
      const currentStatus = observationObj.status || "";

      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      if (value && currentStatus) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === value?.trim() &&
            score.status === currentStatus?.trim()
        );
        calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? "0";
        calculatedMaxPoints = matchingScore?.max_pts ?? "0";
      }

      const criticalityChangedFields = {
        criticality: value,
        status: "",
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          criticality: value,
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, criticalityChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              criticality: value,
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, criticalityChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateStatusList(currentArea, currentEquipment, currentCategory, value);
      } else {
        setStatusList([]);
      }
      setObservationList([]);
      setRecommendationList([]);
      setLegalReferenceList([]);
      return;
    }

    if (field === "status") {
      // Clear dependent fields when status changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";
      const currentCheckpoint = observationObj.check_points || "";
      const currentCriticality = observationObj.criticality || "";

      // Calculate score based on current criticality and new status
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      if (currentCriticality && value) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === currentCriticality?.trim() &&
            score.status === value?.trim()
        );

        calculatedScore =
          matchingScore?.earned ??
          matchingScore?.score ??
          "0";

        calculatedMaxPoints =
          matchingScore?.max_pts ??
          "0";
      }

      const statusChangedFields = {
        status: value,
        observation: "",
        recommendations: "",
      };
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          status: value,
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, statusChangedFields),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? {
              ...obs,
              status: value,
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
              emptyFields: withUpdatedEmptyFields(obs.emptyFields, statusChangedFields),
            }
            : obs
        ),
      }));
      if (value) {
        populateObservationList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
        populateRecommendationList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
        populateLegalReferenceList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
      } else {
        setObservationList([]);
        setRecommendationList([]);
        setLegalReferenceList([]);
      }
      return;
    }

    if (field === "observation") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          observation: value,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, { observation: value }),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? { ...obs, observation: value, emptyFields: withUpdatedEmptyFields(obs.emptyFields, { observation: value }) }
            : obs
        ),
      }));
      updateEditedFields("O");
      return;
    }

    if (field === "recommendations") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          recommendations: value,
          selectedRefIndex: index,
          emptyFields: withUpdatedEmptyFields(base.emptyFields, { recommendations: value }),
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? { ...obs, recommendations: value, emptyFields: withUpdatedEmptyFields(obs.emptyFields, { recommendations: value }) }
            : obs
        ),
      }));
      updateEditedFields("R");
      return;
    }

    if (field === "legal_reference") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          legal_reference: value,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => ({
        ...prev,
        [observationObj.table_type]: (prev[observationObj.table_type] || []).map((obs) =>
          obs.sr_no === observationObj.sr_no || (obs.id != null && obs.id === observationObj.id)
            ? { ...obs, legal_reference: value }
            : obs
        ),
      }));
      updateEditedFields("LR");
      return;
    }

    // Default handling for other fields
    setEditedObservations((prev) => {
      const existingIndex = prev.findIndex(
        (obs) =>
          (obs.sr_no && obs.sr_no === observationObj.sr_no)

      );

      const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;

      const updatedObs = {
        ...base,
        [field]: value,
        selectedRefIndex: index,
      };

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedObs;
        return updated;
      } else {
        return [...prev, updatedObs];
      }
    });

    switch (field) {
      case "category":
        updateEditedFields("CA");
        break;
      case "check_points":
        updateEditedFields("CH");
        break;
      case "observation":
        updateEditedFields("O");
        break;
      case "criticality":
        updateEditedFields("CR");
        break;
      case "recommendations":
        updateEditedFields("R");
        break;
      case "is_reference":
        updateEditedFields("I");
        break;
      case "legal_reference":
        updateEditedFields("LR");
        break;
      default:
        break;
    }

    return true;
  }, [currentEditedRow, editedFields]);

  // Helper to determine if a field should be disabled
  // For isNew rows: disabled unless this specific row is being edited
  // For regular rows: disabled unless this specific row is being edited
  const isFieldDisabled = (observation) => {
    if (observation.variant) {
      return true;
    }
    // All rows (both isNew and regular) are only editable when this specific row is in edit mode
    return !(isEditing && currentEditedRow === observation.sr_no);
  };

  const handleCloseGallery = () =>{
    setOpenGallery(false);
  }

  // Handler for starting edit mode on a new row (isNew rows)
  const handleEditNewRow = (observation) => {
    // Check if another row is already being edited
    if (isEditing && currentEditedRow !== -1 && currentEditedRow !== observation.sr_no) {
      const { selectedObservationMap, groupedObservationIndexMap } =
        observationLookupMapsRef.current;
      const unsavedRow = selectedObservationMap.get(currentEditedRow);
      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex =
        groupedObservationIndexMap?.[unsavedTableType]?.[currentEditedRow] ?? -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    // Populate dropdown lists based on the row's current values
    // This ensures selectable fields have proper options when editing starts
    if (observation.area) {
      populateEquipmentList(observation.area, "area");
    }
    if (observation.equipment && observation.area) {
      populateHseCategoryList(observation.equipment, "equipment", observation.area);
    }
    if (observation.category && observation.area && observation.equipment) {
      populateCheckpointList(observation.category, "category", observation.area, observation.equipment);
    }
    if (observation.check_points && observation.area && observation.equipment && observation.category) {
      populateCriticalityList(observation.check_points, "check_points", observation.area, observation.equipment, observation.category);
    }
    if (observation.area && observation.equipment && observation.category && observation.criticality) {
      populateStatusList(observation.area, observation.equipment, observation.category, observation.criticality);
    }
    if (observation.area && observation.equipment && observation.category && observation.check_points && observation.criticality && observation.status) {
      populateObservationList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
      populateRecommendationList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
      populateLegalReferenceList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
    }

    // Set editing state for this row
    setIsEditing(true);
    setCurrentEditedRow(observation.sr_no);
    setIsReportEdited(true);

    // Add to editedObservations if not already there
    setEditedObservations((prev) => {
      const existingIndex = prev.findIndex((obs) => obs.sr_no === observation.sr_no);
      if (existingIndex !== -1) {
        return prev;
      }
      return [...prev, { ...observation }];
    });
  };

  const handleVariantSelection = (selectedVariants, removedSrNos) => {
    const updatedEdited = [...editedObservations];
    const updatedSelected = [...selectedObservations];

    selectedVariants.forEach((variantObs) => {
      // Mark as selected and variant
      variantObs.variant = true;
      variantObs.is_selected = 1;

      // Add to selectedObservations if not already there
      const alreadySelected = updatedSelected.some(
        (obs) => obs.sr_no === variantObs.sr_no || (obs.id != null && obs.id === variantObs.id)
      );
      if (!alreadySelected) {
        updatedSelected.push(variantObs);
      }

      // Add to editedObservations if not already there
      const alreadyEdited = updatedEdited.some(
        (obs) => obs.sr_no === variantObs.sr_no || (obs.id != null && obs.id === variantObs.id)
      );

      if (!alreadyEdited) {
        const selectedRefIndex = updatedSelected.length - 1;
        const refIndex = AllObservations.findIndex(
          (obs) =>
            obs.sr_no === variantObs.sr_no || (obs.id != null && obs.id === variantObs.id)
        );
        updatedEdited.push({
          ...variantObs,
          selectedRefIndex,
          refIndex,
        });
      }
    });

    // If any variants were removed, clear them from selectedObservations
    const filteredSelected = updatedSelected.filter(
      (obs) => !removedSrNos.includes(obs.sr_no)
    );

    setSelectedObservations(filteredSelected);
    setEditedObservations(updatedEdited);
  };

  const populateCategoryList = async (selectedAreas) => {
    try {
      // Filter the observations based on the selected areas
      const filteredObservations = allData.data.filter((item) =>
        selectedAreas.includes(item.area)
      );

      // Extract unique categories from the filtered observations
      const categories = [
        ...new Set(filteredObservations.map((item) => item.category)),
      ];

      setCategoryList(categories);
    } catch (error) {
      console.log("An error occurred:", error);
    }
  };

  const getCriticalObservations = async () => {
    try {
      const res = await axios.get(
        module === "cmv"
          ? `${config.PATH}/api/get-hse-cmv-critical-observations/${selectedReportData.report_id}`
          : `${config.PATH}/api/get-hse-critical-observations/${selectedReportData.report_id}`
      );
      setCriticalObservations(res.data.data);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    populateCategoryList(selectedArea); // Populate the category list based on selected areas
  }, [selectedArea]);

  useEffect(() => {
    getOrgList();
    populateAreaList();
  }, [allData, selectedCategory, selectedParam]);

  useEffect(() => {
    getSitesByOrganization(
      selectedOrganization ? selectedOrganization.value : null
    );
  }, [selectedOrganization, selectedSite]);



  useEffect(() => {
    getCriticalObservations();
    const updatedCriticalObservations = criticalObservations.filter((obs) => {
      return selectedObservations.some(
        (selectedObs) =>
          selectedObs.observation === obs.observation &&
          selectedObs.report_id === obs.report_id
      );
    });

    setCriticalObservations(updatedCriticalObservations);
  }, []);

  const refreshAllObservations = async () => {
    try {
      // Fetch the updated data from the API or wherever

      const endPoint =
        module === "cmv"
          ? `${config.PATH}/api/hse-cmv-report/${selectedReportData.report_id}`
          : `${config.PATH}/api/hse-report/${selectedReportData.report_id}`;
      const report = await axios.get(endPoint);

      // console.log("enddate",endPoint);
      setAllObservations(report.data.AllObservations);
    } catch (error) {
      console.error("Error refreshing AllObservations:", error);
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderBottom: "none",
      boxShadow: "none",
      cursor: "pointer",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
  };

  const handleDateTime = (e) => {
    setSelectedDateTime(e.target.value);
  };

  const handleStateChange = (e) => {
    setSelectedState(e);
  };

  const handleOrganizationSelection = async (selectedOption) => {
    if (selectedOption && selectedOption.__isNew__ === true) {
      const isValidOrganizationName = /^[a-zA-Z0-9_-]+$/.test(
        selectedOption.label
      );

      const exceedsCharacterLimit = selectedOption.label.length > 40;

      if (!isValidOrganizationName || exceedsCharacterLimit) {
        let errorMessage = "";
        if (!isValidOrganizationName) {
          errorMessage =
            "Invalid organization name. Only hyphens ( - ), underscores ( _ ) and alphanumeric characters are allowed.";
        } else {
          errorMessage = `Organization name cannot exceed 40 characters. You have typed ${selectedOption.label.length} characters.`;
        }
        alert(errorMessage);
        return;
      }

      const payload = {
        org_name: selectedOption.label,
      };
      try {
        const response = await axios.post(
          `${config.PATH}/api/create-hse-org`,
          payload
        );
        const newOrganization = response.data; // Assuming the API returns the newly created organization object
        setSelectedOrganization({
          label: newOrganization.org_name,
          value: newOrganization.id,
        });
        setSelectedSite(null);
      } catch (error) {
        console.log("Failed to create organization:", error);
        return;
      }
    } else {
      setSelectedOrganization(selectedOption);
      setSelectedSite(null);
    }
  };

  const getSitesByOrganization = async (orgId) => {
    try {
      const response = await axios.get(
        `${config.PATH}/api/hse-organizations/${orgId}/sites`
      );
      const siteOptions = response.data.map((site) => ({
        label: site.site_name,
        value: site.site_name,
      }));
      setSiteOptions(siteOptions);
    } catch (error) {
      console.log("Error:", error.response?.data?.error || error.message);
    }
  };

  const getOrgList = async () => {
    try {
      const response = await axios.get(`${config.PATH}/api/get-hse-orgs`);
      setOrgList(response.data);
    } catch (error) {
      console.log("Error:", error.response?.data?.error || error.message);
    }
  };

  const handleSiteSelection = async (selectedOption) => {
    if (selectedOption && selectedOption.__isNew__ === true) {
      const isValidSiteName = /^[a-zA-Z0-9_-]+$/.test(selectedOption.label);

      // Check if the length exceeds 40 characters

      const exceedsCharacterLimit = selectedOption.label.length > 40;

      if (!isValidSiteName || exceedsCharacterLimit) {
        let errorMessage = "";
        if (!isValidSiteName) {
          errorMessage =
            "Invalid Site name. Only hyphens ( - ), underscores ( _ ) and alphanumeric characters are allowed.";
        } else {
          errorMessage = `Site name cannot exceed 40 characters. You have typed ${selectedOption.label.length} characters.`;
        }
        alert(errorMessage);
        return;
      }

      const payload = {
        site_name: selectedOption.label,
        org_id: selectedOrganization ? selectedOrganization.value : null,
      };
      try {
        await axios.post(`${config.PATH}/api/create-hse-site`, payload);
      } catch (error) {
        console.log("Failed to create site:", error);
        return;
      }
    }
    setSelectedSite(selectedOption);
  };

  // const populateAreaList = async () => {
  //   try {
  //     // Check if selectedSector is an object with a valid value property
  //     if (selectedSector) {
  //       // Extract values from selectedParam if it contains any
  //       const selectedParamValues = selectedParam.map((param) => param.value);

  //       // Filter and get unique areas based on selectedParam values
  //       const area = allData.data
  //         .filter((e) => selectedParamValues.includes(e.table_type))
  //         .map((e) => e.area);

  //       let uniqueAreaList = [...new Set(area)];
  //       setAreaList(uniqueAreaList);
  //     } else {
  //       // Handle the case where selectedSector is null, undefined, or does not have a value
  //       setAreaList([]);
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  const populateAreaList = async (tableType = null) => {
    try {
      let filteredData = allData?.data || [];

      // If tableType is provided, filter by table_type
      if (tableType) {
        filteredData = filteredData.filter((e) => e.table_type === tableType);
      }

      const area = filteredData.map((e) => e.area);
      let uniqueAreaList = [...new Set(area)];
      setAreaList(uniqueAreaList);

    } catch (err) {
      console.log(err);
    }
  };

  const populateEquipmentList = async (val, field) => {
    try {

      const equipment = (allData?.data || [])
        .filter((e) => e.area === val)   // filter by selected area
        .map((e) => e.equipment);                // get equipment

      let uniqueEquipmentList = [...new Set(equipment)];

      setEquipmentList(uniqueEquipmentList);

    } catch (err) {
      console.log(err);
    }
  }

  const populateHseCategoryList = async (val, field, selectedArea) => {
    try {
      const category = (allData?.data || [])
        .filter((e) => e.equipment === val && e.area === selectedArea)   // filter by selected equipment AND area
        .map((e) => e.category);                // get category

      let uniqueCategoryList = [...new Set(category)];

      setHseCategoryList(uniqueCategoryList);

    } catch (err) {
      console.log(err);
    }
  }

  const populateCheckpointList = async (val, field, selectedArea, selectedEquipment) => {
    try {
      const checkpoints = (allData?.data || [])
        .filter((e) => e.category === val && e.area === selectedArea && e.equipment === selectedEquipment)   // filter by category AND area AND equipment
        .map((e) => e.check_points);          // get check_points

      let uniqueCheckpointList = [...new Set(checkpoints)];
      setCheckpointList(uniqueCheckpointList);
    } catch (err) {
      console.log(err);
    }
  }

  const populateCriticalityList = async (val, field, selectedArea, selectedEquipment, selectedCategory) => {
    try {
      const criticality = (allData?.data || [])
        .filter((e) => e.check_points === val && e.area === selectedArea && e.equipment === selectedEquipment && e.category === selectedCategory)
        .map((e) => e.criticality);

      let uniqueCriticalityList = [...new Set(criticality)];
      setCriticalityList(uniqueCriticalityList);
    } catch (err) {
      console.log(err);
    }
  }

  const populateObservationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const observations = (allData?.data || [])
        .filter((e) => {
          let match = true;
          if (selectedArea) match = match && e.area === selectedArea;
          if (selectedEquipment) match = match && e.equipment === selectedEquipment;
          if (selectedCategory) match = match && e.category === selectedCategory;
          if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
          if (selectedCriticality) match = match && e.criticality === selectedCriticality;
          if (selectedStatus) match = match && e.status === selectedStatus;
          return match;
        })
        .map((e) => e.observation);

      let uniqueObservationList = [...new Set(observations)];
      setObservationList(uniqueObservationList);
    } catch (err) {
      console.log(err);
    }
  }

  const populateRecommendationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const recommendations = (allData?.data || [])
        .filter((e) => {
          let match = true;
          if (selectedArea) match = match && e.area === selectedArea;
          if (selectedEquipment) match = match && (e.equipment === selectedEquipment || e.equipment === selectedEquipment);
          if (selectedCategory) match = match && e.category === selectedCategory;
          if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
          if (selectedCriticality) match = match && e.criticality === selectedCriticality;
          if (selectedStatus) match = match && e.status === selectedStatus;
          return match;
        })
        .map((e) => e.recommendations);

      let uniqueRecommendationList = [...new Set(recommendations)];
      setRecommendationList(uniqueRecommendationList);
    } catch (err) {
      console.log(err);
    }
  }

  const populateLegalReferenceList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      console.log('populateLegalReferenceList called with:', { selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus });
      const legalReferences = (allData?.data || [])
        .filter((e) => {
          let match = true;
          if (selectedArea) match = match && e.area === selectedArea;
          if (selectedEquipment) match = match && (e.equipment === selectedEquipment || e.equipment === selectedEquipment);
          if (selectedCategory) match = match && e.category === selectedCategory;
          if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
          if (selectedCriticality) match = match && e.criticality === selectedCriticality;
          if (selectedStatus) match = match && e.status === selectedStatus;
          return match;
        })
        .map((e) => e.legal_reference);

      let uniqueLegalReferenceList = [...new Set(legalReferences)];
      console.log('Legal references found:', uniqueLegalReferenceList);
      setLegalReferenceList(uniqueLegalReferenceList);
    } catch (err) {
      console.log('Error in populateLegalReferenceList:', err);
    }
  }

  const populateStatusList = async (selectedArea, selectedEquipment, selectedCategory, selectedCriticality) => {
    try {
      const statuses = (allData?.data || [])
        .filter((e) => {
          let match = true;
          if (selectedArea) match = match && e.area === selectedArea;
          if (selectedEquipment) match = match && e.equipment === selectedEquipment;
          if (selectedCategory) match = match && e.category === selectedCategory;
          if (selectedCriticality) match = match && e.criticality === selectedCriticality;
          return match;
        })
        .map((e) => e.status);

      let uniqueStatusList = [...new Set(statuses)];
      setStatusList(uniqueStatusList);
    } catch (err) {
      console.log(err);
    }
  }

  // New populate functions for new row dropdown lists
  const populateNewRowAreaList = async (tableType = null) => {
    try {
      let filteredData = allData?.data || [];

      // If tableType is provided, filter by table_type
      if (tableType) {
        filteredData = filteredData.filter((e) => e.table_type === tableType);
      }

      const area = filteredData.map((e) => e.area);
      let uniqueAreaList = [...new Set(area)];
      setNewRowAreaList(uniqueAreaList);

    } catch (err) {
      console.log(err);
    }
  };

  // NOTE: These functions are now deprecated - replaced with cache-based operations in handleChangeNewRowSelect
  // Kept for reference but no longer used to avoid expensive filtering on large datasets
  /*
    const populateNewRowEquipmentList = async (val, field) => {
      try {
        const equipment = (allData?.data || [])
          .filter((e) => e.area === val)
          .map((e) => e.equipment);
  
        let uniqueEquipmentList = [...new Set(equipment)];
        setNewRowEquipmentList(uniqueEquipmentList);
  
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowHseCategoryList = async (val, field, selectedArea) => {
      try {
        const category = (allData?.data || [])
          .filter((e) => e.equipment === val && e.area === selectedArea)
          .map((e) => e.category);
  
        let uniqueCategoryList = [...new Set(category)];
        setNewRowHseCategoryList(uniqueCategoryList);
  
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowCheckpointList = async (val, field, selectedArea, selectedEquipment) => {
      try {
        const checkpoints = (allData?.data || [])
          .filter((e) => e.category === val && e.area === selectedArea && e.equipment === selectedEquipment)
          .map((e) => e.check_points);
  
        let uniqueCheckpointList = [...new Set(checkpoints)];
        setNewRowCheckpointList(uniqueCheckpointList);
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowCriticalityList = async (val, field, selectedArea, selectedEquipment, selectedCategory) => {
      try {
        const criticality = (allData?.data || [])
          .filter((e) => e.check_points === val && e.area === selectedArea && e.equipment === selectedEquipment && e.category === selectedCategory)
          .map((e) => e.criticality);
  
        let uniqueCriticalityList = [...new Set(criticality)];
        setNewRowCriticalityList(uniqueCriticalityList);
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowStatusList = async (selectedArea, selectedEquipment, selectedCategory, selectedCriticality) => {
      try {
        // Only populate if prerequisite fields are selected
        if (!selectedArea || !selectedEquipment || !selectedCategory) {
          setNewRowStatusList([]);
          return;
        }
  
        const statuses = (allData?.data || [])
          .filter((e) => {
            let match = true;
            if (selectedArea) match = match && e.area === selectedArea;
            if (selectedEquipment) match = match && e.equipment === selectedEquipment;
            if (selectedCategory) match = match && e.category === selectedCategory;
            if (selectedCriticality) match = match && e.criticality === selectedCriticality;
            return match;
          })
          .map((e) => e.status);
  
        let uniqueStatusList = [...new Set(statuses)];
        setNewRowStatusList(uniqueStatusList);
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowObservationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
      try {
        // Only populate if prerequisite fields are selected
        if (!selectedArea || !selectedEquipment || !selectedCategory || !selectedCheckpoint || !selectedCriticality || !selectedStatus) {
          setNewRowObservationList([]);
          return;
        }
  
        const observations = (allData?.data || [])
          .filter((e) => {
            let match = true;
            if (selectedArea) match = match && e.area === selectedArea;
            if (selectedEquipment) match = match && e.equipment === selectedEquipment;
            if (selectedCategory) match = match && e.category === selectedCategory;
            if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
            if (selectedCriticality) match = match && e.criticality === selectedCriticality;
            if (selectedStatus) match = match && e.status === selectedStatus;
            return match;
          })
          .map((e) => e.observation);
  
        let uniqueObservationList = [...new Set(observations)];
        setNewRowObservationList(uniqueObservationList);
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowRecommendationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
      try {
        // Only populate if prerequisite fields are selected
        if (!selectedArea || !selectedEquipment || !selectedCategory || !selectedCheckpoint || !selectedCriticality || !selectedStatus) {
          setNewRowRecommendationList([]);
          return;
        }
  
        const recommendations = (allData?.data || [])
          .filter((e) => {
            let match = true;
            if (selectedArea) match = match && e.area === selectedArea;
            if (selectedEquipment) match = match && (e.equipment === selectedEquipment || e.equipment === selectedEquipment);
            if (selectedCategory) match = match && e.category === selectedCategory;
            if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
            if (selectedCriticality) match = match && e.criticality === selectedCriticality;
            if (selectedStatus) match = match && e.status === selectedStatus;
            return match;
          })
          .map((e) => e.recommendations);
  
        let uniqueRecommendationList = [...new Set(recommendations)];
        setNewRowRecommendationList(uniqueRecommendationList);
      } catch (err) {
        console.log(err);
      }
    }
  
    const populateNewRowLegalReferenceList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
      try {
        // Only populate if prerequisite fields are selected
        if (!selectedArea || !selectedEquipment || !selectedCategory || !selectedCheckpoint || !selectedCriticality || !selectedStatus) {
          setNewRowLegalReferenceList([]);
          return;
        }
  
        const legalReferences = (allData?.data || [])
          .filter((e) => {
            let match = true;
            if (selectedArea) match = match && e.area === selectedArea;
            if (selectedEquipment) match = match && (e.equipment === selectedEquipment || e.equipment === selectedEquipment);
            if (selectedCategory) match = match && e.category === selectedCategory;
            if (selectedCheckpoint) match = match && e.check_points === selectedCheckpoint;
            if (selectedCriticality) match = match && e.criticality === selectedCriticality;
            if (selectedStatus) match = match && e.status === selectedStatus;
            return match;
          })
          .map((e) => e.legal_reference);
  
        let uniqueLegalReferenceList = [...new Set(legalReferences)];
        setNewRowLegalReferenceList(uniqueLegalReferenceList);
      } catch (err) {
        console.log(err);
      }
    }
  */

  // Pre-compute dropdown options cache for performance
  const dropdownOptionsCache = useMemo(() => {
    const data = allData?.data || [];
    const cache = {
      areas: {},
      equipment: {},
      category: {},
      checkpoints: {},
      criticality: {},
      status: {},
      observation: {},
      recommendation: {},
      legalReference: {}
    };

    // Build areas by table_type
    data.forEach(item => {
      const tableType = item.table_type || 'default';
      if (!cache.areas[tableType]) {
        cache.areas[tableType] = new Set();
      }
      if (item.area) cache.areas[tableType].add(item.area);
    });

    // Build equipment by area
    data.forEach(item => {
      const area = item.area;
      if (!cache.equipment[area]) {
        cache.equipment[area] = new Set();
      }
      if (item.equipment) cache.equipment[area].add(item.equipment);
    });

    // Build category by area and equipment
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}`;
      if (!cache.category[key]) {
        cache.category[key] = new Set();
      }
      if (item.category) cache.category[key].add(item.category);
    });

    // Build checkpoints by area, equipment, and category
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}`;
      if (!cache.checkpoints[key]) {
        cache.checkpoints[key] = new Set();
      }
      if (item.check_points) cache.checkpoints[key].add(item.check_points);
    });

    // Build criticality by area, equipment, category, and checkpoint
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}`;
      if (!cache.criticality[key]) {
        cache.criticality[key] = new Set();
      }
      if (item.criticality) cache.criticality[key].add(item.criticality);
    });

    // Build status by area, equipment, category, and criticality
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.criticality}`;
      if (!cache.status[key]) {
        cache.status[key] = new Set();
      }
      if (item.status) cache.status[key].add(item.status);
    });

    // Build observation by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.observation[key]) {
        cache.observation[key] = new Set();
      }
      if (item.observation) cache.observation[key].add(item.observation);
    });

    // Build recommendation by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.recommendation[key]) {
        cache.recommendation[key] = new Set();
      }
      if (item.recommendations) cache.recommendation[key].add(item.recommendations);
    });

    // Build legal reference by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.legalReference[key]) {
        cache.legalReference[key] = new Set();
      }
      if (item.legal_reference) cache.legalReference[key].add(item.legal_reference);
    });

    // Convert Sets to Arrays
    Object.keys(cache.areas).forEach(key => {
      cache.areas[key] = Array.from(cache.areas[key]);
    });
    Object.keys(cache.equipment).forEach(key => {
      cache.equipment[key] = Array.from(cache.equipment[key]);
    });
    Object.keys(cache.category).forEach(key => {
      cache.category[key] = Array.from(cache.category[key]);
    });
    Object.keys(cache.checkpoints).forEach(key => {
      cache.checkpoints[key] = Array.from(cache.checkpoints[key]);
    });
    Object.keys(cache.criticality).forEach(key => {
      cache.criticality[key] = Array.from(cache.criticality[key]);
    });
    Object.keys(cache.status).forEach(key => {
      cache.status[key] = Array.from(cache.status[key]);
    });
    Object.keys(cache.observation).forEach(key => {
      cache.observation[key] = Array.from(cache.observation[key]);
    });
    Object.keys(cache.recommendation).forEach(key => {
      cache.recommendation[key] = Array.from(cache.recommendation[key]);
    });
    Object.keys(cache.legalReference).forEach(key => {
      cache.legalReference[key] = Array.from(cache.legalReference[key]);
    });

    return cache;
  }, [allData]);

  // Getter functions to retrieve options from cache
  const getAreaOptions = useCallback((tableType = null) => {
    const key = tableType || 'default';
    return dropdownOptionsCache.areas[key] || [];
  }, [dropdownOptionsCache]);

  const getEquipmentOptions = useCallback((area) => {
    return dropdownOptionsCache.equipment[area] || [];
  }, [dropdownOptionsCache]);

  const getCategoryOptions = useCallback((area, equipment) => {
    const key = `${area}|${equipment}`;
    return dropdownOptionsCache.category[key] || [];
  }, [dropdownOptionsCache]);

  const getCheckpointOptions = useCallback((area, equipment, category) => {
    const key = `${area}|${equipment}|${category}`;
    return dropdownOptionsCache.checkpoints[key] || [];
  }, [dropdownOptionsCache]);

  const getCriticalityOptions = useCallback((area, equipment, category, checkpoint) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}`;
    return dropdownOptionsCache.criticality[key] || [];
  }, [dropdownOptionsCache]);

  const getStatusOptions = useCallback((area, equipment, category, criticality) => {
    const key = `${area}|${equipment}|${category}|${criticality}`;
    return dropdownOptionsCache.status[key] || [];
  }, [dropdownOptionsCache]);

  const getObservationOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.observation[key] || [];
  }, [dropdownOptionsCache]);

  const getRecommendationOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.recommendation[key] || [];
  }, [dropdownOptionsCache]);

  const getLegalReferenceOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.legalReference[key] || [];
  }, [dropdownOptionsCache]);

  // ON-DEMAND ROW OPTIONS - Compute options only for specific row, not all rows
  // Cache row option objects by hierarchy so unchanged rows keep a stable
  // reference across edits (lets HseObservationRow's memo skip them).
  const getRowOptions = useMemo(() => {
    const cache = new Map();
    return (observation) => {
      const rowOptKey = `${observation.table_type ?? ""}|${observation.area ?? ""}|${observation.equipment ?? ""}|${observation.category ?? ""}|${observation.check_points ?? ""}|${observation.criticality ?? ""}|${observation.status ?? ""}`;
      const cachedRowOpts = cache.get(rowOptKey);
      if (cachedRowOpts) return cachedRowOpts;
      const fallbackCrit = Array.isArray(criticalityOptionList) ? criticalityOptionList : [];
      const fallbackStatus = Array.isArray(statusOptionList) ? statusOptionList : [];

      let criticalityOpts = [];
      if (observation.check_points) {
        const cached = getCriticalityOptions(
          observation.area,
          observation.equipment,
          observation.category,
          observation.check_points
        );
        criticalityOpts = cached.length > 0 ? cached : fallbackCrit;
      }

      let statusOpts = [];
      if (observation.criticality) {
        const cached = getStatusOptions(
          observation.area,
          observation.equipment,
          observation.category,
          observation.criticality
        );
        statusOpts = cached.length > 0 ? cached : fallbackStatus;
      }

      let observationOpts = [];
      let recommendationOpts = [];
      let legalOpts = [];

      if (
        observation.area &&
        observation.equipment &&
        observation.category &&
        observation.check_points &&
        observation.criticality &&
        observation.status
      ) {
        observationOpts = getObservationOptions(
          observation.area,
          observation.equipment,
          observation.category,
          observation.check_points,
          observation.criticality,
          observation.status
        );

        const recRaw = getRecommendationOptions(
          observation.area,
          observation.equipment,
          observation.category,
          observation.check_points,
          observation.criticality,
          observation.status
        );
        const recTrimmed = [
          ...new Set(
            recRaw
              .map((r) => (typeof r === "string" ? r.trim() : r))
              .filter(Boolean)
          ),
        ];
        recommendationOpts = recTrimmed.length === 0 ? ["N/A"] : recTrimmed;

        const legRaw = getLegalReferenceOptions(
          observation.area,
          observation.equipment,
          observation.category,
          observation.check_points,
          observation.criticality,
          observation.status
        );
        const legTrimmed = [
          ...new Set(
            legRaw
              .map((r) => (typeof r === "string" ? r.trim() : r))
              .filter(Boolean)
          ),
        ];
        legalOpts = legTrimmed.length === 0 ? ["N/A"] : legTrimmed;
      }

      const computedRowOpts = {
        criticalityOpts,
        statusOpts,
        observationOpts,
        recommendationOpts,
        legalOpts,
      };
      cache.set(rowOptKey, computedRowOpts);
      return computedRowOpts;
    };
  }, [
    getCriticalityOptions,
    getStatusOptions,
    getObservationOptions,
    getRecommendationOptions,
    getLegalReferenceOptions,
    criticalityOptionList,
    statusOptionList,
  ]);

  const handleChangeArea = (areas) => {
    // console.log("All before ", AllObservations);
    // console.log("selecte before", selectedObservations);
    // console.log("All critical before ", criticalObservations);


    try {
      const selectedArea = areas.map((e) => e.value);
      setSelectedArea(selectedArea);

      if (selectedArea.length === 0) {
        setAllObservations([]);
        setSelectedObservations([]);

        return;
      }

      //  Combine source data
      const combinedData = [
        ...allData.data,
        ...(selectedReportData.AllObservations || []),
      ];

      //  Get newly added observations (manual rows)
      // const newObservations = AllObservations.filter((obs) => obs.isNew);
      const newObservations = [
        ...selectedObservations,
        ...AllObservations,
      ].filter((obs) => obs.isNew);
      // console.log(selectedObservations);
      // console.log(newObservations)

      //  Filter observations by selected area
      const filteredObservations = combinedData.filter((item) =>
        selectedArea.includes(item.area)
      );

      //  Merge logic with priority: edited > previously selected > base
      const mergedObservations = filteredObservations.map((obs) => {
        const edited = editedObservations.find((e) => e.sr_no === obs.sr_no);
        const existing = AllObservations.find((o) => o.sr_no === obs.sr_no);
        const selected = selectedObservations.find((s) => s.sr_no === obs.sr_no);

        return {
          ...obs,
          ...existing,
          ...edited,
          ...selectedObservations,
          // ...(selected || {}),
          is_selected: selected ? 1 : 0,
          imageUrls: selected?.imageUrls || [],
        };
      });

      //  Combine everything: merged + manually added
      const combined = [...mergedObservations, ...newObservations];

      //  Deduplicate using sr_no
      const uniqueMap = new Map();
      combined.forEach((obs) => {
        uniqueMap.set(obs.sr_no, obs); // latest entry wins
      });
      const dedupedFinalObs = Array.from(uniqueMap.values());

      //  Filter selectedObservations that still exist
      const updatedSelected = selectedObservations.filter((sel) =>
        dedupedFinalObs.some((obs) => obs.sr_no === sel.sr_no)
      );
      // Combine manually added critical observations
      const updatedCritical = [
        ...criticalObservations.filter((obs) => obs.criticality === "High" || obs.sr_no === null),
        ...manualCriticalObservations,
      ].filter(
        (obs, index, self) =>
          obs.observation?.trim() !== "" &&
          index === self.findIndex((o) => o.observation === obs.observation)
      );

      setCriticalObservations(updatedCritical);


      //  Final state updates
      setAllObservations(dedupedFinalObs);
      setSelectedObservations(updatedSelected);
      // console.log("All", AllObservations);
      // console.log("selecte", selectedObservations);
      // console.log("All critical after ", criticalObservations);
      // console.log("updatedcri", updatedCritical)

    } catch (error) {
      console.log("An error occurred in handleChangeArea:", error);
    }
  };

  const handleChangeCategory = async (cat) => {
    const selectedCat = cat.map((e) => e.value);
    setSelectedCategory(selectedCat);

    try {
      const filteredObservations = allData.data
        .concat(selectedReportData.AllObservations)
        .filter(
          (observation) =>
            selectedCat.includes(observation.category) &&
            selectedArea.includes(observation.area)
        )
        .filter((observation, index, self) => {
          const observationValue = observation.observation;

          // Check if the observationValue is unique in the array
          return (
            index ===
            self.findIndex((obs) => obs.observation === observationValue)
          );
        })
        .map((observation) => {
          const selectedObs = selectedObservations.find(
            (selectedObs) => selectedObs.observation === observation.observation
          );
          if (selectedObs) {
            return {
              ...observation,
              is_selected: 1,
              score: selectedObs.score,
              imageUrls: selectedObs.imageUrls,
            };
          }
          return observation;
        });

      setAllObservations(filteredObservations);
      setSelectedObservations(
        filteredObservations.filter((e) => e.is_selected === 1)
      );
      // Check if any observations in selectedObservations are related to removed categories
      const removedCategories = selectedObservations.filter(
        (selectedObs) => !selectedCat.includes(selectedObs.category)
      );
      if (removedCategories.length > 0) {
        // Uncheck these observations
        const updatedSelectedObservations = selectedObservations.filter(
          (selectedObs) =>
            !removedCategories.some(
              (removedCat) => removedCat.sr_no === selectedObs.sr_no
            )
        );
        setSelectedObservations(updatedSelectedObservations);
      }
    } catch (error) {
      console.log("Error:", error.response?.data?.error || error.message);
    }
  };


  // const handleObservationSelection = (observation, index) => {
  //   const tempObs = [...AllObservations];

  //   let isAlreadySelected = observation.is_selected;

  //   if (isAlreadySelected) {
  //     delete tempObs[index].is_selected;
  //     setSelectedObservations(
  //       [...selectedObservations].filter(
  //         (e) =>
  //           `${e.sr_no || e.id}` !== `${observation.sr_no || observation.id}` &&
  //           e.observation !== observation.observation
  //       )
  //     );
  //     setCriticalObservations(
  //       [...selectedObservations].filter(
  //         (e) =>
  //           e.criticality === "High" &&
  //           `${e.sr_no || e.id}` !== `${observation.sr_no || observation.id}` &&
  //           e.observation !== observation.observation
  //       )
  //     );
  //   } else {
  //     tempObs[index] = { ...observation, is_selected: 1 };
  //     setSelectedObservations([
  //       ...selectedObservations,
  //       { ...observation, refIndex: index },
  //     ]);
  //     setCriticalObservations(
  //       [...selectedObservations, observation].filter(
  //         (e) => e.criticality === "High"
  //       )
  //     );
  //   }
  //   setAllObservations(tempObs);
  // };





  const handleObservationSelection = (observation, index) => {
    const tempObs = [...AllObservations];
    const isAlreadySelected = observation.is_selected;

    // Toggle is_selected
    if (isAlreadySelected) {
      delete tempObs[index].is_selected;
    } else {
      tempObs[index] = { ...observation, is_selected: 1 };
    }

    setAllObservations(tempObs);

    // Update selectedObservations list
    const updatedSelectedObservations = isAlreadySelected
      ? selectedObservations.filter(
        (e) =>
          `${e.sr_no || e.id}` !== `${observation.sr_no || observation.id}` &&
          e.observation !== observation.observation
      )
      : [...selectedObservations, { ...observation, refIndex: index }];

    setSelectedObservations(updatedSelectedObservations);

    // Filter for high critical observations only from selected
    const selectedHighCritical = updatedSelectedObservations.filter(
      (e) => e.criticality === "High"
    );

    // Deduplicate merged criticals: manual + edited + selected-high
    const editedMap = new Map();

    // Add existing edited or manually added criticals
    [...criticalObservations, ...manualCriticalObservations].forEach((obs) => {
      const key = `${obs.sr_no || obs.id || obs.observation}`.trim();
      editedMap.set(key, obs);
    });

    // Merge selected high critical observations (give priority to manually edited ones)
    selectedHighCritical.forEach((obs) => {
      const key = `${obs.sr_no || obs.id || obs.observation}`.trim();
      if (!editedMap.has(key)) {
        editedMap.set(key, obs);
      }
    });

    // Final merged critical observations
    const finalCriticals = Array.from(editedMap.values());

    setCriticalObservations(finalCriticals);
    setHasEditedCriticalObservations(true);
    // console.log("obs critical", criticalObservations)
  };


  const handleProceed = async () => {
    console.log('------ handleProceed');

    try {
      // setLoading(true)
      // Make API call with selectedObservations as input
      const payload = {
        observations: selectedObservations.map((e) => e.observation),
        // observations: selectedObservations.map((e) => e.row_id),
        report_id: selectedReportData.report_id,
      };

      // let res = await axios.post(
      //   `${config.PATH}/api/hse-recommendations`,
      //   payload
      // );
      // const receivedRecommendations = res.data.data;
      // setRecommendations(res.data.data);
      const reportData = {
        report_id: selectedReportData.report_id,
        user_id: userId,
        date_time: selectedDateTime,
        organization: selectedOrganization
          ? selectedOrganization.label
          : selectedOrganization,
        org_id: selectedOrganization
          ? selectedOrganization.value
          : selectedOrganization,
        site: selectedSite ? selectedSite.label : selectedSite,
        state:selectedState ? selectedState.label : null,
        area: selectedArea,
        category: selectedCategory,
        background_brief: backgroundBrief,
        audit_score_analysis: auditScoreAnalysis,
        classification_of_audit_observations: classificationOfAuditObservations,
        improvement_opportunity_areas: improvementOpportunityAreas,
        overall_assessment_indicator: overallAssessmentIndicator,
        introduction: introduction,
        contents: contents,
        exe_summary: exeSummary,
        conclusion: conclusion,
        best_practice: bestPractice,
        the_way_forward: theWayForward,
        image_urls: JSON.stringify(imageUrlsByRow),
        is_edited: isReportEdited,
        start_date: startDate,
        end_date: endDate,
        is_saved: true,
        time_of_audit_from: timeFrom || null,
        time_of_audit_to: timeTo || null,
        brief_property_description: briefPropertyDescription || null,
        num_of_floors: numOfFloors || null,
        average_staff_footfall: avgStaffFootfall || null,
        no_objection_certificate: noObjectionCertificate || null,
        national_building_code_category: nationalBuildingCodeCategory || null,
        coordinating_person_clientSide: coordinationgPersonClientside || null,
        report_prepared_by: reportPreparedBy || null,
        report_reviewed_by: reportReviewedBy || null,
        sector_type: selectedSector?.value || null,
        building_name: buildingName || null
      };
      if (module === "cmv") {
        reportData.type = "cmv";
      } else {
        reportData.type = "primary";
      }
      console.log('reportData: ',reportData);
      handleNext();

      const cmvEndPOint = `${config.PATH}/api/save-update-hse-cmv-report`;
      const reportEndPoint = `${config.PATH}/api/save-update-hse-report`;

      if (module === "cmv") {
        await axios.post(cmvEndPOint, reportData);
      } else {
        await axios.post(reportEndPoint, reportData);
      }

      const observationsData = {
        report_id: selectedReportData.report_id,
        // observation: selectedObservations,
        // critical_observations: criticalObservations,
        all_observations: AllObservations || [],
        organization: selectedOrganization.label,
        site: selectedSite.label,
        user_id: userId,
      };

      const observationEndPoint = `${config.PATH}/api/save-update-hse-observations`;
      const cmvObservationEndPoint = `${config.PATH}/api/save-update-hse-cmv-observations`;

      if (module === "cmv") {
        await axios.post(cmvObservationEndPoint, observationsData);
      } else {
        await axios.post(observationEndPoint, observationsData);
      }
    } catch (error) {
      console.log("Error:", error.response?.data?.error || error.message);
    }
  };

  const keyNotToCheck = [
    "remarks",
    "score",
    "refIndex",
    "selectedRefIndex",
    "equipment",
    "is_selected",
    "imageUrls",
    "edited_fields",
    "status",
  ];
  useEffect(() => {
    // Update refIndex for initially selected observations
    setSelectedObservations((prevSelectedObservations) =>
      prevSelectedObservations.map((obs) => {
        let indexInAllObservations;
        if (obs.id) {
          indexInAllObservations = AllObservations.findIndex(
            (allObs) => allObs.id === obs.id
          );
        } else if (obs.sr_no) {
          indexInAllObservations = AllObservations.findIndex(
            (allObs) => allObs.sr_no === obs.sr_no
          );
        }
        return {
          ...obs,
          refIndex:
            indexInAllObservations !== -1 ? indexInAllObservations : null,
        };
      })
    );
  }, [AllObservations]);

  const updateOrgReportStatus = () => {
    try {
      const org_id = selectedOrganization.value;
      axios.post(`${config.PATH}/api/update-org-report-status/${org_id}`);
    } catch (error) {
      console.log(error.message);
    }
  };

  const updateSiteReportStatus = () => {
    try {
      const org_id = selectedOrganization.value;
      const site_name = selectedSite.value;
      axios.post(
        `${config.PATH}/api/update-site-report-status/${org_id}/${site_name}`
      );
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleComplete = async () => {
    if (!isSaved) {
      toast.warning("Please save the report before marking as complete.");
      return;
    }
    const userConfirmed = window.confirm(
      `Are you sure you want to mark this report as complete? Once done action cannot be reversed and you cannot edit the report.`
    );

    if (!userConfirmed) {
      return;
    }
    setIsComplete(true);
    handleSave(true);
    closeReport();
    setOpenReportList(false);
    getAllHseReports();
  };

  const handleSave = async (complete) => {
    // console.log("global",bestPractice)
    if (otherDetails.trim()) {
      toast.warning("Please add the Other critical observation before proceeding.");
      return;
    }

    const hasEmptyObservation = criticalObservations.some(obs => obs.observation.trim() === "");
    if (hasEmptyObservation) {
      toast.warning("Critical observation cannot be empty!");
      return;
    }
    try {
      setLoading(true);
      // console.log("allobservation",AllObservations);

      let updatedSelectedObservations = [...selectedObservations];
      let someData = AllObservations ? [...AllObservations] : [];
      let updatedAllObservations = someData;
      const regroupByArea = (rows) => {
        const grouped = [];
        const areaSet = Array.from(new Set(rows.map(r => r.area)));
        areaSet.forEach(area => {
          grouped.push(...rows.filter(r => r.area === area));
        });
        return grouped;
      };

      if (isEditing && currentEditedRow !== -1) {
        const rowStillExists = selectedObservations.some(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        );
        if (rowStillExists) {
          const unsavedRow = selectedObservations.find(
            (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
          );
          const unsavedTableType = unsavedRow?.table_type || "";
          const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
            ? safeGroupedObservations[unsavedTableType].findIndex(
              (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
            )
            : -1;
          const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

          toast.error(
            unsavedSrNo
              ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before saving the report.`
              : `You have an unsaved observation row in "${unsavedTableType}". Please save it before saving the report.`
          );
          setLoading(false);
          return;
        }
        setIsEditing(false);
        setCurrentEditedRow(-1);
        setEditedObservations([]);
      }

      // check if anything added in new row
      if (Object.keys(newRowInputs).length) {
        for (const newRowTableType of Object.keys(newRowInputs)) {
          const row = newRowInputs[newRowTableType];
          
          if (row?.area?.length || row?.category?.length) {
            setLoading(false);
            toast.warning("Please save new observation");
            return; // This will now successfully exit your entire function
          }
        }
      }


      if (editedObservations[0]) {
        // Trim observations
        const trimmedObservations = editedObservations.map((obj) => {
          const trimmedObj = { ...obj };
          // Adjust the keys below to match the fields that need trimming
          if (trimmedObj.observation) trimmedObj.observation = trimmedObj.observation.trim();
          // Add any other fields to trim here
          return trimmedObj;
        });


        // trimmedObservations.forEach((obj) => {
        //   updatedSelectedObservations[obj.selectedRefIndex] = obj;
        //   updatedAllObservations[obj.refIndex] = { ...obj, is_selected: 1 };
        // });
        trimmedObservations.forEach((obj) => {
          const srNoOrId = obj.sr_no || obj.id;

          // Update selectedObservations
          const selIndex = updatedSelectedObservations.findIndex(
            (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
          );
          if (selIndex !== -1) {
            updatedSelectedObservations[selIndex] = obj;
          }

          // Update AllObservations
          const allIndex = updatedAllObservations.findIndex(
            (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
          );
          if (allIndex !== -1) {
            updatedAllObservations[allIndex] = { ...obj, is_selected: 1 };
          }
        });


        // if (isReportEdited) {
        //   const payload = trimmedObservations
        //     .map(({ sr_no, score, edited_fields, ...rest }) => ({
        //       ...rest,
        //       edited_fields: editedFields,
        //     }))
        //     .filter((item) => Object.keys(item).length > 0);

        //   if (payload.length > 0) {
        //     await axios.post(`${config.PATH}/api/insert-new-hse-row`, payload);
        //   }
        // }

        // console.log("setselectedobservations", setSelectedObservations);
        // console.log("setAllobs", setAllObservations);
      }

      updatedSelectedObservations = regroupByArea(updatedSelectedObservations);
      updatedAllObservations = regroupByArea(updatedAllObservations)
      requestAnimationFrame(() => {
        setSelectedObservations(updatedSelectedObservations);
        setAllObservations(updatedAllObservations);
      });
      const reportData = {
        report_id: selectedReportData.report_id,
        user_id: userId,
        date_time: selectedDateTime,
        organization: selectedOrganization?.label || selectedOrganization,
        org_id: selectedOrganization?.value || selectedOrganization,
        site: selectedSite?.label || selectedSite,
        area: selectedArea,
        category: selectedCategory,
        background_brief: backgroundBrief,
        audit_score_analysis: auditScoreAnalysis,
        classification_of_audit_observations: classificationOfAuditObservations,
        improvement_opportunity_areas: improvementOpportunityAreas,
        overall_assessment_indicator: overallAssessmentIndicator,
        contents: contents,
        introduction: introduction,
        exe_summary: exeSummary,
        findings:exeSummaryFindigs,
        key_observations:exeSummaryKeyObservations || null,
        conclusion: conclusion,
        best_practice: bestPractice,
        the_way_forward: theWayForward,
        image_urls: JSON.stringify(imageUrlsByRow),
        is_edited: isReportEdited,
        is_complete: complete === true,
        start_date: startDate,
        end_date: endDate,
        is_saved: true,
        time_of_audit_from: timeFrom || null,
        time_of_audit_to: timeTo || null,
        brief_property_description: briefPropertyDescription || null,
        num_of_floors: numOfFloors || null,
        average_staff_footfall: avgStaffFootfall || null,
        no_objection_certificate: noObjectionCertificate || null,
        national_building_code_category: nationalBuildingCodeCategory || null,
        coordinating_person_clientSide: coordinationgPersonClientside || null,
        report_prepared_by: reportPreparedBy || null,
        report_reviewed_by: reportReviewedBy || null,
        sector_type: selectedSector?.value || null,
        type: module === "cmv" ? "cmv" : "primary",
      };

      console.log('reportData: ',reportData);

      const cmvEndPOint = `${config.PATH}/api/save-update-hse-cmv-report`;
      const reportEndPoint = `${config.PATH}/api/save-update-hse-report`;

      await axios.post(module === "cmv" ? cmvEndPOint : reportEndPoint, reportData);

      const observationsData = {
        report_id: selectedReportData.report_id,
        all_observations: updatedAllObservations.map(
          ({
            tempPreview,
            largeBase64,
            cachedImages,
            ...rest
          }) => rest
        ),
        organization: selectedOrganization.label,
        site: selectedSite.label,
        user_id: userId,
      };

      const observationEndPoint = `${config.PATH}/api/save-update-hse-observations`;
      const cmvObservationEndPoint = `${config.PATH}/api/save-update-hse-cmv-observations`;

      await axios.post(module === "cmv" ? cmvObservationEndPoint : observationEndPoint, observationsData);

      // setSelectedObservations(updatedSelectedObservations);
      // setAllObservations(updatedAllObservations);
      setIsSaved(true);
      setEditedObservations([]);
      setCurrentEditedRow(-1);
      setIsEditing(false);
      setDisableSaveNext(false);
      // refreshAllObservations();
      getAllHseData();
      saveCriticalObservations(complete);
      setExeSummaryFindigs(exeSummaryFindigs)
      setExeSummaryKeyObservations(exeSummaryKeyObservations);
      saveFacilityInfo();
      setLoading(false);
      updateOrgReportStatus();
      updateSiteReportStatus();
      setEditedFields([]);
      setConfirmationShown(false);
      toast.success(`${complete === true ? "Report Completed and Saved" : "Report Saved"}`);
      // console.log("updatedSelectedObservations after", selectedObservations);
      // console.log("updateAllObservation", updatedAllObservations)
    } catch (err) {
      // console.log("error in saving :", err);
      toast.error("Failed to save report. Please try again.", err);
      // setLoading(false); // Add this line here to reset loading in case of an error
    }
  };

  const saveCriticalObservations = async (complete) => {
    try {
      // Create a new array with objects excluding the 'id' field
      const observationsToSave = criticalObservations.map(
        ({ id, ...observation }) => observation
      );


      const payload = {
        criticalObservations: observationsToSave,
        report_id: selectedReportData.report_id,
      };

      //   const endpoint = complete===true
      // ? `${config.PATH}/api/save-critical-hse-cmv-observations`
      // : `${config.PATH}/api/save-hse-critical-observations`;

      //   await axios.post(endpoint,payload);

      const criticalObsEndPoint = `${config.PATH}/api/save-hse-critical-observations`;
      const cmvCriticalObsEndPoint = `${config.PATH}/api/save-critical-hse-cmv-observations`;

      if (module === "cmv") {
        await axios.post(cmvCriticalObsEndPoint, payload);
      } else if (complete === true && module !== "cmv") {
        await axios.post(cmvCriticalObsEndPoint, payload);
        await axios.post(criticalObsEndPoint, payload);
      } else {
        await axios.post(criticalObsEndPoint, payload);
      }

      // console.log("Critical observations saved successfully.");
    } catch (error) {
      console.error("Error saving critical observations:", error.message);
    }
  };

  const handleChange = (e, name) => {
    setIsSaved(false);
    if (name === "background") {
      setBackgroundBrief(e.target.value);
    } else if (name === "contents") {
      setContents(e.target.value);
    }
    else if (name === "classification") {
      setClassificationOfAuditObservations(e.target.value);
    }
    else if (name === "exe") {
      setExeSummary(e.target.value);
    } else if (name === "conclusion") {
      setConclusion(e.target.value);
    } else if (name === "other details") {
      setOtherDetails(e.target.value);
    } else if (name === "best_practice") {
      setbestPractice(e.target.value);
    } else if (name === "the_way_forward") {
      setTheWayForward(e.target.value);
    } else if (name === "intro") {
      setIntroduction(e.target.value);
    }
  };

  const handleNext = async () => {

    const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());
    if (unsavedCritical) {
      toast.warning("Please save the critical observations before proceeding.");
      return;
    }
    if (otherDetails.trim()) {
      toast.warning("Please add the Other critical observation before proceeding.");
      return;
    }

    if (isEditing) {
      toast.warning("Please save changes before proceeding.");
      return;
    }
    // Capture a fresh Dashboard snapshot while screen 11's chart container is
    // still mounted, so the PDF/Word exports always receive a chartImage —
    // even when there are no observations.
    if (screenNumber === 10) {
      const image = await convertChartToImage();
      if (image) setChartImage(image);
    }
    setScreenNumber(screenNumber + 1);

    if(screenNumber === 9){
      setBulkUploadedImages([]);
      console.log('--- empty Bulk Upload stack ---');
    }
    // if (screenNumber === 10) {
    //   setScreenNumber(screenNumber + 1);
    // } else {
    //   setScreenNumber(screenNumber + 1);
    // }
    // setOpenReportList(false);
  };

  const handlePrev = () => {
    const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());

    if (unsavedCritical) {
      toast.warning("Please save the critical observations before going back.");
      return;
    }
    if (otherDetails.trim()) {
      toast.warning("Please add the Other critical observation before going back.");
      return;
    }
    if (isEditing) {
      toast.warning("Please save changes before going back.");
      return;
    }
    setScreenNumber(screenNumber - 1);
    // if (screenNumber === 13) {
    //   setScreenNumber(screenNumber - 1);
    // } else {
    //   setScreenNumber(screenNumber - 1);
    // }
    if(screenNumber === 9){
      setBulkUploadedImages([]);
      console.log('--- empty Bulk Upload stack ---');
    }
  };

  const removeItem = (index) => {
    const updatedObservations = [...criticalObservations];
    updatedObservations.splice(index, 1);
    setCriticalObservations(updatedObservations);
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      // SVG is vector: rasterising it to JPEG loses quality, and an SVG with
      // <foreignObject> taints the canvas so toBlob throws. Upload it as-is.
      if (file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "")) {
        resolve(file);
        return;
      }
      const img = new Image();

      img.onload = () => {
        const MAX_WIDTH = 800; // Maximum width for the compressed image
        const MAX_HEIGHT = 800; // Maximum height for the compressed image

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.7); // 0.7 is the image quality (0.0 - 1.0)
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // const handleImageUpload = async (table_type, identifier, area, files) => {
  //   // Create deep copies to avoid mutation
  //   const selectedObsCopy = [...selectedObservations];
  //   const obsCopy = [...AllObservations];
  //   const editedObsCopy = [...editedObservations];

  //   // Ensure the correct structure for deep copying
  //   selectedObsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   obsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   editedObsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   try {
  //     setLoading(true);

  //     for (const file of Array.from(files)) {
  //       if (!file.type.startsWith("image/")) {
  //         throw new Error("Only image files are allowed.");
  //       }

  //       const compressedImage = await compressImage(file);

  //       const formData = new FormData();
  //       formData.append("image", compressedImage);

  //       const response = await axios.post(
  //         `${config.PATH}/api/upload/image`,
  //         formData,
  //         {
  //           headers: {
  //             "Content-Type": "multipart/form-data",
  //           },
  //         }
  //       );

  //       const imageUrl = response.data.imageUrl;

  //       // Update the imageUrls in selectedObservations
  //       const selectedObs = selectedObsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier) &&
  //           item.area === area
  //       );
  //       if (selectedObs) {
  //         selectedObs.imageUrls = [...(selectedObs.imageUrls || []), imageUrl];
  //       }

  //       // Update the imageUrls in AllObservations
  //       const obs = obsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier) &&
  //           item.area === area
  //       );
  //       if (obs) {
  //         obs.imageUrls = [...(obs.imageUrls || []), imageUrl];
  //       }

  //       // Update editedObservations if it has values
  //       const editedObs = editedObsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier) &&
  //           item.area === area
  //       );
  //       if (editedObs) {
  //         editedObs.imageUrls = [...(editedObs.imageUrls || []), imageUrl];
  //       }
  //     }

  //     setSelectedObservations(selectedObsCopy);
  //     setAllObservations(obsCopy);

  //     // Update editedObservations only if it has values
  //     if (editedObsCopy.length > 0) {
  //       setEditedObservations(editedObsCopy);
  //     }

  //     setLoading(false);
  //     toast.success("Images uploaded successfully!");
  //   } catch (error) {
  //     console.log("Error uploading image:", error);
  //     toast.error(error.message);
  //   }
  // };
  // The row's file input calls this first. We validate, then open the crop modal
  // instead of uploading straight away — the actual upload (handleImageUpload)
  // runs on the cropped blobs the modal returns.
  const handleImagePick = (table_type, identifier, area, fileList) => {
    const picked = Array.from(fileList || []);
    const extOf = (name = "") => name.split(".").pop().toLowerCase();

    // iPhone HEIC/TIFF photos aren't supported. We detect by extension as well as
    // type because Chrome/Firefox report an empty file.type for HEIC, so a MIME
    // check alone would miss them. These get the on-screen note, not an upload.
    const isUnsupported = (f) =>
      /heic|heif|tiff?/i.test(f.type) ||
      ["heic", "heif", "tif", "tiff"].includes(extOf(f.name));

    // Supported formats: JPEG and PNG only. SVG is intentionally blocked.
    const isSupported = (f) =>
      ["image/jpeg", "image/png"].includes(f.type) ||
      ["jpg", "jpeg", "png"].includes(extOf(f.name));

    picked.filter(isUnsupported).forEach((f) =>
      toast.error(
        `${f.name} can't be used here. iPhone HEIC/TIFF images aren't supported — please upload a JPEG or PNG instead (on iPhone: Settings → Camera → Formats → Most Compatible).`
      )
    );

    const files = picked.filter((f) => isSupported(f) && !isUnsupported(f));
    if (files.length === 0) {
      // Only show the generic message when nothing already got the HEIC/TIFF note.
      if (!picked.some(isUnsupported)) {
        toast.error("Only JPEG or PNG images are allowed.");
      }
      return;
    }
    setCropState({ open: true, files, target: { table_type, identifier, area } });
  };

     // The row's file input calls this first. We validate, then open the crop modal
  // instead of uploading straight away — the actual upload (handleImageUpload)
  // runs on the cropped blobs the modal returns.
  const handleBulkImagePick = async (fileList) => {
    console.log('--- handleBulkImagePick --');
    const picked = Array.from(fileList || []);
    const extOf = (name = "") => name.split(".").pop().toLowerCase();

    // iPhone HEIC/TIFF photos aren't supported. We detect by extension as well as
    // type because Chrome/Firefox report an empty file.type for HEIC, so a MIME
    // check alone would miss them. These get the on-screen note, not an upload.
    const isUnsupported = (f) =>
      /heic|heif|tiff?/i.test(f.type) ||
      ["heic", "heif", "tif", "tiff"].includes(extOf(f.name));

    // Supported formats: JPEG and PNG only. SVG is intentionally blocked.
    const isSupported = (f) =>
      ["image/jpeg", "image/png"].includes(f.type) ||
      ["jpg", "jpeg", "png"].includes(extOf(f.name));

    picked.filter(isUnsupported).forEach((f) =>
      toast.error(
        `${f.name} can't be used here. iPhone HEIC/TIFF images aren't supported — please upload a JPEG or PNG instead (on iPhone: Settings → Camera → Formats → Most Compatible).`
      )
    );

    const files = picked.filter((f) => isSupported(f) && !isUnsupported(f));
    if (files.length === 0) {
      // Only show the generic message when nothing already got the HEIC/TIFF note.
      if (!picked.some(isUnsupported)) {
        toast.error("Only JPEG or PNG images are allowed.");
      }
      return;
    }
    console.log('cropped iles: ',files);
    setCropState({ open: true, files, target: null });
  };

  const handleCropCancel = () =>
    setCropState({ open: false, files: [], target: null });

  const handleCropComplete = async (croppedBlobs) => {
    console.log('--- handleCropComplete --- croppedBlobs: ', croppedBlobs)
    
    setCropState({ open: false, files: [], target: null });
      // bulk existing code
    try {
      // Upload all images in parallel
      const settled = await Promise.allSettled(
        croppedBlobs.map(async (file) => {
          const preview = URL.createObjectURL(file);
          const compressed = await compressImage(file);
          const formData = new FormData();
          formData.append("image", compressed);
          const res = await axios.post(`${config.PATH}/api/upload/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return {
            id: `bulk-${Date.now()}-${Math.random()}`,
            url: res.data.imageUrl,
            preview,
            assigned: false,
          };
        })
      );

      const succeeded = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failCount = settled.length - succeeded.length;

      if (succeeded.length > 0) {
        setBulkUploadedImages((prev) => [...prev, ...succeeded]);
        toast.success(`${succeeded.length} photo(s) added to gallery${failCount > 0 ? ` (${failCount} failed)` : ""}`);
        console.log('BulkUploadedImage: ',bulkUploadedImages);
      } else {
        toast.error("All photo uploads failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload photos to gallery");
    } finally {
      setIsBulkUploading(false);
    }
    //---
  };

  const handleImageUpload = async (table_type, identifier, area, files) => {
    try {
      setLoading(true);

      // First, save pending column edits from editedObservations to main arrays
      if (editedObservations.length > 0) {
        setSelectedObservations((prev) => {
          const updated = [...prev];
          editedObservations.forEach((obj) => {
            const srNoOrId = obj.sr_no || obj.id;
            const index = updated.findIndex(
              (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
            );
            if (index !== -1) {
              updated[index] = obj;
            }
          });
          return updated;
        });

        setAllObservations((prev) => {
          const updated = [...prev];
          editedObservations.forEach((obj) => {
            const srNoOrId = obj.sr_no || obj.id;
            const index = updated.findIndex(
              (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
            );
            if (index !== -1) {
              updated[index] = { ...obj, is_selected: 1 };
            }
          });
          return updated;
        });
      }

      const uploadedUrls = [];

      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          throw new Error("Only image files are allowed.");
        }

        const compressedImage = await compressImage(file);

        const formData = new FormData();
        formData.append("image", compressedImage);

        const response = await axios.post(
          `${config.PATH}/api/upload/image`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        uploadedUrls.push(response.data.imageUrl);
      }

      const updateImages = (arr) => {
        let changed = false;
        const updated = arr.map((item) => {
          if (
            item.table_type === table_type &&
            (item.sr_no === identifier || item.id === identifier)
          ) {
            changed = true;
            return { ...item, imageUrls: [...(item.imageUrls || []), ...uploadedUrls] };
          }
          return item;
        });
        return changed ? updated : arr;
      };

      setSelectedObservations((prev) => updateImages(prev));
      setAllObservations((prev) => updateImages(prev));
      setEditedObservations((prev) => {
        if (prev.length === 0) return prev;
        return updateImages(prev);
      });

      // Also update safeGroupedObservations directly so new images appear
      // while the row is still in edit mode, without triggering a stale-data rebuild
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[table_type]) {
          updated[table_type] = updated[table_type].map((item) =>
            (item.sr_no === identifier || item.id === identifier)
              ? { ...item, imageUrls: [...(item.imageUrls || []), ...uploadedUrls] }
              : item
          );
        }
        return updated;
      });

      setLoading(false);
      toast.success("Images uploaded successfully!");
    } catch (error) {
      console.log("Error uploading image:", error);
      toast.error(error.message);
    }
  };

  const handleRemoveImage = (
  table_type,
  area,
  identifier,
  imageIndex,
  imageUrl
) => {
  debugger;

  const updateObservation = (arr) =>
    arr.map((obs) => {
      if (
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || (obs.id != null && obs.id === identifier))
      ) {
        const updatedImages = (obs.imageUrls || []).filter(
          (_, idx) => idx !== imageIndex
        );

        return {
          ...obs,
          ...(updatedImages.length > 0
            ? { imageUrls: updatedImages }
            : { imageUrls: [] }),
        };
      }

      return obs;
    });

  if (identifier === -1) {
    setNewRowInputs((prev) => {
      const current = prev[table_type];
      if (!current) return prev;

      const updatedImages = (current.imageUrls || []).filter(
        (_, idx) => idx !== imageIndex
      );

      return {
        ...prev,
        [table_type]: {
          ...current,
          imageUrls: updatedImages,
        },
      };
    });
    // selectedObservation.image is a snapshot taken when the gallery opened,
    // not a live read of newRowInputs — the gallery's "Assigned Images"
    // section reads from it, so without refreshing it here the removed
    // image kept showing there until the gallery was closed and reopened.
    setSelectedObservation((prev) => {
      if (!prev?.image) return prev;
      const updatedImages = (prev.image.imageUrls || []).filter(
        (_, idx) => idx !== imageIndex
      );
      return { ...prev, image: { ...prev.image, imageUrls: updatedImages } };
    });
  } else {
    // Resolved synchronously inside the updater, before React flushes it, so
    // it can be used right below for the allObservations sync in this same
    // tick — see the resolvedUrl pattern used elsewhere in this file.
    let updatedSelectedList = null;
    setSelectedObservations((prev) => {
      // Pick up any live field edits first — handleCellEdit only writes
      // those into editedObservations/safeGroupedObservations in real time,
      // so selectedObservations can lag behind until Save. Sync from
      // editedObservations before removing the image so we don't operate
      // on a stale copy of this row.
      const synced = syncObservationInList(editedObservations, prev, identifier);
      const next = updateObservation(synced);
      updatedSelectedList = next;
      return next;
    });

    // Sync allObservations from the just-updated selectedObservations
    // (rather than independently re-deriving it via updateObservation) so
    // this row doesn't drift out of sync with any edits selectedObservations
    // already has that allObservations hasn't picked up yet.
    setAllObservations((prev) =>
      syncObservationInList(updatedSelectedList || [], prev, identifier)
    );

    setEditedObservations((prev) => updateObservation(prev));

    setSafeGroupedObservations((prev) => ({
      ...prev,
      [table_type]: prev[table_type]
        ? updateObservation(prev[table_type])
        : prev[table_type],
    }));

    setImageUrlsByRow((prev) => {
      const rowKey = `${table_type}-${identifier}`;

      const obs = AllObservations.find(
        (o) =>
          o.table_type === table_type &&
          o.area === area &&
          (o.sr_no === identifier || o.id === identifier)
      );

      const updatedImages = (obs?.imageUrls || []).filter(
        (_, idx) => idx !== imageIndex
      );

      const updated = { ...prev };

      if (updatedImages.length) {
        updated[rowKey] = updatedImages;
      } else {
        delete updated[rowKey];
      }

      return updated;
    });

    setSelectedObservation((prev) => {
      if (
        !prev?.image ||
        !(
          prev.image.sr_no === identifier ||
          prev.image.id === identifier
        )
      ) {
        return prev;
      }

      return {
        ...prev,
        image: {
          ...prev.image,
          imageUrls: (prev.image.imageUrls || []).filter(
            (_, idx) => idx !== imageIndex
          ),
        },
      };
    });
  }

  const imgObj = {
    id: `bulk-${Date.now()}-${Math.random()}`,
    url: imageUrl,
    preview: imageUrl,
    assigned: false,
  };

  console.log("Back to gallery:", imgObj);

  setBulkUploadedImages((prev) => {
    if (prev.some((img) => img.url === imageUrl)) {
      return prev;
    }

    return [...prev, imgObj];
  });
};

  const handleClose = () => {
    if (isEditing && screenNumber === 9) {
      toast.warning("Please save changes before closing the report.");
      return;
    } else if (!isSaved && screenNumber !== 1 && !exp) {
      toast.warning("Please save the report before closing.");
    } else {
      closeReport();
    }
  };

// handle bulk image upload
  const handleBulkImageUpload = async (files) => {
    console.log('-- handleBulkImageUpload --');
    if (!files || files.length === 0) return;
    const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setIsBulkUploading(true);
    try {
      // Upload all images in parallel
      const settled = await Promise.allSettled(
        imageFiles.map(async (file) => {
          const preview = URL.createObjectURL(file);
          const compressed = await compressImage(file);
          const formData = new FormData();
          formData.append("image", compressed);
          const res = await axios.post(`${config.PATH}/api/upload/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return {
            id: `bulk-${Date.now()}-${Math.random()}`,
            url: res.data.imageUrl,
            preview,
            assigned: false,
          };
        })
      );

      const succeeded = settled.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failCount = settled.length - succeeded.length;

      if (succeeded.length > 0) {
        setBulkUploadedImages((prev) => [...prev, ...succeeded]);
        toast.success(`${succeeded.length} photo(s) added to gallery${failCount > 0 ? ` (${failCount} failed)` : ""}`);
        console.log('BulkUploadedImage: ',bulkUploadedImages);
      } else {
        toast.error("All photo uploads failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload photos to gallery");
    } finally {
      setIsBulkUploading(false);
    }
  };

  // open bulk image gallery
  const handleOpenGallery = (tableType,observation) => {
    console.log('---- handleOpenGallery ',observation);
    setOpenGallery(true);
    setCurrentTableType(tableType);

    // This only decides which image list the gallery dialog shows — it must
    // never touch currentEditedRow/isEditing. It used to call
    // setCurrentEditedRow(-1) whenever observation was the empty-object
    // placeholder passed by the "add new row" form's upload button, which
    // cancelled whatever OTHER row was actively being edited (disabling it,
    // since isFieldDisabled checks currentEditedRow === observation.sr_no) —
    // just from opening the gallery for the new row being composed.
    if (currentEditedRow == -1 || !observation || Object.keys(observation).length === 0) {
      // Nothing is being edited, or this was opened from the "add new row"
      // form (empty-object placeholder) — show that row's own pending images.
      setSelectedObservation({image: newRowInputs[tableType], index: -1});
    } else {
      setSelectedObservation({image: observation, index: observation.sr_no});
    }
  }

  // Syncs a single row in `targetList` with its up-to-date version from
  // `sourceList`, matched by sr_no (or id, this file's identifier for a row
  // without a sr_no yet). Only the matching row is touched — every other row
  // in targetList is left exactly as-is.
  const syncObservationInList = (sourceList, targetList, identifier) => {
    const matchesIdentifier = (obs) =>
      (obs.sr_no != null && obs.sr_no === identifier) ||
      (obs.id != null && obs.id === identifier);

    const sourceObservation = sourceList.find(matchesIdentifier);
    if (!sourceObservation) return targetList;

    return targetList.map((obs) =>
      matchesIdentifier(obs) ? { ...obs, ...sourceObservation } : obs
    );
  };

  const handleAssignBulkImageToRow = (tableType, imageId) => {
  console.log(
    "-- handleAssignBulkImageToRow -- imageId:",
    imageId,
    "tableType:",
    tableType
  );

  const img = bulkUploadedImages.find(i => i.url === imageId);
  if (!img) return;

  // Assign to whichever row the gallery is actually showing — tracked by
  // handleOpenGallery via selectedObservation.index — not the globally
  // "current" currentEditedRow. Those can differ: the gallery can be opened
  // for the "add new row" form (selectedObservation.index === -1) while a
  // *different* row is still being edited (currentEditedRow !== -1). Using
  // currentEditedRow here would silently attach the image to that other
  // row instead of the new row the user actually picked it for.
  const targetId = selectedObservation?.index;

  if (targetId != null && targetId !== -1) {

    const appendUrl = (arr) =>
      arr.map(item => {
        if (
          item.table_type === tableType &&
          (item.sr_no === targetId || item.id === targetId)
        ) {
          const existing = item.imageUrls || [];

          if (existing.includes(img.url)) {
            return item;
          }

          return {
            ...item,
            imageUrls: [...existing, img.url],
          };
        }

        return item;
      });

    // Resolved synchronously inside the updater, before React flushes it, so
    // it can be used right below for the allObservations sync in this same
    // tick — see the resolvedUrl pattern used elsewhere in this file.
    let updatedSelectedList = null;
    setSelectedObservations(prev => {
      // Pick up any live field edits first — handleCellEdit only writes
      // those into editedObservations/safeGroupedObservations in real time,
      // so selectedObservations can lag behind until Save. Sync from
      // editedObservations before appending the image so we don't append
      // onto a stale copy of this row.
      const synced = syncObservationInList(editedObservations, prev, targetId);
      const next = appendUrl(synced);
      updatedSelectedList = next;
      return next;
    });

    // Sync allObservations from the just-updated selectedObservations
    // (rather than independently re-deriving it via appendUrl) so this row
    // doesn't drift out of sync with any edits selectedObservations already
    // has that allObservations hasn't picked up yet.
    setAllObservations(prev =>
      syncObservationInList(updatedSelectedList || [], prev, targetId)
    );

    setEditedObservations(prev =>
      prev.length === 0 ? prev : appendUrl(prev)
    );

    setSafeGroupedObservations(prev => ({
      ...prev,
      [tableType]: prev[tableType]
        ? appendUrl(prev[tableType])
        : prev[tableType],
    }));

    setSelectedObservation(prev => {
      if (
        !prev?.image ||
        !(
          prev.image.sr_no === targetId ||
          prev.image.id === targetId
        )
      ) {
        return prev;
      }

      const existing = prev.image.imageUrls || [];

      if (existing.includes(img.url)) {
        return prev;
      }

      return {
        ...prev,
        image: {
          ...prev.image,
          imageUrls: [...existing, img.url],
        },
      };
    });

    setIsReportEdited(true);

  } else {

    const existing = newRowInputs[tableType]?.imageUrls || [];
    if (!existing.includes(img.url)) {
      const updatedUrls = [...existing, img.url];
      setNewRowInputs(prev => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          imageUrls: updatedUrls,
        },
      }));
      // selectedObservation.image is a snapshot taken when the gallery
      // opened, not a live read of newRowInputs — refresh it here too, or
      // the gallery's "Assigned Images" section won't show the image just
      // assigned until the gallery is closed and reopened.
      setSelectedObservation(prev =>
        prev ? { ...prev, image: { ...prev.image, imageUrls: updatedUrls } } : prev
      );
    }

  }

  // Remove from gallery
  setBulkUploadedImages(prev =>
    prev.filter(item => item.url !== imageId)
  );
};

  const handleUnassignBulkImageFromRow = (tableType, imageUrl, srno) => {
    console.log('-- handleUnassignBulkImageFromRow -- imageUrl: '+imageUrl+' tableType:'+tableType);
    // console.log('allObservations: ',allObservations);

    // No pool flag to clear — just remove from the new-row input list.
    // if(currentEditedRow == -1){
    //   // new observation
    //   setNewRowInputs((prev) => ({
    //     ...prev,
    //     [tableType]: {
    //       ...prev[tableType],
    //       imageUrls: (prev[tableType]?.imageUrls || []).filter((u) => u !== imageUrl),
    //     },
    //   }));
    // } else {
    //   // existing
    //   console.log('Existing Obs: ',currentEditedRow);
    //   let currentObs = AllObservations.filter(ftr => ftr.sr_no == currentEditedRow)[0];
    //   console.log('Current Observation: ',currentObs);
      
    //   let existingImgs = currentObs.imageUrls;
    //   currentObs.imageUrls = existingImgs.filter(ftri => ftri != imageUrl);

    //   //update selected observations
    //   // setSelectedObservation(currentObs);
    //   setObservations(currentObs);
    //   setEditedObservations(currentObs);
      
    //   // update observation set for images
    //   setAllObservations([...allObservations.filter(ftr => ftr.sr_no != currentEditedRow), currentObs]); 
    //   setSelectedObservation({image: currentObs, index: currentEditedRow});
    // }
    

    //add to image stack again to make it available for assign to observation
    // const preview = imageUrl.createObjectURL(file);
    // console.log('preview: ',preview);
    // let imgObj =  {
    //         id: `bulk-${Date.now()}-${Math.random()}`,
    //         url: imageUrl,
    //         preview: imageUrl,
    //         assigned: false,
    //       };

    // console.log('Back to gallery: ',imgObj);

    // //removing from gallery
    // // let img = bulkUploadedImages.find((i) => i.url === imageUrl);
    // // img.assigned=false;
    // setBulkUploadedImages([...bulkUploadedImages,imgObj]);
  };

  const handleAssignBulkImageToExistingRow = (table_type, identifier, imageId) => {
    console.log('-- handleAssignBulkImageToExistingRow -- imageId',imageId);
    let resolvedUrl = null;

    // Check current state inside the setter to prevent stale-closure double-assign
    setBulkUploadedImages((prev) => {
      const img = prev.find((i) => i.id === imageId);
      if (!img || img.assigned) return prev;
      resolvedUrl = img.url;
      return prev.map((i) => (i.id === imageId ? { ...i, assigned: true } : i));
    });

    // resolvedUrl is set synchronously before React flushes the setter,
    // so we can use it for the follow-up state updates in the same event tick.
    if (!resolvedUrl) return;

    const url = resolvedUrl;
    const appendUrl = (arr) =>
      arr.map((item) => {
        if (
          item.table_type === table_type &&
          (item.sr_no === identifier || item.id === identifier)
        ) {
          const existing = item.imageUrls || [];
          // Deduplicate: skip if URL already present (handles rapid double-clicks)
          if (existing.includes(url)) return item;
          return { ...item, imageUrls: [...existing, url] };
        }
        return item;
      });

    setSelectedObservations((prev) => appendUrl(prev));
    setAllObservations((prev) => appendUrl(prev));
    setEditedObservations((prev) => (prev.length === 0 ? prev : appendUrl(prev)));
    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };
      if (updated[table_type]) updated[table_type] = appendUrl(updated[table_type]);
      return updated;
    });
    console.log('bulkUploadedImages: ',bulkUploadedImages);
    setIsReportEdited(true);
  };

  const cleanupOrphanedBulkImages = () => {
    console.log('-- cleanupOrphanedBulkImages --');
    if (bulkUploadedImages.length === 0) return;
    const urls = bulkUploadedImages.map((i) => i.url);
    bulkUploadedImages.forEach((i) => { if (i.preview) URL.revokeObjectURL(i.preview); });
    setBulkUploadedImages([]);
    // Fire-and-forget — don't block the close on the network call
    axios.delete(`${config.PATH}/api/upload/images`, { data: { imageUrls: urls } })
      .catch((err) => console.error("Failed to cleanup orphaned bulk images:", err));
  };

  const ImageGallery = ({
    bulkUploadedImages,
    handleAssignBulkImageToRow,
    handleRemoveImage
  }) => {
    // selectedObservation is the row the gallery was actually opened for (set
    // by handleOpenGallery), which can differ from the globally-current
    // currentEditedRow — e.g. gallery opened for the "add new row" form while
    // a different row is still mid-edit. Re-deriving assignedImages from
    // currentEditedRow/allObservations here showed (and let you remove
    // images from) that other row's data instead of the row the gallery is
    // actually displaying.
    const assignedImages = selectedObservation?.image;
    return(
      <Dialog 
        open={openGallery} 
        onClose={handleCloseGallery} 
        maxWidth="lg" 
        PaperProps={{
                      sx: {
                        border: "1px solid black",
                        borderRadius: "10px",
                      },
                    }}
      >
        <DialogTitle>Image Gallery</DialogTitle>
        <DialogContent>
          <div>
            <p>Assigned Images</p>
            {assignedImages?.imageUrls?.length > 0 && (
              <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(8, 1fr)",
                      gap: "10px",
                    }}>
              {assignedImages?.imageUrls?.map( (img, idx) => (
                <div key={img} className="" style={{ display: "flex",position:"relative",width:"100px",height:"100px" }}>
                  <Tooltips key={img} title="Click to un-assign to this row" placement="top">
                    <img
                        src={img}
                        alt="available"
                        className={`bulk-thumb ${img.length ? 'bulk-thumb--available':'bulk-thumb--assigned'}`}
                        style={{
                                cursor: "pointer",
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                        onClick={() => handleRemoveImage(currentTableType, assignedImages.area, selectedObservation?.index, idx, img)}
                      />
                      <CancelIcon
                        onClick={() => handleRemoveImage(currentTableType, assignedImages.area, selectedObservation?.index, idx, img)}
                        className="bulk-thumb-remove"
                      />
                  </Tooltips>
                </div>
              ))}
            </div>
            )}
            <hr />
            {unassignedBulkImages.length == 0 ? 
              <p>All photos assigned</p>
            :
            unassignedBulkImages.length && (
              <>
              <p>Available Images</p>
              <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: "10px",
                    }}>
              {unassignedBulkImages.length && unassignedBulkImages.map(img => (
                <div key={img.id} className="" style={{ display: "flex",position:"relative",width:"200px",height:"200px" }}>
                  <Tooltips key={img.id} title="Click to assign to this row" placement="top">
                    <img
                      src={img.url}
                      alt="available"
                      className={`bulk-thumb ${img.assigned? 'bulk-thumb--available':'bulk-thumb--assigned'}`}
                      style={{
                              cursor: "pointer",
                              width: "200px",
                              height: "200px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                      onClick={() => handleAssignBulkImageToRow(currentTableType, img.url)}
                    />
                  </Tooltips>
                </div>
              ))
              }
              </div>
              </>
              
            )}
            </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGallery}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  // view and delete Bulk image uploaded images
  const ViewBulkViewerModal =({open, close, imgUrl, deleteBulkImage}) => {
    // console.log('ViewBulkImageModal -open ',imgUrl)
    return (
      <Dialog 
        open={open} 
        onClose={close} 
        maxWidth="lg" 
        PaperProps={{
                      sx: {
                        border: "1px solid black",
                        borderRadius: "10px",
                      },
                    }}
      >
        <DialogContent>
          <img src={imgUrl} alt="Selected Image" />
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteBulkImage} color="error">Delete</Button>
          <Button onClick={close}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const closeReport = () => {
    setOpenSavedReport(false);
  };
  const debouncedSetTheWayForward = useRef(
    debounce((value) => {
      setTheWayForward(value);
    }, 2000),
  ).current;
  const debouncedSetConclusion = useRef(
    debounce((value) => {
      setConclusion(value);
    }, 2000),
  ).current;
  const debouncedSetExeSummaryFindings = useRef(
      debounce((value) => {
        setExeSummaryFindigs(value);
      }, 2000),
    ).current;
    const debouncedSetExeSummaryKeyObservations = useRef(
      debounce((value) => {
        setExeSummaryKeyObservations(value);
      }, 2000),
    ).current;
  // const handleScoreChange = (event, tableType, sr_no) => {
  //   setIsSaved(false);
  //   const newScore = parseInt(event.target.value, 10);

  //   // Validate the new score
  //   if ((newScore >= 0 && newScore <= 5) || event.target.value === "") {
  //     // Clone the observations and selectedObservations arrays
  //     const newObservations = [...AllObservations];
  //     const newSelectedObservations = [...selectedObservations];

  //     // Find the relevant observation in observations using either sr_no or id
  //     const globalIndex = newObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (globalIndex !== -1) {
  //       newObservations[globalIndex].score =
  //         event.target.value === "" ? null : newScore;
  //     }

  //     // Find the relevant observation in selectedObservations using either sr_no or id
  //     const selectedIndex = newSelectedObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (selectedIndex !== -1) {
  //       newSelectedObservations[selectedIndex].score =
  //         event.target.value === "" ? null : newScore;
  //     }

  //     // Update the state with the modified arrays
  //     setAllObservations(newObservations);
  //     setSelectedObservations(newSelectedObservations);
  //   } else {
  //     alert("Invalid score. Please enter a value between 0 and 5.");
  //     // Find the observation to restore the original score in the input field
  //     const originalObservation = AllObservations.find(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );
  //     event.target.value = originalObservation ? originalObservation.score : "";
  //   }
  // };

  // handleScoreChange function is now disabled since score is read-only and calculated from filter function
  // const handleScoreChange = (event, tableType, sr_no) => {
  //   setIsSaved(false);
  //   const newScore = parseInt(event.target.value, 10);

  //   if ((newScore >= 0 && newScore <= 5) || event.target.value === "") {
  //     const newObservations = [...AllObservations];
  //     const newSelectedObservations = [...selectedObservations];

  //     const globalIndex = newObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (globalIndex !== -1) {
  //       newObservations[globalIndex].score =
  //         event.target.value === "" ? null : newScore;
  //     }

  //     const selectedIndex = newSelectedObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (selectedIndex !== -1) {
  //       newSelectedObservations[selectedIndex].score =
  //         event.target.value === "" ? null : newScore;
  //     }


  //     setIsEditing(true);
  //     setCurrentEditedRow(sr_no);
  //     setIsReportEdited(true);

  //     const updatedEdited = [...editedObservations];
  //     const existingIndex = updatedEdited.findIndex((obs) => obs.sr_no === sr_no);

  //     const newScoreValue = event.target.value === "" ? null : newScore;

  //     if (existingIndex !== -1) {
  //       updatedEdited[existingIndex] = {
  //         ...updatedEdited[existingIndex],
  //         score: newScoreValue,
  //       };
  //     } else {
  //       const obsToClone = newObservations[globalIndex];
  //       if (obsToClone) {
  //         updatedEdited.push({
  //           ...obsToClone,
  //           score: newScoreValue,
  //         });
  //       }
  //     }

  //     setAllObservations(newObservations);
  //     setSelectedObservations(newSelectedObservations);
  //     setEditedObservations(updatedEdited);
  //   } else {
  //     alert("Invalid score. Please enter a value between 0 and 5.");
  //     const originalObservation = AllObservations.find(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );
  //     event.target.value = originalObservation ? originalObservation.score : "";
  //   }
  // };
  const handleDuplicateRow = (table_type, area, identifier) => {
    if (isEditing) {
      toast.warn(
        "Please finish editing the current row before adding a new row."
      );
      return;
    }

    // Find the row to duplicate
    const selectedOriginalRow = selectedObservations.find(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || (obs.id != null && obs.id === identifier))
    );

    if (!selectedOriginalRow) {
      toast.error("Observation not found.");
      return;
    }

    // Create a duplicated row
    const duplicatedRowForSelected = {
      ...selectedOriginalRow,
      refIndex: selectedOriginalRow.refIndex + 1, // Adjust refIndex or handle it based on your requirement
      imageUrls: selectedOriginalRow.imageUrls
        ? [...selectedOriginalRow.imageUrls]
        : [],
    };

    // Create copies of state arrays
    const updatedAllObservations = [...AllObservations];
    const updatedSelectedObservations = [...selectedObservations];

    // Insert duplicated row into observations and selectedObservations
    updatedAllObservations.splice(selectedOriginalRow.refIndex + 1, 0, {
      ...duplicatedRowForSelected,
      is_selected: 1,
    });
    updatedSelectedObservations.splice(
      updatedSelectedObservations.findIndex(
        (obs) =>
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || obs.id === identifier)
      ) + 1,
      0,
      duplicatedRowForSelected
    );

    // Update state
    setAllObservations(updatedAllObservations);
    setSelectedObservations(updatedSelectedObservations);

    toast.success("New row added");
  };

  const handleDeleteRow = (table_type, area, identifier, bypassEditGuard = false) => {
    console.log(table_type, area, identifier);
    // Find the row to check if it's a new row
    const rowToDelete = selectedObservations.find(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || (obs.id != null && obs.id === identifier))
    );

    // Allow deletion of new rows even when editing, since they're still being worked on
    if (!bypassEditGuard && isEditing && currentEditedRow !== -1 && !(rowToDelete && rowToDelete.isNew)) {
      toast.warn(
        "Please finish editing the current row before deleting the row."
      );
      return;
    }

    // Create deep copies to avoid mutation
    const updatedSelectedObservations = [...selectedObservations];
    let updatedAllObservations = [...AllObservations]; // Use let instead of const

    // if (updatedSelectedObservations.length === 1) {
    //   toast.warn("Cannot delete the last row.");
    //   return;
    // }

    // Find the index to delete using table_type, area, and identifier (sr_no or id)
    const rowIndex = updatedSelectedObservations.findIndex(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || (obs.id != null && obs.id === identifier))
    );

    if (rowIndex === -1) {
      toast.error("Row not found.");
      return;
    }

    const observationToDelete = updatedSelectedObservations[rowIndex];
    const refIndex = observationToDelete.refIndex;


    updatedSelectedObservations.splice(rowIndex, 1);
    updatedAllObservations = updatedAllObservations.filter(
      (obs) =>
        !(
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || (obs.id != null && obs.id === identifier))
        )
    );

    updatedSelectedObservations.forEach(
      (e) => (e.refIndex = e.refIndex > refIndex ? e.refIndex - 1 : e.refIndex)
    );

    setSelectedObservations(updatedSelectedObservations);
    setAllObservations(updatedAllObservations);
    setIsEditing(false);
    setEditedObservations([]);
    setCurrentEditedRow(-1);
    toast.error("Row deleted");
    // Calculate remaining rows for this table type
    const remainingRows = updatedSelectedObservations.filter(o => o.table_type === table_type);

    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };
      // const remainingRows = updatedSelectedObservations.filter(o => o.table_type === table_type);

      if (remainingRows.length === 0) {
        delete updated[table_type]; // remove table from table container
      } else {
        updated[table_type] = remainingRows;
      }
      return updated;
    });

    // Remove the deleted table type from selectedTableType
    // setSelectedTableType((prev) => prev.filter(t => t.value !== table_type));
    if (remainingRows.length === 0) {
      setSelectedTableType((prev) => prev.filter(t => t.value !== table_type));
    }

    setNewObservationAdded(true);
  };

  const sectors = () => {
    try {
      const options = [];
      const list = [
        ...new Set(
          allData.data
            .map((e) => e.sector_type)
            .filter(
              (sector_type) =>
                sector_type !== null &&
                sector_type !== undefined &&
                sector_type !== ""
            )
        ),
      ];
      list.map((area) => options.push({ label: area, value: area }));
      return options;
    } catch (err) {
      console.log(err);
    }
  };

  const handleChangeSector = (e) => {
    if (!e) {
      setSelectedSector([]);
      setSelectedParam([]);
      setSelectedArea([]);
      setAreaList([]);
    }
    setSelectedSector(e);
  };

  const handleChangeParam = (e) => {

    if (!e || e.length === 0) {
      setSelectedArea([]);
      setAreaList([]);
      // Added code to disable proceed button
      setAllObservations([]);
      setSelectedObservations([]);
    }
    setSelectedParam(e);

  };

  const getScoreColor = (percentage) => {
    if (percentage <= 33) {
      return "#FF0000"; // Red
    } else if (percentage > 33 && percentage <= 66) {
      return "#FFA500"; // Orange
    } else {
      return "#006400"; // Dark Green
    }
  };

  // const pieOptions = {
  //   labels: [
  //     `Obtained Score(${scorePercent}%)`,
  //     `Remaining Score(${100 - scorePercent}%)`,
  //   ],
  //   colors: [getScoreColor(scorePercent), "grey"],
  //   dataLabels: {
  //     enabled: true,
  //   },
  //   legend: {
  //     show: true,
  //     position: "bottom",
  //     horizontalAlign: "center",
  //     fontSize: "14px",
  //     markers: {
  //       width: 16,
  //       height: 16,
  //     },
  //   },
  //   responsive: [
  //     {
  //       breakpoint: 480,
  //       options: {
  //         chart: {
  //           width: 200,
  //         },
  //         legend: {
  //           position: "bottom",
  //         },
  //       },
  //     },
  //   ],
  // };
  const pieOptions = {
    chart: {
      type: "pie",
      animations: {
        enabled: false, // IMPORTANT FIX
      },
    },

    labels: ["Score", "Remaining"],

    tooltip: {
      enabled: true,
    },

    legend: {
      position: "bottom",
    },

    plotOptions: {
      pie: {
        dataLabels: {
          // Pull the percentage labels in toward the center so they sit
          // inside their slice instead of crossing the circle's outline
          // (a small slice like "Remaining" otherwise lands right on the rim).
          offset: -25,
          // Drop the label on very thin slices so it can't spill over the edge.
          minAngleToShowLabel: 12,
        },
      },
    },

    dataLabels: {
      enabled: true,
      // Hide the in-slice label for thin slices (< 10%). For a small
      // "Remaining" sliver this stops its "7.0%" label from overlapping the
      // large "Score" label; the value is instead shown outside the circle
      // (see the callout in the pie render below). Larger slices keep the
      // default one-decimal percentage label.
      formatter: function (val) {
        return val < 10 ? "" : `${val.toFixed(1)}%`;
      },
    },

    noData: {
      text: "No Data",
    },
  };


  const areaCounts = data.reduce((counts, entry) => {
    const area = entry.area;
    counts[area] = (counts[area] || 0) + 1;
    return counts;
  }, {});

  const areasForAreaChart = Object.keys(areaCounts);
  const counts = areasForAreaChart.map((area) => {
    // Skip "NA" (not applicable) rows: their status yields max_pts = 0, so they
    // shouldn't count toward the area's earned score or its possible total.
    const areaObservations = data.filter(
      (entry) => entry.area === area && parseFloat(entry.max_pts || 0) > 0
    );
    const areaScore = areaObservations.reduce(
      (acc, entry) => acc + parseFloat(entry.score || 0),
      0
    );
    // Use each observation's own max_pts (matches the overall score calc in
    // fetchDataForCharts) instead of assuming a fixed 5 points per observation,
    // so the per-area bars reconcile with the overall Audit Score.
    const totalPossibleScore = areaObservations.reduce(
      (acc, entry) => acc + parseFloat(entry.max_pts || 0),
      0
    );
    const percentage =
      totalPossibleScore > 0
        ? Math.floor((areaScore / totalPossibleScore) * 100)
        : 0;
    return percentage.toFixed(0);
  });

  const barOptions = {
    chart: {
      id: "bar-chart",
    },
    colors: ["#005cdb"],
    xaxis: {
      categories: areasForAreaChart.map((area) => {
        // Truncate the area name if it's too long
        const maxChars = 15; // Adjust the maximum characters as needed
        return area.length > maxChars
          ? area.substring(0, maxChars) + "..."
          : area;
      }),
      labels: {
        style: {
          fontSize: "10px", // Adjust the font size as needed
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        dataLabels: {
          position: "top", // top, center, bottom
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val;
      },
      offsetY: -20,
      style: {
        fontSize: "10px",
        colors: ["#304758"],
      },
    },
    tooltip: {
      y: {
        formatter: function (value, { dataPointIndex }) {
          // Display the full area name on hover
          const area = areasForAreaChart[dataPointIndex];
          return `${area}: ${value}%`; // Show area name along with percentage
        },
      },
    },
  };
  // Group the data by area and severity
  const areaSeverityData = data.reduce((result, entry) => {
    const area = entry.area;
    const severity = entry.criticality;

    if (!result[area]) {
      result[area] = { High: 0, Medium: 0, Low: 0 };
    }

    result[area][severity] += 1;
    return result;
  }, {});

  // Extract areas and severity counts
  const areas = Object.keys(areaSeverityData);
  const severityChartData = Object.keys(areaSeverityData).map((area) => ({
    name: area,
    High: areaSeverityData[area].High,
    Medium: areaSeverityData[area].Medium,
    Low: areaSeverityData[area].Low,
  }));

  // Transpose the data to match the series structure
  const transposedData = {
    High: [],
    Medium: [],
    Low: [],
  };

  severityChartData.forEach((area) => {
    transposedData.High.push(area.High);
    transposedData.Medium.push(area.Medium);
    transposedData.Low.push(area.Low);
  });

  // Prepare series data with specific colors for each severity level
  const seriesData = [
    { name: "High", data: [] },
    { name: "Medium", data: [] },
    { name: "Low", data: [] },
  ];

  areas.forEach((area) => {
    seriesData[0].data.push(areaSeverityData[area]?.High || 0);
    seriesData[1].data.push(areaSeverityData[area]?.Medium || 0);
    seriesData[2].data.push(areaSeverityData[area]?.Low || 0);
  });

  // Severity chart options
  const severityChartOptions = {
    chart: {
      id: "severity-chart",
      stacked: true,
    },
    xaxis: {
      categories: areas.map((area) => {
        // Truncate the area name if it's too long
        const maxChars = 15; // Adjust the maximum characters as needed
        return area.length > maxChars
          ? area.substring(0, maxChars) + "..."
          : area;
      }),
      labels: {
        style: {
          fontSize: "10px", // Adjust the font size as needed
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "10px", // Adjust the font size for the total data label
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false, // Hide data labels
    },
    colors: ["#FF0000", "#006400", "#005cdb"],
    tooltip: {
      y: {
        formatter: function (value, { dataPointIndex }) {
          // Display the full area name on hover
          const area = areas[dataPointIndex];
          return `${area}:${value}`;
        },
      },
    },
  };

  const [manualCriticalObservations, setManualCriticalObservations] = useState([]);
  const addObservation = () => {
    // Check if there's already any empty observation
    const hasEmptyObservation = criticalObservations.some(
      (obs) => obs.observation.trim() === ""
    );

    if (hasEmptyObservation) {
      toast.warning("Please fill the existing empty critical observation before adding a new one!");
      return;
    }

    // Prepare new observation: either from user input or blank
    const newObservation = {
      observation: otherDetails.trim() !== "" ? otherDetails.trim() : ""
    };

    // Add to the end of the list
    const updatedManual = [...manualCriticalObservations, newObservation];
    setManualCriticalObservations(updatedManual);
    // setCriticalObservations([...criticalObservations, newObservation]);
    const clonedCritical = criticalObservations.map((obs) => ({ ...obs }));
    setCriticalObservations([...clonedCritical, { ...newObservation }]);

    setHasEditedCriticalObservations(true);
    setOtherDetails(""); // Clear the input

  };
  const handleObservationEdit = (index, e) => {
    setDisableSaveNext(true);
    const updatedObservations = [...criticalObservations];
    updatedObservations[index].observation = e.target.value;
    setCriticalObservations(updatedObservations);
    // console.log("criticalObs", criticalObservations)
  };

  const handleStartEndDate = (date, name) => {
    if (!date) {
      // Handle case when date is null or undefined
      if (name === "start-date") {
        setStartDate(null);
      } else if (name === "end-date") {
        setEndDate(null);
      } else {
        if (startDate) setEndDate(null);
      }
      return;
    }

    // Ensure that the date part is set to midnight (00:00:00)
    const adjustedDate = new Date(date);
    adjustedDate.setHours(0, 0, 0, 0);

    if (name === "start-date") {
      if (endDate && adjustedDate > endDate) {
        toast.warning("Start date cannot be after the end date.");
        setStartDate(null);
        return;
      }
      setStartDate(adjustedDate);
    } else {
      if (startDate && adjustedDate < startDate) {
        toast.warning("End date cannot be before the start date.");
        setEndDate(null);
        return;
      }
      setEndDate(adjustedDate);
    }
  };

  useEffect(() => {
    const uniqueCategories = new Set();
    const filteredOptions = [];

    allData.data.forEach((e) => {
      if (area.includes(e.area) && !uniqueCategories.has(e.category)) {
        filteredOptions.push({ value: e.category, label: e.category });
        uniqueCategories.add(e.category);
      }
    });

    setCategoryOptions(filteredOptions);
  }, [getAllHseData, area]);

  const customSelectStylesCreatable = {
    placeholder: (provided) => ({
      ...provided,
      color: "#6d6d6d",
    }),
    control: (provided) => ({
      ...provided,
      // borderBottom: "none", // Hide the separator
      boxShadow: "none", // Hide the box shadow
      cursor: "pointer", // Show the pointer cursor
      width: "150px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      borderLeft: 0, // Hide the vertical separator beside the dropdown indicator
      padding: 0,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999, // Increase the z-index
      position: "absolute",
    }),
  };

  const handleConfirmSelection = (selectedVariants, removedItems) => {
    let map = {};

    const getId = (item) => item.sr_no || item.id;

    // Update both states with the selected variants
    setSelectedObservations(
      [...selectedObservations, ...selectedVariants].filter((e) => {
        const id = getId(e);
        if (!(map[id] || removedItems.includes(id))) {
          map[id] = true;
          return true;
        } else {
          return false;
        }
      })
    );

    map = {};
    setAllObservations(
      [...AllObservations, ...selectedVariants].filter((e) => {
        const id = getId(e);
        if (!(map[id] || removedItems.includes(id))) {
          map[id] = true;
          return true;
        } else {
          return false;
        }
      })
    );
  };


  // const handleConfirmSelection = (selectedVariants, removedItems) => {
  //   let map = {};

  //   const getId = (item) => item.sr_no || item.id || item.tempId;

  //   // Update both states with the selected variants
  //   setSelectedObservations(
  //     [...selectedObservations, ...selectedVariants].filter((e) => {
  //       const id = getId(e);
  //       if (!(map[id] || removedItems.includes(id))) {
  //         map[id] = true;
  //         return true;
  //       } else {
  //         return false;
  //       }
  //     })
  //   );

  //   map = {};
  //   setAllObservations(
  //     [...AllObservations, ...selectedVariants].filter((e) => {
  //       const id = getId(e);
  //       if (!(map[id] || removedItems.includes(id))) {
  //         map[id] = true;
  //         return true;
  //       } else {
  //         return false;
  //       }
  //     })
  //   );
  // };
  const closeVariantModal = () => {
    setOpenVariantModal(false);
  };

  const criticalityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
  ];

  const getObservationVariants = async (observation, index) => {
    try {
      if (isEditing && currentEditedRow !== index) {
        toast.warn("Please finish editing the current row.");
        return;
      }
      setOpenVariantModal(true);
      const payload = {
        observation,
        report: "saved",
      };

      const response = await axios.post(
        `${config.PATH}/api/search-hse-data-by-observation`,
        payload
      );

      // Ensure the response data is an array
      if (Array.isArray(response.data)) {
        // Add variant: true to each item in the response data
        const updatedVariants = response.data.map((item) => ({
          ...item,
          variant: true,
        }));
        setObservationVariants(updatedVariants);
      } else {
        console.log("Unexpected response data format:", response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleChangeFacilityInfo = (event) => {
    const { name, value } = event.target;
    setFacilityInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleNewKeyChange = (event) => {
    setNewKey(event.target.value);
  };

  const handleNewValueChange = (event) => {
    setNewValue(event.target.value);
  };

  const handleAddNewField = () => {
    if (newKey && newValue) {
      const keyExists = facilityInfo.hasOwnProperty(newKey);
      const valueExists = facilityInfo[newKey] === newValue;

      if (keyExists) {
        alert("This field already exists.");
        return;
      }

      setFacilityInfo((prevInfo) => ({
        ...prevInfo,
        [newKey]: newValue,
      }));
      setNewKey("");
      setNewValue("");
    } else {
      alert("Please fill both the fields before adding.");
    }
  };

  const handleRemoveField = (key) => {
    if (Object.keys(facilityInfo).length === 1) {
      alert("Cannot remove the last field.");
      return;
    }

    const updatedFacilityInfo = { ...facilityInfo };
    delete updatedFacilityInfo[key];
    setFacilityInfo(updatedFacilityInfo);
  };

  const saveFacilityInfo = async () => {
    try {
      const payload = {
        report_id: selectedReportData.report_id,
        facility_info: facilityInfo,
      };
      const res = await axios.post(
        `${config.PATH}/api/save-facility-info`,
        payload
      );
    } catch (err) {
      console.log(err.message);
    }
  };

  const paramsOption = () => {
    try {
      const options = [];
      paramList.map((param) => options.push({ label: param, value: param }));
      return options;
    } catch (err) {
      console.log(err);
    }
  };

  const getParamList = () => {
    if (!selectedSector || selectedSector.length === 0) {
      setParamList([]);
      setSelectedParam([]);
      setSelectedArea([]);
      // setAreasToDisplay(null);
      // setCategoriesToDisplay(null);
      // return [];
      return;
    }

    const list = [
      ...new Set(
        allData.data
          .filter((e) => e.sector_type === selectedSector.value) // Filter based on selectedSector
          .map((e) => e.table_type)
          .filter(
            (table_type) =>
              table_type !== null &&
              table_type !== undefined &&
              table_type !== ""
          )
      ),
    ];

    setParamList(list);
    // return list;
  };

  // const filteredObservations = AllObservations.filter(
  //   (observation) =>
  //     observation.table_type !== null &&
  //     observation.table_type !== undefined &&
  //     (observation.observation
  //       .toLowerCase()
  //       .includes(searchTerm.toLowerCase()) ||
  //       observation.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       observation.table_type.toLowerCase().includes(searchTerm.toLowerCase()))
  // );
  const filteredObservations = (AllObservations || []).filter(
    (observation) =>
      observation.table_type !== null &&
      observation.table_type !== undefined &&
      (
        observation.observation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        observation.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        observation.table_type?.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );


  // Group filtered observations by table_type
  // const groupedByTableType = filteredObservations.reduce(
  //   (groups, observation) => {
  //     const { table_type } = observation;
  //     if (!groups[table_type]) {
  //       groups[table_type] = [];
  //     }
  //     groups[table_type].push(observation);
  //     return groups;
  //   },
  //   {}
  // );
  const groupedByTableType = filteredObservations.reduce((groups, observation) => {
    const { table_type, area } = observation;

    // Initialize the table_type group if it doesn't exist
    if (!groups[table_type]) {
      groups[table_type] = {};
    }

    // Initialize the area group within the table_type if it doesn't exist
    if (!groups[table_type][area]) {
      groups[table_type][area] = [];
    }

    // Add the observation to the appropriate table_type and area group
    groups[table_type][area].push(observation);

    return groups;
  }, {});

  // console.log("groupedByTableType", groupedByTableType);

  const allDataForGlobalSearch = allData.data.filter(
    (e) => e.table_type !== null
  );
  const filteredAllData = allDataForGlobalSearch.filter(
    (observation) =>
      observation.observation
        .toLowerCase()
        .includes(globalSearchTerm.toLowerCase()) ||
      observation.area.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
      (observation.table_type &&
        observation.table_type
          .toLowerCase()
          .includes(globalSearchTerm.toLowerCase()))
  );
  // Group filtered observations by area
  const groupedData = filteredAllData.reduce((groups, observation) => {
    const { table_type } = observation;
    if (!groups[table_type]) {
      groups[table_type] = [];
    }
    groups[table_type].push(observation);
    return groups;
  }, {});

  // const groupedObservations = selectedObservations.reduce(
  //   (acc, observation) => {
  //     if (!acc[observation.table_type]) {
  //       acc[observation.table_type] = [];
  //     }
  //     acc[observation.table_type].push(observation);
  //     return acc;
  //   },
  //   {}
  // );
  // const groupedObservations = (selectedObservations || []).reduce(
  //   (acc, observation) => {
  //     if (!acc[observation.table_type]) {
  //       acc[observation.table_type] = [];
  //     }
  //     acc[observation.table_type].push(observation);
  //     return acc;
  //   },
  //   {}
  // );
  // const safeGroupedObservations = { ...groupedObservations };
  // if (!safeGroupedObservations["Default"]) {
  //   safeGroupedObservations["Default"] = [];
  // }
  const [selectedTableType, setSelectedTableType] = useState([]);
  const [tableTypes, setTableTypes] = useState([]);
  const [safeGroupedObservations, setSafeGroupedObservations] = useState({});
  const [editInputValues, setEditInputValues] = useState({});

  const observationLookupMaps = useMemo(() => {
    const selectedObservationMap = new Map();
    (selectedObservations || []).forEach((obs) => {
      const key = obs.sr_no ?? obs.id;
      if (key != null && key !== "") {
        selectedObservationMap.set(key, obs);
      }
    });
    const groupedObservationIndexMap = {};
    Object.keys(safeGroupedObservations || {}).forEach((tableType) => {
      groupedObservationIndexMap[tableType] = {};
      (safeGroupedObservations[tableType] || []).forEach((obs, index) => {
        const key = obs.sr_no ?? obs.id;
        if (key != null && key !== "") {
          groupedObservationIndexMap[tableType][key] = index;
        }
      });
    });
    return { selectedObservationMap, groupedObservationIndexMap };
  }, [selectedObservations, safeGroupedObservations]);

  observationLookupMapsRef.current = observationLookupMaps;

  const resetDependentLists = useCallback(() => {
    setStatusList([]);
    setObservationList([]);
    setRecommendationList([]);
    setLegalReferenceList([]);
  }, []);

const handleEmptyCheck = useCallback((observation, tableType) => {
  if (!observation) {
    console.warn("handleEmptyCheck called with no observation");
    return [];
  }

  const emptyFields = Object.entries(EMPTY_CHECK_FIELD_LABELS)
    .filter(([key]) => isEmptyValue(observation[key]))
    .map(([, label]) => label);

  const updatedObservation = {
    ...observation,
    emptyFields,
  };

  const updateRow = (item) => {
    const isMatch = updatedObservation.id != null
      ? item.id === updatedObservation.id
      : item.sr_no === updatedObservation.sr_no;

    return isMatch
      ? {
          ...item,
          ...updatedObservation,
          is_selected: 1,
        }
      : item;
  };

  setEditedObservations(prev => prev.map(updateRow));
  setSelectedObservations(prev => prev.map(updateRow));
  setAllObservations(prev => prev.map(updateRow));

  setSafeGroupedObservations(prev => ({
    ...prev,
    [tableType]: (prev[tableType] || []).map(updateRow),
  }));

  if (emptyFields.length > 0) {
    setEmptyFieldFound(true);
    toast.warning(`${emptyFields.join(", ")} can't be set empty`);
  }

  return emptyFields;
}, []);
  // BATCH SAVE UPDATES - Consolidate cascading state updates into single operation
  // Assumes the caller has already run handleEmptyCheck and only invokes this
  // when it returned no empty fields - this function no longer re-validates.
  const handleSaveRowBatch = useCallback(
    (observation, tableType) => {
      // Prepare updated observation with default legal_reference
      const finalObservation = {
        ...observation,
        legal_reference: observation.legal_reference === "" ? "N/A" : observation.legal_reference,
      };

      // Batch all updates together
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex(
          (obs) => obs.sr_no === observation.sr_no || (obs.id != null && obs.id === observation.id)
        );
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            legal_reference: finalObservation.legal_reference,
          };
          return updated.length > 0 ? updated : [];
        }
        return [];
      });

      setSafeGroupedObservations((prev) => ({
        ...prev,
        [tableType]: (prev[tableType] || []).map((obs) =>
          obs.sr_no === observation.sr_no || (obs.id != null && obs.id === observation.id)
            ? { ...obs, legal_reference: finalObservation.legal_reference }
            : obs
        ),
      }));

      setSelectedObservations((prev) => {
        if (editedObservations.length === 0) return prev;
        const updated = [...prev];
        editedObservations.forEach((obj) => {
          const srNoOrId = obj.sr_no || obj.id;
          const idx = updated.findIndex(
            (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
          );
          if (idx !== -1) {
            updated[idx] = {
              ...obj,
              legal_reference: finalObservation.legal_reference,
            };
          }
        });
        return updated;
      });

      setAllObservations((prev) => {
        if (editedObservations.length === 0) return prev;
        const updated = [...prev];
        editedObservations.forEach((obj) => {
          const srNoOrId = obj.sr_no || obj.id;
          const idx = updated.findIndex(
            (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
          );
          if (idx !== -1) {
            updated[idx] = {
              ...obj,
              is_selected: 1,
              legal_reference: finalObservation.legal_reference,
            };
          }
        });
        return updated;
      });

      setEditedObservations([]);
      setIsEditing(false);
      setCurrentEditedRow(-1);
      toast.success("Row changes saved. You can now edit other rows.");
      setNewObservationAdded(true);
    },
    [editedObservations]
  );

  //DEBOUNCE HANDLERS FOR TEXTAREA FIELDS (Fixes lag with 20+ rows)
  const debouncedObservationEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  const debouncedRecommendationEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  const debouncedLegalReferenceEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  // CLEANUP DEBOUNCED FUNCTIONS ON UNMOUNT
  useEffect(() => {
    return () => {
      debouncedObservationEdit.cancel();
      debouncedRecommendationEdit.cancel();
      debouncedLegalReferenceEdit.cancel();
    };
  }, [debouncedObservationEdit, debouncedRecommendationEdit, debouncedLegalReferenceEdit]);

  // useEffect(() => {
  //   const groupedObservations = (selectedObservations || []).reduce((acc, observation) => {
  //     const key = observation.table_type ;
  //     if (!key) return acc;
  //     if (!acc[key]) acc[key] = [];
  //     acc[key].push(observation);
  //     return acc;
  //   }, {});

  //   // Ensure "Default" always exists
  //   // if (!groupedObservations["Default"]) groupedObservations["Default"] = [];

  //   // setSafeGroupedObservations(groupedObservations);
  //    setSafeGroupedObservations((prev) => ({
  //   ...prev, // keep old tables (even if empty)
  //   ...groupedObservations, // overwrite only those that have data
  // }));
  // }, [selectedObservations]);

  // Extract table types from backend data

  //     useEffect(() => {
  //   if (selectedObservations?.length > 0) {
  //     const uniqueTypes = [
  //       ...new Set(selectedObservations.map((obs) => obs.table_type).filter(Boolean))
  //     ];

  //     // Prepopulate select with saved types
  //     const preselectedOptions = uniqueTypes.map((t) => ({ label: t, value: t }));

  //     setSelectedTableType(preselectedOptions);

  //     // Ensure dropdown options include them
  //     setTableTypes((prev) => [...new Set([...prev, ...uniqueTypes])]);

  //     // Initialize grouped observations
  //     setSafeGroupedObservations((prev) => {
  //       const updated = { ...prev };
  //       uniqueTypes.forEach((type) => {
  //         updated[type] = selectedObservations.filter(
  //           (obs) => obs.table_type === type
  //         );
  //       });
  //       return updated;
  //     });
  //   }
  // }, [selectedObservations]);

  //     useEffect(() => {
  //       if (allData?.data?.length > 0) {
  //         const types = [
  //           ...new Set(
  //             allData.data
  //               .map((d) => {
  //                 if (typeof d.table_type === "object" && d.table_type !== null) {
  //                   return d.table_type.value || d.table_type.type || "";
  //                 }
  //                 return d.table_type;
  //               })
  //               .filter(Boolean)
  //               .map(String)
  //           ),
  //         ];
  //         setTableTypes(types);

  //         if (types.length > 0 && !selectedTableType) {
  //           setSelectedTableType(types[0]);
  //         }
  //       }
  //     }, [allData]);

  useEffect(() => {
    const groupedObservations = (selectedObservations || []).reduce((acc, observation) => {
      const key = observation.table_type;
      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(observation);
      return acc;
    }, {});
    // if (!groupedObservations["Default"]) groupedObservations["Default"] = [];

    // setSafeGroupedObservations(groupedObservations);
    //    setSafeGroupedObservations((prev) => ({
    //   ...prev, // keep old tables (even if empty)
    //   ...groupedObservations, // overwrite only those that have data
    // }));
    // Rebuilding each group straight from groupedObservations[key] used to
    // reorder existing rows whenever selectedObservations' own order shifted
    // for reasons unrelated to this table (e.g. handleSave's regroupByArea
    // reorders the whole flat list by area, which reshuffles a table_type's
    // rows if it spans more than one area) — so adding one new observation
    // could visibly renumber every other row's Sr. No. Keep each row's
    // existing position (refreshing its data) and only append rows that are
    // genuinely new to this group, so display order stays stable.
    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        const freshRows = groupedObservations[key] || [];
        const freshByIdentity = new Map();
        freshRows.forEach((row) => {
          const rowKey = row.sr_no ?? row.id;
          if (rowKey != null) freshByIdentity.set(rowKey, row);
        });

        const reordered = (prev[key] || [])
          .map((row) => {
            const rowKey = row.sr_no ?? row.id;
            return rowKey != null && freshByIdentity.has(rowKey)
              ? freshByIdentity.get(rowKey)
              : null;
          })
          .filter(Boolean);

        const seenKeys = new Set(
          reordered.map((row) => row.sr_no ?? row.id).filter((k) => k != null)
        );
        const newRows = freshRows.filter((row) => {
          const rowKey = row.sr_no ?? row.id;
          return rowKey == null || !seenKeys.has(rowKey);
        });

        updated[key] = [...reordered, ...newRows];
      });
      Object.keys(groupedObservations).forEach((key) => {
        if (!updated[key]) updated[key] = groupedObservations[key];
      });
      return updated;
    });

  }, [selectedObservations]);

  useEffect(() => {
    // if (allData?.data?.length > 0) {
    //   const types = [
    //     ...new Set(
    //       allData.data
    //         .map((d) => {
    //           if (typeof d.table_type === "object" && d.table_type !== null) {
    //             return d.table_type.value || d.table_type.type || "";
    //           }
    //           return d.table_type;
    //         })
    //         .filter(Boolean)
    //         .map(String)
    //     ),
    //   ];

    //   setTableTypes(types);

    //   // ✅ Restore only types that actually exist in grouped or selected observations
    //   const activeTypes = [
    //     ...new Set(
    //       (selectedObservations || [])
    //         .map((obs) => obs.table_type)
    //         .filter(Boolean)
    //     ),
    //   ];

    if (allData?.data?.length > 0) {
      const types = [
        ...new Set(
          allData.data
            .map((d) => {
              if (typeof d.table_type === "object" && d.table_type !== null) {
                return d.table_type.value || d.table_type.type || "";
              }
              return d.table_type;
            })
            .filter(Boolean)
            .map(String)
        ),
      ];

      setTableTypes(types);

      // Restore only types that actually exist in grouped or selected observations
      const activeTypes = [
        ...new Set(
          (selectedObservations || [])
            .map((obs) => obs.table_type)
            .filter(Boolean)
        ),
      ];

      if (
        activeTypes.length > 0 &&
        (!selectedTableType || selectedTableType.length === 0)
      ) {
        const formatted = activeTypes.map((t) => ({ label: t, value: t }));
        setSelectedTableType(formatted);
      }
    }
  }, [allData, selectedObservations]);


  const selectedObservationRowClassKeys = useMemo(() => {
    const s = new Set();
    for (const obs of selectedObservations || []) {
      s.add(
        `${obs.table_type ?? ""}|${obs.area ?? ""}|${obs.sr_no ?? ""}|${obs.id ?? ""}`
      );
    }
    return s;
  }, [selectedObservations]);

  /** Memoized dropdown option arrays for the "add new row" line (screen 10) — avoids recomputing on every parent render. */
  const newRowDropdownOptionsByTableType = useMemo(() => {
    const types = new Set([
      ...Object.keys(safeGroupedObservations || {}),
      ...Object.keys(newRowInputs || {}),
    ]);
    const out = {};
    const fallbackCrit = Array.isArray(criticalityOptionList) ? criticalityOptionList : [];
    const fallbackStatus = Array.isArray(statusOptionList) ? statusOptionList : [];

    for (const tableType of types) {
      const row = newRowInputs[tableType] || {};
      const area = row.area;
      const equipment = row.equipment;
      const category = row.category;
      const check_points = row.check_points;
      const criticality = row.criticality;
      const status = row.status;

      let critOpts = [];
      if (check_points && area && equipment && category) {
        const c = getCriticalityOptions(area, equipment, category, check_points);
        critOpts = c.length > 0 ? c : fallbackCrit;
      }

      let statusOpts = [];
      if (criticality && area && equipment && category) {
        const c = getStatusOptions(area, equipment, category, criticality);
        statusOpts = c.length > 0 ? c : fallbackStatus;
      }

      let observationOpts = [];
      if (area && equipment && category && check_points && criticality && status) {
        observationOpts = getObservationOptions(
          area,
          equipment,
          category,
          check_points,
          criticality,
          status
        );
      }

      let recommendationOpts = [];
      if (row.observation && area && equipment && category && check_points && criticality && status) {
        const raw = getRecommendationOptions(
          area,
          equipment,
          category,
          check_points,
          criticality,
          status
        );
        const cleaned = [
          ...new Set(
            raw
              .map((r) => (typeof r === "string" ? r.trim() : r))
              .filter(Boolean)
          ),
        ];
        recommendationOpts = cleaned.length === 0 ? ["N/A"] : cleaned;
      }

      let legalOpts = [];
      if (row.recommendations && area && equipment && category && check_points && criticality && status) {
        const raw = getLegalReferenceOptions(
          area,
          equipment,
          category,
          check_points,
          criticality,
          status
        );
        const cleaned = [
          ...new Set(
            raw
              .map((r) => (typeof r === "string" ? r.trim() : r))
              .filter(Boolean)
          ),
        ];
        legalOpts = cleaned.length === 0 ? ["N/A"] : cleaned;
      }

      out[tableType] = {
        critOpts,
        statusOpts,
        observationOpts,
        recommendationOpts,
        legalOpts,
      };
    }
    return out;
  }, [
    safeGroupedObservations,
    newRowInputs,
    getCriticalityOptions,
    getStatusOptions,
    getObservationOptions,
    getRecommendationOptions,
    getLegalReferenceOptions,
    criticalityOptionList,
    statusOptionList,
  ]);








  //   const handleTableTypeChange = (newValue) => {
  //   if (!newValue) {
  //     setSelectedTableType(null);
  //     return;
  //   }

  //   const value = newValue.value;
  //   setSelectedTableType(newValue);

  //   setTableTypes((prev) => {
  //     if (!prev.includes(value)) return [...prev, value];
  //     return prev;
  //   });

  //   setSafeGroupedObservations((prev) => {
  //     if (!prev[value]) {
  //       return { ...prev, [value]: [] };
  //     }
  //     return prev;
  //   });
  // };

  // const renderNewRowInputs = (tableType) => (
  //   <TableRow key={`new-row-${tableType}`}>
  //     <TableCell>{groupedObservations[tableType]?.length + 1}</TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.area || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "area")}
  //         placeholder="Area"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.check_points || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "check_points")}
  //         placeholder="Check Point"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.observation || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "observation")}
  //         placeholder="Observation"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.criticality || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "criticality")}
  //         placeholder="Criticality"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.recommendations || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "recommendations")}
  //         placeholder="Recommendations"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <textarea
  //         className="input-field"
  //         value={newRowInputs[tableType]?.is_reference || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "is_reference")}
  //         placeholder="IS Reference"
  //       />
  //     </TableCell>
  //     <TableCell className="editable-cell" style={{ height: "100px" }}>
  //       <input
  //         // className="input-field"
  //         value={newRowInputs[tableType]?.score || ""}
  //         onChange={(e) => handleChangeNewRow(tableType, e, "score")}
  //         placeholder="Score"
  //         type="number"
  //         style={{ height: "31px", marginTop: "-2%" }}
  //       />
  //     </TableCell>
  //     <TableCell>
  //       <div className="image-container">
  //         <div className="upload-container">
  //           <input
  //             type="file"
  //             accept="image/*"
  //             // onChange={(e) => handleImageUploadNewRow(tableType, e.target.files)}
  //             multiple
  //             style={{ color: "transparent" }}
  //             disabled={true}
  //           />
  //           {newRowInputs[tableType]?.imageUrls?.length === 0 && (
  //             <div className="no-file-chosen">No file chosen</div>
  //           )}
  //         </div>
  //       </div>
  //     </TableCell>
  //     <TableCell>
  //       <div style={{ display: "flex", justifyContent: "center" }}>
  //         <CheckIcon
  //           onClick={() => handleAddRow(tableType)}
  //           style={{ cursor: "pointer", color: "green" }}
  //         />
  //       </div>
  //     </TableCell>
  //   </TableRow>
  // );
  // const handleTableTypeChange = (newValues) => {
  //   const selectedValues = newValues ? newValues.map(v => v.value) : [];

  //   // Update CreatableSelect
  //   setSelectedTableType(newValues || []);

  //   // Merge newly created types into tableTypes
  //   setTableTypes((prev) => {
  //     const all = [...new Set([...prev, ...selectedValues])];
  //     return all;
  //   });

  //   // Merge grouped observations instead of replacing
  //   setSafeGroupedObservations((prev) => {
  //     const updated = { ...prev }; // keep old types
  //     selectedValues.forEach((val) => {
  //       if (!updated[val]) {
  //         // For new type, initialize empty array
  //         updated[val] = [];
  //       }
  //     });
  //     return updated;
  //   });

  //   // Only filter out removed table types (keep all selected so far)
  //   setSelectedObservations((prev) =>
  //     prev.filter((obs) => selectedValues.includes(obs.table_type))
  //   );

  //   setAllObservations((prev) =>
  //     prev.filter((obs) => selectedValues.includes(obs.table_type))
  //   );

  //   setEditedObservations((prev) =>
  //     prev.filter((obs) => selectedValues.includes(obs.table_type))
  //   );
  // };
  const handleTableTypeChange = (newValues) => {
    console.log('--------------- handleTableTypeChange')
    const selectedValues = newValues ? newValues.map(v => v.value) : [];
    setSelectedTableType(newValues || []);

    setTableTypes((prev) => {
      const all = [...new Set([...prev, ...selectedValues])];
      return all;
    });

    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };


      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });

      selectedValues.forEach((val) => {
        if (!updated[val]) updated[val] = [];
      });

      // to perform autosave each observation with table type
      setNewObservationAdded(true);

      return updated;
    });


    setSelectedObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );

    setAllObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );

    setEditedObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );
    // Clear input values for deselected table types
    setNewRowInputs((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });
      return updated;
    });

    setNewRowInputValues((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });
      return updated;
    });
    setNewObservationAdded(true);
  };


  const getMultiLineValue = (tableType, field) => {
    const inputVal = newRowInputValues[tableType]?.[field];
    const committedVal = newRowInputs[tableType]?.[field];
    if (inputVal === "" && committedVal) return committedVal;
    return inputVal ?? "";
  };

  const handleMultiLineChange = (tableType, val, field) => {
    handleInputChange(tableType, val, field);
  };

  const createChangeHandler = useCallback(
    (tableType, field) => (val) => {
      handleMultiLineChange(tableType, val, field);
    },
    []
  );

  const handleMultiLineSelect = (tableType, val, field) => {
    handleChangeNewRowSelect(tableType, { value: val }, field);
  };

  const handleMultiLineBlur = (tableType, field) => {
    // Only commit if the value was explicitly selected (not just typed)
    // Let the MultiLineCreatableSelect handle the blur without auto-committing
    handleFieldBlur(tableType, field);
  };

  const getEditMultiLineValue = (observationId, field) => {
    const inputVal = editInputValues[`${observationId}-${field}`];
    const observation = allData?.data?.find((e) => e.sr_no === observationId);
    const committedVal = observation?.[field];
    if (inputVal === "" && committedVal) return committedVal;
    return inputVal ?? "";
  };

  const handleEditMultiLineChange = (observationId, val, field) => {
    handleEditInputChange(observationId, val, field);
  };

  const handleEditMultiLineSelect = (observationId, val, field) => {
    const observation = allData?.data?.find((e) => e.sr_no === observationId);
    if (observation) {
      handleCellEdit(
        { label: val, value: val },
        allData?.data?.findIndex((e) => e.sr_no === observationId),
        field,
        observation[field],
        observation
      );
    }
  };

  const handleEditMultiLineBlur = (observationId, field) => {
    const val = editInputValues[`${observationId}-${field}`];
    if (val !== undefined && val !== "") {
      const observation = allData?.data?.find((e) => e.sr_no === observationId);
      if (observation) {
        handleCellEdit(
          { value: val.trim(), label: val.trim() },
          allData?.data?.findIndex((e) => e.sr_no === observationId),
          field,
          observation[field],
          observation
        );
      }
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationId}-${field}`]: "",
      }));
    }
  };

  const renderNewRowInputs = useCallback((tableType, trailingRowIndex) => {
    const nr = newRowDropdownOptionsByTableType[tableType] || {
      critOpts: [],
      statusOpts: [],
      observationOpts: [],
      recommendationOpts: [],
      legalOpts: [],
    };
    return (
      <TableRow key={`new-row-${tableType}`} aria-rowindex={trailingRowIndex}>
        <TableCell>{safeGroupedObservations[tableType]?.length + 1}</TableCell>
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
        <textarea
          className="input-field"
          value={newRowInputs[tableType]?.area || ""}
          onChange={(e) => handleChangeNewRow(tableType, e, "area")}
          placeholder="Area"
        />
      </TableCell> */}
        {/* area */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={areaSelectRef}
              placeholder="Area"
              multiline={false}
              options={getAreaOptions(tableType)}
              value={getMultiLineValue(tableType, "area")}
              // onChange={createChangeHandler(tableType, "area")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "area")}
              onFocus={() => handleFieldFocus(tableType, "area")}
              onBlur={() => handleMultiLineBlur(tableType, "area")}
              onMenuOpen={() => setIsAreaDropdownOpen(true)}
              onMenuClose={() => setIsAreaDropdownOpen(false)}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Area") ? "error-input" : ""}
            />
          </div>
        </TableCell>

        {/* Equipment */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={equipmentSelectRef}
              placeholder="Equipment"
              multiline={false}
              options={getEquipmentOptions(newRowInputs[tableType]?.area)}
              value={getMultiLineValue(tableType, "equipment")}
              // onChange={createChangeHandler(tableType, "equipment")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "equipment")}
              onFocus={() => handleFieldFocus(tableType, "equipment")}
              onBlur={() => handleMultiLineBlur(tableType, "equipment")}
              onMenuOpen={() => setIsEquipmentDropdownOpen(true)}
              onMenuClose={() => setIsEquipmentDropdownOpen(false)}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Equipment") ? "error-input" : ""}
            />
          </div>
        </TableCell>

        {/* Category */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={categorySelectRef}
              placeholder="Category"
              multiline={false}
              options={getCategoryOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment)}
              value={getMultiLineValue(tableType, "category")}
              // onChange={createChangeHandler(tableType, "category")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "category")}
              onFocus={() => handleFieldFocus(tableType, "category")}
              onBlur={() => handleMultiLineBlur(tableType, "category")}
              onMenuOpen={() => setIsCategoryDropdownOpen(true)}
              onMenuClose={() => setIsCategoryDropdownOpen(false)}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Category") ? "error-input" : ""}
            />
          </div>
        </TableCell>
        {/* checkpoints */}
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
        <textarea
          className="input-field"
          value={newRowInputs[tableType]?.check_points || ""}
          onChange={(e) => handleChangeNewRow(tableType, e, "check_points")}
          placeholder="Check Point"
        />
      </TableCell> */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={checkpointSelectRef}
              placeholder="Check Point"
              multiline={true}
              options={getCheckpointOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category)}
              value={getMultiLineValue(tableType, "check_points")}
              // onChange={createChangeHandler(tableType, "check_points")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "check_points")}
              onFocus={() => handleFieldFocus(tableType, "check_points")}
              onBlur={() => handleMultiLineBlur(tableType, "check_points")}
              onMenuOpen={() => setIsCheckpointDropdownOpen(true)}
              onMenuClose={() => setIsCheckpointDropdownOpen(false)}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Checkpoint") ? "error-input" : ""}
            />
          </div>
        </TableCell>

        {/* Criticality */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            {/* <Select
              ref={criticalitySelectRef}
              styles={{
                ...customSelectStylesCreatable,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder="Criticality"
              options={(() => {
                if (!newRowInputs[tableType]?.check_points) {
                  return []; // No options until checkpoint is selected
                }
                const cachedOptions = getCriticalityOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points);
                const optionsToUse = cachedOptions.length > 0 ? cachedOptions : criticalityOptionList;
                return optionsToUse.map((criticality) => ({
                  label: criticality,
                  value: criticality,
                }));
              })()}
              value={
                newRowInputs[tableType]?.criticality
                  ? { label: newRowInputs[tableType].criticality, value: newRowInputs[tableType].criticality }
                  : null
              }
              isSearchable={false}
              isClearable
              onChange={(e) => handleChangeNewRowSelect(tableType, e, "criticality")}
              onMenuOpen={() => setIsCriticalityDropdownOpen(true)}
              onMenuClose={() => setIsCriticalityDropdownOpen(false)}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            /> */}
            <MultiLineCreatableSelect
              ref={criticalitySelectRef}
              placeholder="Criticality"
              multiline={false}
              allowCreate={false}
              options={nr.critOpts}
              value={getMultiLineValue(
                tableType,
                "criticality"
              )}
              // onChange={(val) =>
              //   handleMultiLineChange(
              //     tableType,
              //     val,
              //     "criticality"
              //   )
              // }
              onSelect={(val) =>
                handleMultiLineSelect(
                  tableType,
                  val,
                  "criticality"
                )
              }
              onFocus={() =>
                handleFieldFocus(
                  tableType,
                  "criticality"
                )
              }
              onBlur={() =>
                handleMultiLineBlur(
                  tableType,
                  "criticality"
                )
              }
              onMenuOpen={() =>
                setIsCriticalityDropdownOpen(true)
              }
              onMenuClose={() =>
                setIsCriticalityDropdownOpen(false)
              }
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Criticality") ? "error-input" : ""}
            />
          </div>
        </TableCell>

        {/* Status */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            {/* <Select
              ref={statusSelectRef}
              styles={{
                ...customSelectStylesCreatable,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder="Status"
              options={(() => {
                if (!newRowInputs[tableType]?.criticality) {
                  return []; // No options until criticality is selected
                }
                const cachedOptions = getStatusOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.criticality);
                const optionsToUse = cachedOptions.length > 0 ? cachedOptions : statusOptionList;
                return optionsToUse.map((status) => ({
                  label: status,
                  value: status,
                }));
              })()}
              value={
                newRowInputs[tableType]?.status
                  ? { label: newRowInputs[tableType].status, value: newRowInputs[tableType].status }
                  : null
              }
              isSearchable={false}
              isClearable
              onChange={(e) => handleChangeNewRowSelect(tableType, e, "status")}
              onMenuOpen={() => setIsStatusDropdownOpen(true)}
              onMenuClose={() => setIsStatusDropdownOpen(false)}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            /> */}
            <MultiLineCreatableSelect
              ref={statusSelectRef}
              placeholder="Status"
              multiline={false}
              allowCreate={false}
              options={nr.statusOpts}
              value={getMultiLineValue(
                tableType,
                "status"
              )}
              // onChange={(val) =>
              //   handleMultiLineChange(
              //     tableType,
              //     val,
              //     "status"
              //   )
              // }
              onSelect={(val) =>
                handleMultiLineSelect(
                  tableType,
                  val,
                  "status"
                )
              }
              onFocus={() =>
                handleFieldFocus(
                  tableType,
                  "status"
                )
              }
              onBlur={() =>
                handleMultiLineBlur(
                  tableType,
                  "status"
                )
              }
              onMenuOpen={() =>
                setIsStatusDropdownOpen(true)
              }
              onMenuClose={() =>
                setIsStatusDropdownOpen(false)
              }
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Status") ? "error-input" : ""}
            />
          </div>
        </TableCell>

        {/* Observations */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Observation"
              multiline={true}
              options={nr.observationOpts}
              value={getMultiLineValue(tableType, "observation")}
              // onChange={createChangeHandler(tableType, "observation")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "observation")}
              onFocus={() => handleFieldFocus(tableType, "observation")}
              onBlur={() => handleMultiLineBlur(tableType, "observation")}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Observation") ? "error-input" : ""}
            />
          </div>
        </TableCell>
        {/* Recommendations */}

        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Recommendations"
              multiline={true}
              options={nr.recommendationOpts}
              value={getMultiLineValue(tableType, "recommendations")}
              // onChange={createChangeHandler(tableType, "recommendations")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "recommendations")}
              onFocus={() => handleFieldFocus(tableType, "recommendations")}
              onBlur={() => handleMultiLineBlur(tableType, "recommendations")}
              menuPortalTarget={document.body}
              containerClasses={emptyInput.includes("Recommendations") ? "error-input" : ""}
            />
          </div>
        </TableCell>
        {/* Legal Reference */}

        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Legal Reference"
              multiline={false}
              options={nr.legalOpts}
              value={getMultiLineValue(tableType, "legal_reference")}
              // onChange={createChangeHandler(tableType, "legal_reference")}
              onSelect={(val) => handleMultiLineSelect(tableType, val, "legal_reference")}
              onFocus={() => handleFieldFocus(tableType, "legal_reference")}
              onBlur={() => handleMultiLineBlur(tableType, "legal_reference")}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Max Points */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Max Points"
              multiline={false}
              allowCreate={false}
              isScoring={true}
              value={newRowInputs[tableType]?.max_pts ?? 0}
              isDisabled={true}
            />
          </div>
        </TableCell>

        {/* Scores */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Score"
              multiline={false}
              allowCreate={false}
              isScoring={true}
              value={newRowInputs[tableType]?.score ?? 0}
              isDisabled={true}
            />
          </div>
        </TableCell>
        {/* system_implementation */}
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
        <TextField
          value={newRowInputs[tableType]?.system_implementation || ""}
          placeholder="System Implementation"
          onChange={(e) => handleChangeNewRow(tableType, e, "system_implementation")}
          // sx={{ width: "200px" }}
          sx={{
            width: "200px",
            "& .MuiInputBase-input::placeholder": {
              color: "#6d6d6d",
              opacity: 1,
            },

          }}
          InputProps={{
            sx: { fontSize: "10px" },
          }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={1.5}
        />
      </TableCell> */}
        {/* compliance_check */}
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
        <TextField
          value={newRowInputs[tableType]?.compliance_check || ""}
          placeholder="Compliance Check"
          onChange={(e) => handleChangeNewRow(tableType, e, "compliance_check")}
          // sx={{ width: "200px" }}
          sx={{
    width: "200px",
    "& .MuiInputBase-input::placeholder": {
      color: "#6d6d6d",
      opacity: 1,
    },
    
  }}
          InputProps={{
            sx: { fontSize: "10px" },
          }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={1.5}
        />
      </TableCell> */}

        {/* {image upload } */}
        <TableCell>
          {/* <div className="image-container">
          <div className="upload-container">
            <input
              type="file"
              accept="image/*"
              // onChange={(e) => handleImageUploadNewRow(tableType, e.target.files)}
              multiple
              style={{ color: "transparent" }}
              disabled={true}
            />
            {newRowInputs[tableType]?.imageUrls?.length === 0 && (
              <div className="no-file-chosen">No file chosen</div>
            )}
          </div>
        </div>
      </TableCell>
        <TableCell> */}
          {/* <Tooltips
            title="Please add the row to upload images"
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={2500}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [40, 0],
                    },
                  },
                ],
              },
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "6px 0", border: "1px solid grey", borderRadius: "5px", width: "100px", cursor: "not-allowed", position: "relative", background: "#f0f0f0", opacity: 0.6 }}>
              <CloudUploadOutlinedIcon size={30} />
            </div>
          </Tooltips> */}
          {bulkUploadedImages.length > 0 || newRowInputs[tableType]?.imageUrls?.length > 0
            ? (
            <Tooltips
              title="Assign Objective Evidence to Observation"
              placement="top"
              enterTouchDelay={0}
              leaveTouchDelay={2500}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [40, 0],
                      },
                    },
                  ],
                },
              }}
            >
              <div 
                // onClick={() => {setOpenGallery(true);setCurrentTableType(tableType);}}
                onClick={() => handleOpenGallery(tableType,{})}
                style={{
                        padding: "6px 0px",
                        border: "1px solid grey",
                        borderRadius: "5px",
                        width: "100px",
                        justifyContent: "center",
                        alignItems:"center",
                        display: "flex",
                      }}
                >
                <CloudUploadOutlinedIcon fontSize="30" />
              </div>
            </Tooltips>
            )
            : "Upload Photos"
          }
        </TableCell>
        <TableCell>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CheckIcon
              onClick={() => handleAddRow(tableType)}
              style={{ cursor: "pointer", color: "green" }}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }, [
    safeGroupedObservations,
    newRowInputs,
    newRowInputValues,
    newRowDropdownOptionsByTableType,
    createChangeHandler,
    bulkUploadedImages,
    emptyInput
  ]);

  const handleChangeNewRow = (tableType, e, field) => {
    if (field === "score" && (e.target.value < 0 || e.target.value > 5)) {
      alert("Invalid score. Please enter a value between 0 and 5.");
      return; // Exit the function if the score is invalid
    }
    if (field == "score" && (e.target.value.includes("."))) {
      return;
    }
    if (
      (field === "area" || field === "category" || field === "criticality") &&
      e.target.value.length > 100
    ) {
      alert(`${field} cannot exceed 100 characters.`);
      return; // Do not update the state if validation fails
    }

    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: e.target.value,
      },
    }));
  };
  const handleChangeNewRowSelect = (tableType, selectedOption, field) => {
    const value = selectedOption?.value || "";

    const maxLength = field === "check_points" ? 1000 : 100;
    // Skip character validation for observation, recommendations, and legal_reference fields
    if (
      field !== "observation" &&
      field !== "recommendations" &&
      field !== "legal_reference" &&
      value.length > maxLength
    ) {
      alert(`${field} cannot exceed ${maxLength} characters.`);
      return;
    }

    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: value,
      },
    }));
    // Set the input value so it appears as editable text
    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: "",
      },
    }));

    if (field === "area") {
      let calculatedScore = 0;
      let calculatedMaxPoints = 0;
      // Clear both equipment and category fields when area changes
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,

        },
      }));
      // Use cache instead of filtering allData
      setNewRowEquipmentList(getEquipmentOptions(value));
      setNewRowHseCategoryList([]);
      setNewRowCheckpointList([]);
      setNewRowCriticalityList([]);
      setNewRowStatusList([]);
      setNewRowObservationList([]);
      setNewRowRecommendationList([]);
      setNewRowLegalReferenceList([]);
    }
    if (field === "equipment") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear the category field when equipment changes and populate category list
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      // Use cache instead of filtering allData
      setNewRowHseCategoryList(getCategoryOptions(newRowInputs[tableType]?.area, value));
      setNewRowCheckpointList([]);
      setNewRowCriticalityList([]);
      setNewRowStatusList([]);
      setNewRowObservationList([]);
      setNewRowRecommendationList([]);
      setNewRowLegalReferenceList([]);
    }
    if (field === "category") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear the checkpoint field when category changes and populate checkpoint list
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      // Use cache instead of filtering allData
      setNewRowCheckpointList(getCheckpointOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, value));
      setNewRowCriticalityList([]);
      setNewRowStatusList([]);
      setNewRowObservationList([]);
      setNewRowRecommendationList([]);
      setNewRowLegalReferenceList([]);
    }
    if (field === "check_points") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear the criticality field when checkpoint changes and populate criticality list
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      // Use cache instead of filtering allData
      setNewRowCriticalityList(getCriticalityOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, value));
      setNewRowStatusList([]);
      setNewRowObservationList([]);
      setNewRowRecommendationList([]);
      setNewRowLegalReferenceList([]);
    }
    if (field === "criticality") {
      // Calculate score based on criticality and current status (if exists)
      const currentStatus = newRowInputs[tableType]?.status || "";
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      if (value && currentStatus) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === value?.trim() &&
            score.status === currentStatus?.trim()
        );

        calculatedScore =
          matchingScore?.earned ??
          matchingScore?.score ??
          "0";

        calculatedMaxPoints =
          matchingScore?.max_pts ??
          "0";
      }

      // Clear the status field when criticality changes and populate status list
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      // Use cache instead of filtering allData
      setNewRowStatusList(getStatusOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, value));
      setNewRowObservationList([]);
      setNewRowRecommendationList([]);
      setNewRowLegalReferenceList([]);
    }
    if (field === "status") {
      // Calculate score based on current criticality and new status
      const currentCriticality = newRowInputs[tableType]?.criticality || "";
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      if (currentCriticality && value) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === currentCriticality?.trim() &&
            score.status === value?.trim()
        );
        calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? "0";
        calculatedMaxPoints = matchingScore?.max_pts ?? "0";
      }

      // Clear observation, recommendations, and legal reference when status changes
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));
      // Use cache instead of filtering allData
      setNewRowObservationList(getObservationOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, value));
      setNewRowRecommendationList(getRecommendationOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, value));
      setNewRowLegalReferenceList(getLegalReferenceOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, value));
    }
  };

  const handleInputChange = (tableType, inputValue, field) => {
    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: inputValue,
      },
    }));
  };

  const handleFieldFocus = (tableType, field) => {
    // When focusing a field with a value, convert it to editable text
    const currentValue = newRowInputs[tableType]?.[field];
    if (currentValue) {
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          [field]: currentValue,
        },
      }));
    }

    // remove validation errors
    if(emptyInput.length)
      setEmptyInput([]);
  };


  const handleFieldBlur = (tableType, field) => {
    // Save the edited value on blur
    const inputValue = newRowInputValues[tableType]?.[field];
    if (inputValue !== undefined && inputValue !== "") {
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          [field]: inputValue.trim(),
        },
      }));
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          [field]: "",
        },
      }));
    }
  };

  const handleEditInputChange = (observationId, inputValue, field) => {
    setEditInputValues((prev) => ({
      ...prev,
      [`${observationId}-${field}`]: inputValue,
    }));
  };

  // const handleEditFieldFocus = (observationId, field) => {
  //   // Check for unsaved row before allowing focus
  //   const observation = allData?.data?.find((e) => e.sr_no === observationId);
  //   if (!observation) return;

  //   if (currentEditedRow !== -1 && currentEditedRow !== observationId) {
  //     const unsavedRow = selectedObservations.find(
  //       (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
  //     );
  //     const unsavedTableType = unsavedRow?.table_type || "another table";
  //     const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
  //       ? safeGroupedObservations[unsavedTableType].findIndex(
  //           (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
  //         )
  //       : -1;
  //     const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

  //     toast.warning(
  //       unsavedSrNo
  //         ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
  //         : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
  //     );
  //     return;
  //   }

  //   // Set editing state
  //   setIsEditing(true);
  //   setCurrentEditedRow(observationId);
  //   setIsReportEdited(true);

  //   // When focusing a field with a value, convert it to editable text
  //   const currentValue = observation[field];
  //   if (currentValue) {
  //     setEditInputValues((prev) => ({
  //       ...prev,
  //       [`${observationId}-${field}`]: currentValue,
  //     }));
  //   }
  // };

  const handleEditFieldFocus = (observationId, field) => {
    const observation = allData?.data?.find((e) => e.sr_no === observationId);
    if (!observation) return;

    // Check for unsaved row
    if (currentEditedRow !== -1 && currentEditedRow !== observationId) {
      const unsavedRow = selectedObservations.find(
        (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
      );
      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
        ? safeGroupedObservations[unsavedTableType].findIndex(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        )
        : -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    // Always populate input value for editable text, even on same row
    const currentValue = observation[field];
    if (currentValue) {
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationId}-${field}`]: currentValue,
      }));
    }

    // Only set editing state when switching to a different row
    if (currentEditedRow !== observationId) {
      setIsEditing(true);
      setCurrentEditedRow(observationId);
      setIsReportEdited(true);
    }
  };

  const handleEditFieldBlur = (observationId, field) => {
    console.log('-- handleEditFieldBlur',observationId);
    // Save the edited value on blur
    const inputValue = editInputValues[`${observationId}-${field}`];
    if (inputValue !== undefined && inputValue !== "") {
      const observation = allData?.data?.find((e) => e.sr_no === observationId);
      // observation.emptyFields = [];
      if (observation) {
        handleCellEdit(
          { value: inputValue.trim(), label: inputValue.trim() },
          allData?.data?.findIndex((e) => e.sr_no === observationId),
          field,
          observation[field],
          observation
        );
      }
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationId}-${field}`]: "",
      }));
    }
  };

  useEffect(() => {
  console.log("emptyInput:", emptyInput);
}, [emptyInput]);

  const handleAddRow = (tableType) => {
    console.log('save observation---');

    // handleAddRow queues the new row into editedObservations and then
    // triggers an auto-save (setNewObservationAdded), which processes the
    // *entire* editedObservations queue. If another row is still mid-edit,
    // that row's in-progress (possibly incomplete) changes would ride along
    // and get persisted as a side effect of just adding a new row. Block
    // adding until that edit is finished, so this function only ever
    // touches the new row it creates.
    if (isEditing && currentEditedRow !== -1) {
      toast.warning("Please save the observation you're currently editing before adding a new row.");
      return;
    }

    // Generate a unique sr_no
    // const newSrNo = Date.now();
    const newSrNo = Date.now() % 1000000;
    // Create the new row with a fixed key, unique sr_no, and other properties
    const newRow = {
      ...newRowInputs[tableType],
      sr_no: newSrNo, // Assign the unique sr_no
      table_type: tableType,
      is_selected: 1,
      sector_type: selectedSector.value,
      isNew: true,
    };

    // Initially set score to 0, then update using filter function
    let calculatedScore = 0;
    let calculatedMaxPoints = 0;
    // Filter allScores based on criticality and status to get the earned score
    if (newRow.criticality && newRow.status) {
      const matchingScore = allScores.find(
        (score) =>
          score.criticality === newRow.criticality?.trim() &&
          score.status === newRow.status?.trim()
      );
      calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? 0;
      calculatedMaxPoints = matchingScore?.max_pts ?? 0;
    }

    // Replace empty fields with "N/A"
    const updatedRow = {
      ...newRow,
      area: newRow.area,
      category: newRow.category?.trim() || "N/A",
      check_points: newRow.check_points?.trim() || "N/A",
      observation: newRow.observation,
      criticality: newRow.criticality?.trim() || "N/A",
      recommendations: newRow.recommendations?.trim() || "N/A",
      max_pts: calculatedMaxPoints,
      score: calculatedScore,
      system_implementation: newRow.system_implementation?.trim() || "N/A",
      compliance_check: newRow.compliance_check?.trim() || "N/A",
      legal_reference: newRow.legal_reference?.trim() || "N/A",
      imageUrls: newRow.imageUrls?.length > 0 ? newRow.imageUrls : [],
    };

    // Validate the required fields
    const emptyFields = [];
    if (!newRow.area || newRow.area === "") emptyFields.push("Area");
    if (!newRow.equipment || newRow.equipment === "") emptyFields.push("Equipment");
    if (!newRow.category || newRow.category === "") emptyFields.push("Category");
    if (!newRow.check_points || newRow.check_points === "") emptyFields.push("Checkpoint");
    if (!newRow.criticality || newRow.criticality === "") emptyFields.push("Criticality");
    if (!newRow.status || newRow.status === "") emptyFields.push("Status");
    if (!newRow.observation || newRow.observation === "") emptyFields.push("Observation");
    if (!newRow.recommendations || newRow.recommendations === "") emptyFields.push("Recommendations");
    // if(!newRow.legal_reference || newRow.legal_reference === "") emptyFields.push("Legal Reference");

    if (emptyFields.length > 0) {
      setEmptyInput(emptyFields);
      // newRow.emptyFields = emptyFields;
      toast.warning(`${emptyFields.join(", ")} can't be set empty`);
      console.log('setEmptyInput: ',emptyInput);
      return
    }

    // Batch state updates to reduce re-renders
    setIsReportEdited(true);

    // Update all observation states in a single batch
    setEditedObservations((prevObservations) => [...prevObservations, updatedRow]);
    setSelectedObservations((prev) => [...prev, updatedRow]);
    setAllObservations((prev) => [...(prev || []), updatedRow]);

    // Clear the new row inputs
    initializeNewRowInputs(tableType, selectedSector.value);

    // Clear the dropdown option lists to prevent showing stale options from the previous row
    setNewRowEquipmentList([]);
    setNewRowHseCategoryList([]);
    setNewRowCheckpointList([]);
    setNewRowCriticalityList([]);
    setNewRowStatusList([]);
    setNewRowObservationList([]);
    setNewRowRecommendationList([]);
    setNewRowLegalReferenceList([]);
    setNewObservationAdded(true);
  };
 
  useEffect(() => {
    if(screenNumber === 9 && newObservationAdded){
      handleSave();
      setNewObservationAdded(false);
      console.log('---------- auto save --------');
    }
  }, [newObservationAdded]);

  const riskLevels = [
    {
      range: "≥ 85%",
      risk: "Low Risk",
      color: "#00A651",
      interpretation: "Electrical systems and controls are well maintained, only minor improvements needed.",
    },
    {
      range: "65% – 84%",
      risk: "Medium Risk",
      color: "#FFFF00",
      interpretation: "Controls are adequate but with noticeable weaknesses.",
    },
    {
      range: "25% – 64%",
      risk: "High Risk",
      color: "#FFA500",
      interpretation: "High vulnerabilities with major compliance gaps.",
    },
    {
      range: "< 25%",
      risk: "Severe Risk",
      color: "#FF0000",
      interpretation: "Controls are ineffective, urgent corrective action required.",
    },
  ];
  const getRiskLevel = (score) => {
    if (score >= 85) return riskLevels[0];
    if (score >= 65) return riskLevels[1];
    if (score >= 25) return riskLevels[2];
    return riskLevels[3];
  };

  const currentRisk = getRiskLevel(scorePercent);
  const [references, setReferences] = useState([]);
  const [newHeading, setNewHeading] = useState("");
  // "Add row" drafts, one per section, keyed by the section name so each section
  // can be managed independently. Shape: { [sectionName]: { document, relevance } }.
  const [newRowsBySection, setNewRowsBySection] = useState({});
  // Annexure ids hidden for THIS report only (report-specific delete).
  const [deletedAnnexureIds, setDeletedAnnexureIds] = useState(
    Array.isArray(selectedReportData?.deleted_annexure_ids)
      ? selectedReportData.deleted_annexure_ids
      : []
  );

  // Keep hidden-ids in sync when the report (or its hidden list) changes.
  useEffect(() => {
    if (Array.isArray(selectedReportData?.deleted_annexure_ids)) {
      setDeletedAnnexureIds(selectedReportData.deleted_annexure_ids);
    } else {
      setDeletedAnnexureIds([]);
    }
  }, [
    selectedReportData?.report_id,
    JSON.stringify(selectedReportData?.deleted_annexure_ids),
  ]);

  // Annexures the user has NOT hidden for this report — used for view + export.
  const visibleReferences = references.filter(
    (ref) => !deletedAnnexureIds.includes(ref.id)
  );

  // Group references under their section headings, then flatten back to a
  // section-ordered list for the exporters. Shared pure helpers (Saved / Preview
  // / View all use these) — see utils/annexureGrouping + its unit tests.
  const groupedReferences = useMemo(
    () => groupReferences(visibleReferences),
    [visibleReferences]
  );
  const orderedVisibleReferences = useMemo(
    () => orderReferences(groupedReferences),
    [groupedReferences]
  );


  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      // const res = await axios.get("http://localhost:5000/api/references");
      const res = await axios.get(
        `${config.PATH}/api/get-all-annexure-details`
      );;

      setReferences(res.data);
    } catch (err) {
      console.error("Error fetching references:", err);
    }
  };

  // Add a data row INTO a specific section. The row carries that section's name
  // so the backend can store and later group / update / delete it per-section.
  const handleAddSectionRow = async (sectionName) => {
    const draft = newRowsBySection[sectionName] || { document: "", relevance: "" };
    if (!draft.document.trim() || !draft.relevance.trim()) {
      toast.error("Please fill both fields before adding.");
      return;
    }
    try {
      const res = await axios.post(`${config.PATH}/api/create-annexure-details`, {
        document: draft.document,
        relevance: draft.relevance,
        type: "data",
        section_name: sectionName,
      });
      setReferences([...references, res.data]);
      setNewRowsBySection((prev) => ({
        ...prev,
        [sectionName]: { document: "", relevance: "" },
      }));
    } catch (err) {
      // Backend validation (duplicate / spelling / string) returns 400 with a message.
      const msg =
        err?.response?.data?.error || "Error adding annexure. Please try again.";
      toast.error(msg);
      console.error("Error adding reference:", err);
    }
  };

  // Update one field of a section's pending "add row" draft.
  const handleNewRowChange = (sectionName, field, value) => {
    setNewRowsBySection((prev) => ({
      ...prev,
      [sectionName]: {
        document: "",
        relevance: "",
        ...(prev[sectionName] || {}),
        [field]: value,
      },
    }));
  };

  const handleAddHeading = async () => {
    if (!newHeading.trim()) {
      toast.error("Please enter section heading text.");
      return;
    }
    try {
      const res = await axios.post(`${config.PATH}/api/create-annexure-details`, {
        document: null,
        relevance: "",
        type: "heading",
        section_name: newHeading,
      });
      setReferences([...references, res.data]);
      setNewHeading("");
    } catch (err) {
      const msg =
        err?.response?.data?.error || "Error adding heading. Please try again.";
      toast.error(msg);
      console.error("Error adding heading:", err);
    }
  };

  // Report-specific delete: hides the annexure for THIS report only (the global
  // annexure row is preserved and still available to other reports).
  const handleDelete = async (id) => {
    if (!window.confirm("Hide this annexure for this report?")) return;
    const reportId = selectedReportData?.report_id;
    if (!reportId) {
      toast.error("Report not found. Cannot delete annexure.");
      return;
    }
    try {
      const res = await axios.patch(
        `${config.PATH}/api/reports/${reportId}/annexure/${id}/hide`
      );
      setDeletedAnnexureIds(
        Array.isArray(res?.data?.deleted_annexure_ids)
          ? res.data.deleted_annexure_ids
          : [...deletedAnnexureIds, id]
      );
    } catch (err) {
      toast.error("Could not delete annexure for this report.");
      console.error("Error hiding reference for report:", err);
    }
  };

  // Report-specific section delete: hides the heading AND every row under it for
  // THIS report only, so the section's rows are never left orphaned with no
  // heading. The global annexure rows are preserved for other reports.
  const handleDeleteSection = async (headingRef) => {
    if (!window.confirm("Hide this section and its rows for this report?")) return;
    const reportId = selectedReportData?.report_id;
    if (!reportId) {
      toast.error("Report not found. Cannot delete section.");
      return;
    }
    const group = groupedReferences.find((g) => g.key === headingRef.id);
    const idsToHide = [headingRef.id, ...(group ? group.rows.map((r) => r.id) : [])];
    try {
      // Sequential so each hide builds on the server's prior deleted_annexure_ids
      // (the JSON column is read-modify-write; parallel calls would race).
      let latest = null;
      for (const id of idsToHide) {
        const res = await axios.patch(
          `${config.PATH}/api/reports/${reportId}/annexure/${id}/hide`
        );
        if (Array.isArray(res?.data?.deleted_annexure_ids)) {
          latest = res.data.deleted_annexure_ids;
        }
      }
      setDeletedAnnexureIds(
        latest || [...new Set([...deletedAnnexureIds, ...idsToHide])]
      );
    } catch (err) {
      toast.error("Could not delete section for this report.");
      console.error("Error hiding section for report:", err);
    }
  };

  const cleanHTML = (html) => {
    if (!html) return "";

    return html
      // (<p><br></p>)
      .replace(/(<p>\s*(<br\s*\/?>)*\s*<\/p>){2,}/gi, "<p><br></p>")
      .replace(/(<p>\s*(<br\s*\/?>)*\s*<\/p>)+/gi, "")
      // multiple <p> gaps by replacing with a single one
      .replace(/<\/p>\s*<p>\s*<\/p>\s*<p>/gi, "</p><p>")
      // consecutive <p> tags (large paragraph gaps)
      .replace(/<\/p>\s*<p>/g, "</p><br><p>")
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br>")
      .replace(/¶/g, "")        // Remove literal pilcrow
      .replace(/&para;/g, "")   // Remove encoded pilcrow
      .replace(/\u00B6/g, "")
      .trim();
  };
  const stateOptions = [
  { label: "Andhra Pradesh", value: "Andhra Pradesh" },
  { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
  { label: "Assam", value: "Assam" },
  { label: "Bihar", value: "Bihar" },
  { label: "Chhattisgarh", value: "Chhattisgarh" },
  { label: "Goa", value: "Goa" },
  { label: "Gujarat", value: "Gujarat" },
  { label: "Haryana", value: "Haryana" },
  { label: "Himachal Pradesh", value: "Himachal Pradesh" },
  { label: "Jharkhand", value: "Jharkhand" },
  { label: "Karnataka", value: "Karnataka" },
  { label: "Kerala", value: "Kerala" },
  { label: "Madhya Pradesh", value: "Madhya Pradesh" },
  { label: "Maharashtra", value: "Maharashtra" },
  { label: "Manipur", value: "Manipur" },
  { label: "Meghalaya", value: "Meghalaya" },
  { label: "Mizoram", value: "Mizoram" },
  { label: "Nagaland", value: "Nagaland" },
  { label: "Odisha", value: "Odisha" },
  { label: "Punjab", value: "Punjab" },
  { label: "Rajasthan", value: "Rajasthan" },
  { label: "Sikkim", value: "Sikkim" },
  { label: "Tamil Nadu", value: "Tamil Nadu" },
  { label: "Telangana", value: "Telangana" },
  { label: "Tripura", value: "Tripura" },
  { label: "Uttar Pradesh", value: "Uttar Pradesh" },
  { label: "Uttarakhand", value: "Uttarakhand" },
  { label: "West Bengal", value: "West Bengal" }
];

  const isProceedDisabled =
    !selectedOrganization ||
    !selectedSite ||
    !startDate ||
    !endDate;
  const mobileWidth = window.innerWidth <= 480;
// console.log('screenNumber: ',screenNumber);
  if (screenNumber === 1) {
    return (
      <div>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="modal-container">
            <div className="modal-header">
              <Typography variant="h5">
                {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
              </Typography>
              <button className="custom-close-button" onClick={handleClose}>
                &#10005; {/* Unicode for 'X' */}
              </button>
            </div>
            <div className="modal-content">
              <div className="modal-body">
                <Typography variant="body1" component="div">
                  <div className="select-container">
                    <div className="report-id">
                      Report ID: {`${selectedReportData.report_id}`}
                    </div>
                    <CreatableSelect
                      placeholder="Organization"
                      options={orgList.map((e) => ({
                        label: e.org_name,
                        value: e.id,
                      }))}
                      onChange={handleOrganizationSelection}
                      value={selectedOrganization}
                      isSearchable
                      isClearable
                      isDisabled
                    />
                  </div>
                  <div className="select-container">
                    
                    <CreatableSelect
                      placeholder="Site"
                      options={siteOptions}
                      onChange={handleSiteSelection}
                      value={selectedSite}
                      isSearchable
                      isClearable
                      // isDisabled={!selectedOrganization}
                      isDisabled
                    />
                    <CreatableSelect
                      placeholder="Select State"
                      options={stateOptions}
                      onChange={handleStateChange}
                      value={selectedState}
                      isSearchable
                      isClearable
                    />

                  </div>
                  <div className="flex-container-start-end-date">
                    <div className="to-date-from-date">
                      <DatePicker
                        selected={new Date(startDate)}
                        onChange={(e) => handleStartEndDate(e, "start-date")}
                        className="class-for-date-pickers"
                        placeholderText="Audit Start Date"
                        dateFormat="dd-MM-yyyy"
                        utcOffset={0}
                        maxDate={new Date(endDate)}
                        todayButton={"Today"}
                      />
                    </div>
                    <div className="to-date-from-date">
                      <DatePicker
                        selected={new Date(endDate)}
                        onChange={(e) => handleStartEndDate(e, "end-date")}
                        className="class-for-date-pickers"
                        placeholderText="Audit End Date"
                        dateFormat="dd-MM-yyyy"
                        utcOffset={0}
                        minDate={new Date(startDate)}
                        todayButton={"Today"}
                      />
                    </div>
                  </div>
                  {/* from and to time */}
                  {/* <div className="flex-container-start-end-date">
                    <div className="to-date-from-date">
                      <label>Time of Audit From:</label>
                      <input
                        type="time"
                        value={timeFrom}
                        onChange={(e) => setTimeFrom(e.target.value)}
                        className="class-for-time-pickers"
                      />

                    </div>
                    <div className="to-date-from-date">
                      <label>Time of Audit To:</label>
                      <input
                        type="time"
                        value={timeTo}
                        onChange={(e) => setTimeTo(e.target.value)}
                        className="class-for-time-pickers"
                      />
                    </div>
                  </div> */}
                  <div className="flex-container-start-end-date">
                    <div className="to-date-from-date">
                      <DatePicker
                        selected={timeFrom ? new Date(`1970-01-01T${timeFrom}:00`) : null}
                        onChange={(date) => {
                          if (date) {


                            const hours = date.getHours().toString().padStart(2, "0");
                            const minutes = date.getMinutes().toString().padStart(2, "0");
                            setTimeFrom(`${hours}:${minutes}`);
                          }
                          else {
                            setTimeFrom("")
                          }
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15} // 15 minute intervals
                        timeCaption="Time"
                        dateFormat="HH:mm"
                        // Allow typing manually
                        customInput={<input type="text" />}
                        placeholderText="Time of Audit From" className="class-for-date-pickers" />
                    </div>
                    <div className="to-date-from-date">
                      <DatePicker
                        selected={timeTo ? new Date(`1970-01-01T${timeTo}:00`) : null}
                        onChange={(date) => {
                          if (date) {
                            const hours = date.getHours().toString().padStart(2, "0");
                            const minutes = date.getMinutes().toString().padStart(2, "0");
                            setTimeTo(`${hours}:${minutes}`);
                          }
                          else {
                            setTimeTo("")
                          }
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="HH:mm"
                        customInput={<input type="text" />}
                        placeholderText="Time of Audit To"
                        className="class-for-date-pickers" />
                    </div> </div>
                  {/* brief property Descriptionand  Number of Floors  */}

                  <div className="select-container " style={{ marginTop: "10px" }}>
                    {/* Brief Property Description */}
                    <input
                      type="text"
                      placeholder="Brief Property Description"
                      value={briefPropertyDescription}
                      onChange={(e) => setBriefPropertyDescription(e.target.value)}
                      className="class-for-selects"
                    />

                    {/* Number of Floors */}
                    <input
                      type="text"
                      placeholder="Number of Floors"
                      value={numOfFloors}
                      onChange={(e) => setNumOfFloors(e.target.value)}
                      className="class-for-selects"
                      maxLength={20}
                    />
                  </div>
                  {/* average staff footfall and num of objection certificate */}
                  <div className="select-container " style={{ marginTop: "10px" }}>
                    <input
                      type="text"
                      placeholder="Average Staff Footfall "
                      value={avgStaffFootfall}
                      onChange={(e) => setAvgStaffFootfall(e.target.value)}
                      className="class-for-selects"
                    />
                    <input
                      type="text"
                      placeholder="No Objection Certificate: "
                      value={noObjectionCertificate}
                      onChange={(e) => setNoObjectionCertificate(e.target.value)}
                      className="class-for-selects"
                    />
                  </div>
                  {/* national building code and coordinationg person div  */}
                  <div className="select-container " style={{ marginTop: "10px" }}>
                    <input
                      type="text"
                      placeholder="National Building Code Category "
                      value={nationalBuildingCodeCategory}
                      onChange={(e) => setNationalBuildingCodeCategory(e.target.value)}
                      className="class-for-selects"
                      maxLength={100}
                    />
                    <input
                      type="text"
                      placeholder="Coordinating Person – Client Side "
                      value={coordinationgPersonClientside}
                      onChange={(e) => setCoordinationgPersonClientside(e.target.value)}
                      className="class-for-selects"
                      maxLength={100}
                    />
                  </div>

                  {/* reported prepared by, reviewd by */}
                  <div className="select-container " style={{ marginTop: "10px" }}>
                    <input
                      type="text"
                      placeholder="Report Prepared By "
                      value={reportPreparedBy}
                      onChange={(e) => setReportPreparedBy(e.target.value)}
                      className="class-for-selects"
                      maxLength={100}
                    />
                    <input
                      type="text"
                      placeholder="Report Reviewed By "
                      value={reportReviewedBy}
                      onChange={(e) => setReportReviewedBy(e.target.value)}
                      className="class-for-selects"
                      maxLength={100}
                    />
                  </div>
                  {/* Building Name */}
                  <div className="select-container " style={{ marginTop: "10px" }}>
                    <input
                      type="text"
                      placeholder="Building Name"
                      value={buildingName}
                      onChange={(e) => setBuildingName(e.target.value)}
                      className="class-for-selects"
                      maxLength={100}
                    />
                    <CreatableSelect
                      placeholder="Report Type"
                      options={sectors()}
                      onChange={handleChangeSector}
                      value={selectedSector}
                      isSearchable
                      isClearable
                      isDisabled
                    />
                  </div>









                  {/* <div style={{ gap: "10px" }} className="flex-container">
                    <Select
                      className="select"
                      placeholder="Sector"
                      options={sectors()}
                      onChange={(e) => handleChangeSector(e)}
                      // isMulti={true}
                      value={selectedSector}
                      isClearable
                      defaultValue={selectedSector}
                    />
                    <Select
                      className="select"
                      placeholder="Parameter"
                      options={paramsOption()}
                      onChange={(e) => handleChangeParam(e)}
                      // isMulti={true}
                      value={selectedParam}
                      isClearable
                      isMulti
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          maxHeight: '80px',  // Limit height of selected values
                          overflowY: 'auto',  // Enable scrolling
                          display: 'flex',
                          flexWrap: 'wrap',
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          maxHeight: '80px',  // Keep value container from expanding
                          overflowY: 'auto',  // Add scrollbar when needed
                        }),
                      }}
                    />
                    <Select
                      className="select"
                      placeholder="Area"
                      options={areaList.map((area) => ({
                        label: area,
                        value: area,
                      }))}
                      onChange={(e) => handleChangeArea(e)}
                      isMulti={true}
                 
                         value={selectedArea.map((area) => ({
                        label: area,
                        value: area,
                      }))}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          maxHeight: '80px',  // Limit height of selected values
                          overflowY: 'auto',  // Enable scrolling
                          display: 'flex',
                          flexWrap: 'wrap',
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          maxHeight: '80px',  // Keep value container from expanding
                          overflowY: 'auto',  // Add scrollbar when needed
                        }),
                      }}
                    />
                  </div> */}
                </Typography>
                <div className="observation-and-global-search">

                  {(AllObservations?.length ?? 0) > 0 && (selectedArea?.length ?? 0) > 0 ?
                    <div className="observations-container">
                      {/* <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search Observations"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                      <div>
                        <button
                          onClick={handleOpenDrawer}
                          className="search-bar-button"
                        >
                          <span className="search-icon">&#128269;</span>{" "}
                         
                          <span className="search-text">
                            All Observations
                          </span>
                        </button>

                        <ObservationsDrawer
                          isOpen={isDrawerOpen}
                          onClose={handleCloseDrawer}
                          groupedData={groupedData}
                          globalSearchTerm={globalSearchTerm}
                          setGlobalSearchTerm={setGlobalSearchTerm}
                        />
                      </div>
                    </div> */}

                      {(AllObservations?.length ?? 0) > 0 && (selectedArea?.length ?? 0) > 0 ? (
                        <div className="ao-observations-list">
                          {Object.entries(groupedByTableType || {}).map(([table_type, areas]) => (
                            <div key={table_type} className="table-group">
                              <h4>
                                Parameter : <u>{table_type}</u>
                              </h4>

                              {Object.entries(areas || {}).map(([area, observationsInArea], index) => (
                                <Accordion
                                  key={`${table_type}-${area}`}
                                  expanded={expanded === `${table_type}-${area}`}
                                  onChange={handleChangeAccordion(`${table_type}-${area}`)}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`ao-${index}-content`}
                                    id={`ao-${index}-header`}
                                  >
                                    <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
                                      Area: {area}
                                    </Typography>
                                    <Typography component="span" sx={{ color: "text.secondary" }}>
                                      {(observationsInArea || []).filter((obs) => obs.is_selected === 1).length} out of{" "}
                                      {(observationsInArea || []).length} observations selected
                                    </Typography>
                                  </AccordionSummary>

                                  <AccordionDetails>
                                    {(observationsInArea || []).map((observation, obsIndex) => (
                                      <div key={obsIndex} className="observation-item-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={observation.is_selected === 1}
                                          onChange={() =>
                                            handleObservationSelection(
                                              observation,
                                              (AllObservations || []).indexOf(observation)
                                            )
                                          }
                                        />
                                        <span>
                                          {observation.observation}
                                          {/* (<span style={{ fontWeight: "bold" }}>{observation.category}</span>) */}
                                        </span>
                                        {observation.isNote && <span className="note-label">(Note)</span>}
                                      </div>
                                    ))}
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-observations">No observations available.</div>
                      )}


                    </div>
                    : ""}
                </div>
                {/* <div className="get-recommendations-container">
                  <button
                    className="button-styles"
                    onClick={handleProceed}
                    disabled={
                      // selectedObservations.length === 0 ||
                      !selectedOrganization ||
                      !selectedSite ||
                      !startDate ||
                      !endDate
                      // ||
                      // !timeFrom||!timeTo||!reportReviewedBy||!reportPreparedBy||!noObjectionCertificate||
                      // !numOfFloors||!nationalBuildingCodeCategory||!coordinationgPersonClientside||!briefPropertyDescription
                      // ||!avgStaffFootfall
                    }
                    style={{
                      background:
                        // selectedObservations.length === 0 ||
                        !selectedOrganization ||
                          !selectedSite ||
                          !startDate ||
                          !endDate
                      //     ||
                      // !timeFrom||!timeTo||!reportReviewedBy||!reportPreparedBy||!noObjectionCertificate||
                      // !numOfFloors||!nationalBuildingCodeCategory||!coordinationgPersonClientside||!briefPropertyDescription
                      // ||!avgStaffFootfall
                          ? "lightgrey"
                          : "#efc71d",
                    }}
                  >
                    Proceed
                  </button>
                </div> */}
                <div className="get-recommendations-container">
                  <Tooltips
                    title={isProceedDisabled ? "Please complete Organization, site and dates fields before proceeding" : ""}
                    placement={mobileWidth ? "top" : "left"}
                    // disablePortal={true}
                    PopperProps={{ disablePortal: true }}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                  >
                    <div style={{ display: "inline-block" }}>
                      <button
                        onClick={handleProceed}
                        disabled={isProceedDisabled}
                        style={{
                          background: isProceedDisabled ? "lightgrey" : "#efc71d",
                          cursor: isProceedDisabled ? "not-allowed" : "pointer",
                        }}
                        className="button-styles"
                      >
                        Proceed
                      </button>
                    </div>
                  </Tooltips>
                </div>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </div>
    );
  }
  else if (screenNumber === 2) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      UNDERSTANDING THE REPORT
                    </div>
                    <JoditEditor
                      // ref={editor}
                      ref={contentEditor}
                      placeholder="Enter your text here"
                      value={contents}
                      key={screenNumber} config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setContents(newContent)} // still update on blur
                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </>
    );
  }
  else if (screenNumber === 3) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                {/* <Typography variant="h6">
                  {module === "cmv" ? " Electrical CMV Report" : "Saved Report"} 
                </Typography> */}
                <Typography variant="h5">Saved Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div className="sub-headings">
                    AUDIT DESCRIPTION
                  </div>
                  <div className="review-table-wrapper" style={{ borderRadius: "5px" }}>
                    <table style={{ width: "100%", fontFamily: "montserrat", borderCollapse: "collapse", borderRadius: "5px" }}>
                      <tbody>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC", }}>Client</td>
                          <td>{selectedOrganization.label}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC", }}>Location</td>
                          <td>{selectedSite.label}{selectedState && selectedState?.label != null ? ", "+selectedState.label:""}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC", }}>Date of Site Visit</td>
                          <td>{new Date(startDate).getDate()}-{new Date(startDate).getMonth() + 1
                          }-{new Date(startDate).getFullYear()}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC", }}>Study</td>
                          <td>HSE Audit report</td>
                        </tr>
                        {(timeFrom || timeTo) ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Time of Audit (From & To)</td>
                            {/* <td>{timeFrom.slice(0, 5)} to {timeTo.slice(0, 5)}</td> */}
                            <td>
                              {
                                (timeFrom && timeTo
                                  ? `${timeFrom.slice(0, 5)} to ${timeTo.slice(0, 5)}`
                                  : timeFrom
                                    ? `${timeFrom.slice(0, 5)} to N/A`
                                    : timeTo
                                      ? `N/A to ${timeTo.slice(0, 5)}`
                                      : "N/A") + (" (Date wise breakup)")}
                            </td>

                          </tr>
                          : []}
                        {briefPropertyDescription ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Brief Property Description</td>
                            <td>{briefPropertyDescription}</td>
                          </tr>
                          : []}
                        {numOfFloors ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Number of floors</td>
                            <td>{numOfFloors}</td>
                          </tr>
                          : []}
                        {avgStaffFootfall ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Average Staff Footfall</td>
                            <td>{avgStaffFootfall}</td>
                          </tr>
                          : []}
                        {noObjectionCertificate ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>No Objection Certificate:</td>
                            <td>{noObjectionCertificate}</td>
                          </tr>
                          : []}
                        {nationalBuildingCodeCategory ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>National Building Code Category</td>
                            <td>{nationalBuildingCodeCategory}</td>
                          </tr>
                          : []}
                        {coordinationgPersonClientside ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Coordinating Person – Client Side</td>
                            <td>{coordinationgPersonClientside}</td>
                          </tr>
                          : []}
                        {reportPreparedBy ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Report Prepared By</td>
                            <td>{reportPreparedBy}</td>
                          </tr>
                          : []}
                        {reportReviewedBy ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Report Reviewed By</td>
                            <td>{reportReviewedBy}</td>
                          </tr>
                          : []}
                        {buildingName ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC", }}>Building Name</td>
                            <td>{buildingName}</td>
                          </tr>
                          : []}
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC", }}>Date of Submission of Report</td>
                          <td>{new Date(endDate).getDate()}-{new Date(endDate).getMonth() + 1
                          }-{new Date(endDate).getFullYear()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 4) {
    return (
      <div>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      CLASSIFICATION OF AUDIT OBSERVATIONS
                    </div>
                    <JoditEditor
                      ref={classificationEditor}
                      placeholder="Enter your text here"
                      value={classificationOfAuditObservations}
                      key={screenNumber}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setClassificationOfAuditObservations(newContent)}

                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </div>
    );
  }
  else if (screenNumber === 5) {
    return (
      <div>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      AUDIT OBJECTIVE
                    </div>
                    <JoditEditor
                      ref={editor}
                      placeholder="Enter your text here"
                      value={backgroundBrief}
                      key={screenNumber} config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setBackgroundBrief(newContent)} // This updates backgroundBrief on blur

                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </div>
    );
  }
  else if (screenNumber === 6) {
    return (
      <div>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      AUDIT SCORE ANALYSIS
                    </div>
                    <JoditEditor
                      ref={auditScoreEditor}
                      placeholder="Enter your text here"
                      value={auditScoreAnalysis}
                      key={screenNumber} config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setAuditScoreAnalysis(newContent)} // This updates backgroundBrief on blur

                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </div>
    );
  }
  else if (screenNumber === 7) {
    return (
      <div>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      IMPROVEMENT OPPORTUNITY AREAS
                    </div>
                    <JoditEditor
                      ref={improvementEditor}
                      placeholder="Enter your text here"
                      value={improvementOpportunityAreas}
                      key={screenNumber} config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setImprovementOpportunityAreas(newContent)} // This updates backgroundBrief on blur

                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </div>
    );
  }
  else if (screenNumber === 8) {
    return (
      <>
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Saved Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="sub-headings">
                ANNEXURE - REFERENCES & STANDARDS
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <div className="table-containerr">
                  <table className="styled-tablee">
                    <thead>
                      <tr>
                        <th>Reference Type and Document / Standard</th>
                        <th>Relevance to Audit Findings</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {groupedReferences.map((group) => (
                        <React.Fragment key={group.key}>
                          {/* Section heading row (omitted for the Ungrouped bucket) */}
                          {group.heading && (
                            <tr className="section-headingg">
                              <td colSpan="2">{group.heading.section_name}</td>
                              <td className="action-celll">
                                <DeleteForeverIcon
                                  style={{ cursor: "pointer" }}
                                  className="delete-iconn"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Delete section ${group.section_name || ""} and its rows`}
                                  title="Delete Section"
                                  onClick={() => handleDeleteSection(group.heading)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleDeleteSection(group.heading);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          )}

                          {/* Data rows for this section */}
                          {group.rows.map((ref) => (
                            <tr key={ref.id}>
                              <td>{ref.document}</td>
                              <td>{ref.relevance}</td>
                              <td className="action-celll">
                                <DeleteForeverIcon
                                  style={{ cursor: "pointer" }}
                                  className="delete-iconn"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Delete annexure ${ref.document || ""}`}
                                  title="Delete Row"
                                  onClick={() => handleDelete(ref.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleDelete(ref.id);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          ))}

                          {/* Per-section "add row" (only for real sections) */}
                          {group.heading && (
                            <tr className="new-roww">
                              <td>
                                <input
                                  type="text"
                                  placeholder="Enter new document..."
                                  aria-label={`New document for section ${group.section_name}`}
                                  value={(newRowsBySection[group.section_name] || {}).document || ""}
                                  onChange={(e) =>
                                    handleNewRowChange(group.section_name, "document", e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Enter relevance..."
                                  aria-label={`Relevance for new row in section ${group.section_name}`}
                                  value={(newRowsBySection[group.section_name] || {}).relevance || ""}
                                  onChange={(e) =>
                                    handleNewRowChange(group.section_name, "relevance", e.target.value)
                                  }
                                />
                              </td>
                              <td className="action-celll">
                                <FaCheck
                                  className="add-icon"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Add row to section ${group.section_name}`}
                                  title="Add Row"
                                  onClick={() => handleAddSectionRow(group.section_name)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleAddSectionRow(group.section_name);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Row for Adding a new Section Header */}
                      <tr className="new-roww">
                        <td colSpan="2">
                          <input
                            type="text"
                            placeholder="Enter section heading..."
                            aria-label="New section heading"
                            value={newHeading}
                            onChange={(e) => setNewHeading(e.target.value)}
                          />
                        </td>
                        <td className="action-celll">
                          <FaCheck
                            className="heading-iconn"
                            role="button"
                            tabIndex={0}
                            aria-label="Add section heading"
                            title="Add Section"
                            onClick={handleAddHeading}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleAddHeading();
                              }
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save

                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 9) {
    return (
      <>
        <VariantsModal
          data={observationVariants}
          open={openVairantModal}
          handleClose={closeVariantModal}
          handleConfirmSelection={handleConfirmSelection}
        />
        <ImageViewerModal
          imageUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
        {cropState.open && (
          <ImageCropModal
            open={cropState.open}
            files={cropState.files}
            onCancel={handleCropCancel}
            onComplete={handleCropComplete}
          />
        )}
        {viewBulkImageModal && (
          <ViewBulkViewerModal 
            open={viewBulkImageModal}
            close={handleCloseViewBulkImageModal}
            imgUrl={bulkImageUrl}
            deleteBulkImage={deleteBulkImage}
          />
        )}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Saved Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div style={{ fontWeight: "600" }} className="sub-headings">
                CRITICAL OBSERVATIONS, RECOMMENDATIONS & REASONING - HSE REPORT
              </div>
              {/* ── Bulk photo upload gallery ── */}
              <div
                className="bulk-upload-zone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleBulkImagePick(e.dataTransfer.files);
                }}
              >
                <div className="bulk-upload-zone__toolbar">
                  <label className="bulk-upload-zone__btn">
                    <CloudUploadOutlinedIcon style={{ fontSize: 18 }} />
                    {isBulkUploading ? "Uploading…" : "Upload All Photos"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      disabled={isBulkUploading}
                      onChange={(e) => handleBulkImagePick(e.target.files)}
                    />
                  </label>
                  <span className="bulk-upload-zone__hint">
                    {bulkUploadedImages.length === 0
                      ? "Drag & drop or click to upload all photos at once, then assign them to rows"
                      : `${unassignedBulkImages.length} of ${bulkUploadedImages.length} photo(s) available to assign`}
                  </span>
                  {isBulkUploading && <CircularProgress size={16} />}
                </div>

                {bulkUploadedImages.length > 0 && (
                  <div className="bulk-upload-zone__filmstrip">
                    {bulkUploadedImages.map((img) => (
                      <div key={img.id} className="bulk-filmstrip-thumb">
                        {img.assigned == false && (
                          <div onClick={() => handleViewBulkImage(img.url)}>
                            <img
                              src={img.preview || img.url}
                              alt="gallery"
                              className={img.assigned ? "bulk-filmstrip-img bulk-filmstrip-img--assigned" : "bulk-filmstrip-img"}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                style={{ overflowY: "scroll" }}
                className="review-modal-body"
              >
                <div>
                  {tableTypes.length > 0 && (
                    <CreatableSelect
                      placeholder="Select or type table type..."
                      value={selectedTableType}
                      onChange={handleTableTypeChange}
                      options={tableTypes.map((type) => ({ label: type, value: type }))}
                      isClearable
                      isMulti
                    // formatCreateLabel={(inputValue) => ` Create "${inputValue}"`}
                    // isValidNewOption={() => false}
                    />

                  )}
                </div>
                <div className="table-container">
                  {Object.keys(safeGroupedObservations)
                    .filter((tableType) => {
                      if (!selectedTableType?.length) return true;
                      return selectedTableType.some(
                        (item) => item.value === tableType
                      );
                    })
                    .map((tableType, groupIdx) => {
                      const headingId = `hse-obs-group-${groupIdx}-${tableType.replace(/[^a-zA-Z0-9]+/g, "-")}`;
                      return (
                      <div key={tableType}>
                        <Typography id={headingId} style={{ fontWeight: "500" }} gutterBottom>
                          {tableType}
                        </Typography>
                        <HseObservationGroup
                          tableType={tableType}
                          rows={safeGroupedObservations[tableType]}
                          headingId={headingId}
                          tableRef={tableRef}
                          renderTrailing={(trailingRowIndex) => renderNewRowInputs(tableType, trailingRowIndex)}
                          renderRow={(observation, index, measureRef) => {
                                  const rowClassKey = `${observation.table_type ?? ""}|${observation.area ?? ""}|${observation.sr_no ?? ""}|${observation.id ?? ""}`;
                                  return (
                                    <HseObservationRow
                                      // Key by the absolute virtual-row index, not by
                                      // table_type/area/sr_no — those can repeat within a
                                      // group, and colliding keys make React render
                                      // duplicate/garbled rows and break the virtualizer's
                                      // data-index measurement. The index is always unique
                                      // and rows are fully controlled by parent state, so
                                      // recycling by position is safe.
                                      key={`obs-row-${index}`}
                                      measureRef={measureRef}
                                      dataIndex={index}
                                      rowDd={getRowOptions(observation)}
                                      isRowInSelected={selectedObservationRowClassKeys.has(rowClassKey)}
                                      observation={observation}
                                      index={index}
                                      tableType={tableType}
                                      isEditing={isEditing}
                                      currentEditedRow={currentEditedRow}
                                      selectedObservations={selectedObservations}
                                      safeGroupedObservations={safeGroupedObservations}
                                      selectedObservation={selectedObservation}
                                      openDialog={openDialog}
                                      dialog={dialog}
                                      allData={allData}
                                      criticalityOptionList={criticalityOptionList}
                                      statusOptionList={statusOptionList}
                                      customSelectStylesCreatable={customSelectStylesCreatable}
                                      handleCellEdit={handleCellEdit}
                                      handleDeleteRow={handleDeleteRow}
                                      handleSaveRowBatch={handleSaveRowBatch}
                                      handleEditNewRow={handleEditNewRow}
                                      handleEditFieldFocus={handleEditFieldFocus}
                                      handleEditFieldBlur={handleEditFieldBlur}
                                      handleOpenImageDialog={handleOpenImageDialog}
                                      handleCloseImageDialog={handleCloseImageDialog}
                                      handleRemoveImage={handleRemoveImage}
                                      handleImageUpload={handleImagePick}
                                      handleDialogBoxObservation={handleDialogBoxObservation}
                                      getAreaOptions={getAreaOptions}
                                      getEquipmentOptions={getEquipmentOptions}
                                      getCategoryOptions={getCategoryOptions}
                                      getCheckpointOptions={getCheckpointOptions}
                                      getObservationVariants={getObservationVariants}
                                      isFieldDisabled={isFieldDisabled}
                                      populateHseCategoryList={populateHseCategoryList}
                                      populateCheckpointList={populateCheckpointList}
                                      populateCriticalityList={populateCriticalityList}
                                      populateStatusList={populateStatusList}
                                      populateLegalReferenceList={populateLegalReferenceList}
                                      debouncedObservationEdit={debouncedObservationEdit}
                                      debouncedRecommendationEdit={debouncedRecommendationEdit}
                                      debouncedLegalReferenceEdit={debouncedLegalReferenceEdit}
                                      setIsEditing={setIsEditing}
                                      setNewObservationAdded={setNewObservationAdded}
                                      setCurrentEditedRow={setCurrentEditedRow}
                                      setIsReportEdited={setIsReportEdited}
                                      setSelectedImage={setSelectedImage}
                                      setIsEditAreaDropdownOpen={setIsEditAreaDropdownOpen}
                                      setIsEditEquipmentDropdownOpen={setIsEditEquipmentDropdownOpen}
                                      setIsEditCategoryDropdownOpen={setIsEditCategoryDropdownOpen}
                                      setIsEditCheckpointDropdownOpen={setIsEditCheckpointDropdownOpen}
                                      setIsEditCriticalityDropdownOpen={setIsEditCriticalityDropdownOpen}
                                      setIsEditStatusDropdownOpen={setIsEditStatusDropdownOpen}
                                      setIsEditObservationDropdownOpen={setIsEditObservationDropdownOpen}
                                      setIsEditRecommendationsDropdownOpen={setIsEditRecommendationsDropdownOpen}
                                      editAreaSelectRef={editAreaSelectRef}
                                      editCheckpointSelectRef={editCheckpointSelectRef}
                                      editCriticalitySelectRef={editCriticalitySelectRef}
                                      editStatusSelectRef={editStatusSelectRef}
                                      bulkUploadedImages={bulkUploadedImages}
                                      handleUnassignBulkImageFromRow={handleUnassignBulkImageFromRow}
                                      handleAssignBulkImageToRow={handleAssignBulkImageToRow}
                                      handleOpenGallery={handleOpenGallery}
                                      handleEmptyCheck={handleEmptyCheck}
                                    />
                                  );
                          }}
                        />
                      </div>
                      );
                    })}
                </div>
              </div>
              <hr />
              <div
                style={{ marginBottom: "10px" }}
                className="review-modal-footer"
              >
                <button
                  className="button-styles"
                  onClick={() => handleViewReport(selectedReportData.report_id)}
                  style={{ background: "#efc71d" }}
                >
                  Preview
                </button>
                <button
                  id="save-observation"
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {openViewReport && (
          <ViewHseReportSectionBasedDetails
            selectedReportData={selectedReportDataView}
            setOpenViewReport={setOpenViewReport}
            reportHeader={reportHeader}
            screenNumber={10}
            completeReport={false}
          />
        )}
        {openGallery && (
          <ImageGallery
            bulkUploadedImages= {bulkUploadedImages}
            handleAssignBulkImageToRow = {handleAssignBulkImageToRow}
            handleRemoveImage={handleRemoveImage}
          />
        )}
      </>
    );
  }
  else if (screenNumber === 10) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">DASHBOARD</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-bodyy" style={{ overflowY: "auto" }}>
                <div
                  id="chart-container-for-report"
                  className="chart-container-for-report"
                  ref={chartContainerRef}
                >
                  <div className="total-serverity-div-for-report">
                    <div className="severity-item-for-report">
                      Total Observations
                      <br />
                      <span>{data.length}</span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#FF0000" }}
                      className="severity-item-for-report"
                    >
                      High Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "High").length}
                      </span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#006400" }}
                      className="severity-item-for-report"
                    >
                      Medium Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "Medium").length}
                      </span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#005cdb" }}
                      className="severity-item-for-report"
                    >
                      Low Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "Low").length}
                      </span>
                      <hr />
                    </div>
                  </div>
                  <div className="area-chart-for-report">
                    Area Chart
                    <Chart
                      options={barOptions}
                      series={[
                        {
                          name: "",
                          data: counts,
                        },
                      ]}
                      type="bar"
                      height={300}
                    />
                  </div>
                  <div className="severity-chart-for-report">
                    Severity Chart
                    <Chart
                      options={severityChartOptions}
                      series={seriesData}
                      type="bar"
                      height={300}
                    />
                  </div>
                  {/* <div className="pie-chart-for-report">
                    Audit Score
                    <Chart
                      options={pieOptions}
                      series={[scorePercent, 100 - scorePercent]}
                      type="pie"
                      style={{ width: "100%" }}
                      height={250}
                    />
                  </div> */}
                  <div className="pie-chart-for-report">
                    Audit Score

                    {(() => {
                      // SAFE SCORE
                      const safeScore = Number(scorePercent);

                      // VALIDATE
                      const validScore =
                        isFinite(safeScore) &&
                          !isNaN(safeScore)
                          ? Math.min(100, Math.max(0, safeScore))
                          : 0;

                      const remaining = 100 - validScore;

                 
                      const showRemainingOutside = remaining > 0 && remaining < 10;

                      return (
                        <div
                          style={{
                            position: "relative",
                            // Small headroom so the value sits just above the
                            // circle's top edge, close to the thin sliver.
                            paddingTop: showRemainingOutside ? "18px" : 0,
                            // When the "Remaining" label is drawn inside the slice
                            // (> 10%), nudge the chart in slightly so the label
                            // doesn't sit right against the circle's right edge.
                            paddingRight: remaining > 10 ? "5px" : 0,
                          }}
                        >
                          {showRemainingOutside && (
                            <span
                              style={{
                                position: "absolute",
                                top: 10,
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "12px",
                                fontWeight: "bold",
                                color: "#000000",
                                // Black text on the light page background keeps the
                                // value legible; the subtle shadow still mirrors the
                                // drop shadow ApexCharts applies to the in-slice labels.
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.85)",
                                whiteSpace: "nowrap",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            >
                              {remaining.toFixed(1)}%
                            </span>
                          )}
                          <Chart
                            options={pieOptions}
                            series={[validScore, remaining]}
                            type="pie"
                            width="100%"
                            height={250}
                          />
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  {waitForCharts ? "Wait..." : "Save"}
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  &#171; {waitForCharts ? "Wait..." : "Prev"}
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  {waitForCharts ? "Wait..." : "Next"} &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 11) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">EXECUTIVE SUMMARY</div>
                    <JoditEditor
                      // ref={editor}
                      ref={exeSummaryEditor}
                      placeholder="Enter your text here"
                      value={exeSummary}
                      key={screenNumber} config={config}

                      tabIndex={1}
                      onBlur={(newContent) => setExeSummary(newContent)} // This updates backgroundBrief on blur
                    />
                  </div>
                  <div>
                    <hr />
                    <div className="sub-headings">Findings</div>
                    <JoditEditor
                      key='A1'
                      // ref={editor}
                      ref={exeSummaryFindigsEditor}
                      placeholder="Enter your Findings here"
                      value={exeSummaryFindigs}
                      config={config}
                      tabIndex={2}
                      onBlur={(newContent) => setExeSummaryFindigs(newContent)} // This updates backgroundBrief on blur
                      
                    />
                  </div>

                  <div>
                    <hr />
                    <div className="sub-headings">Key Critical Observation</div>
                    <div
                      style={{
                        flexGrow: 1,
                        minHeight: "100px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                        fontFamily: "inherit",
                        fontSize: "15px",
                        background: "whitesmoke",
                        lineHeight: "1.6",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          (keyObservationDisplay ||
                            "<p>No Observation Selected</p>")
                            .replace(/<br\s*\/?>/gi, "")
                            .replace(/<p>\s*<\/p>/gi, ""),
                      }}
                    >
                  </div>
                    <JoditEditor
                      key='A2'
                      // ref={editor}
                      ref={exeSummaryKeyObservationsEditor}
                      placeholder="Enter your text here"
                      value={exeSummaryKeyObservations}
                      config={config}

                      tabIndex={1}
                      onBlur={(newContent) => setExeSummaryKeyObservations(newContent)} // This updates backgroundBrief on blur
                      
                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {/* {showConfirmationModal && (
          <ConfirmationModal
            setShowConfirmationModal={setShowConfirmationModal}
            handleCloseWithoutSaving={handleCloseWithoutSaving}
          />
        )} */}
      </>
    );
  }
  else if (screenNumber === 12) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">
                  {module === "cmv" ? " Electrical CMV Report" : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">

                  <div className="sub-headings">
                    OVERALL ASSESSMENT INDICATOR
                  </div>
                  <div className="review-modal-body" style={{ display: "flex", gap: "10px", overflowY: "auto", marginBottom: "20px" }}>

                    {/* Left Risk Section */}
                    <div
                      className="left-section-hse"
                      style={{
                        width: "200px",
                        padding: "10px",
                        borderRadius: "4px",
                        backgroundColor: currentRisk.color,
                        color: "#000",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {/* <h4>{currentRisk.risk}</h4>
                      <p>{currentRisk.interpretation}</p> */}
                      {/* <p>{Math.floor((cumulativeScore / 10) * 100)}%</p> */}
                      {/* <p>30%</p> */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>{scorePercent}%</p>
                        <p style={{ margin: "4px 0 0 0", fontSize: "12px", fontWeight: "bold" }}>{currentRisk.risk}</p>
                      </div>
                    </div>

                    {/* Right JoditEditor */}
                    <div style={{ flexGrow: 1 }}>
                      <JoditEditor
                        ref={overallAssessmentEditor}
                        placeholder="Enter your text here"
                        value={overallAssessmentIndicator}
                        key={screenNumber} config={config}
                        tabIndex={1}
                        onBlur={(newContent) => setOverallAssessmentIndicator(newContent)} // This updates backgroundBrief on blur
                        onChange={(newContent) => {
                          const plainText = (newContent || "")
                            .replace(/<[^>]*>/g, "")
                            .replace(/\s+/g, "")
                            .replace(/&nbsp;/g, "")
                            .trim();
                          setCharWarning(plainText.length > 4000);
                        }}
                      />
                      {charWarning && (
                        <p style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                          PDF can generate only 4000 characters
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: "20px", overflowX: "auto" }}>
                    <h3 style={{ color: "#307260", fontFamily: "Montserrat" }}>Risk Legend</h3>
                    {/* <p style={{ fontFamily: "Montserrat" }}>The above image is per display purpose only</p> */}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Montserrat" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#307260", color: "#efc71d" }}>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Score Range</th>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Risk Level</th>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Interpretation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskLevels.map((level, index) => (
                          <tr key={index}>
                            <td style={{ textAlign: "center", color: level.color, border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.range}
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: level.color, color: "#000", border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.risk}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.interpretation}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Typography>

              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 13) {
    return (
      <>
        {loading ? <Loader /> : null}
        <Modal open={true} onClose={handleClose}>
          <div className="review-modal-container">
            <div
              className={exp ? "export-modal-content" : "review-modal-content"}
            >
              <div className="review-modal-header">
                <Typography variant="h5">
                  {exp
                    ? "Export Report"
                    : module === "cmv"
                      ? "HSE CMV Report"
                      : "Saved Report"}
                </Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              {!exp && (
                <div
                  className="review-modal-body"
                  style={{ overflowY: "auto" }}
                >
                  <Typography variant="body1" component="div">
                    <div>
                      <div className="sub-headings">CONCLUSION</div>
                      <JoditEditor
                        // ref={editor}
                        ref={conclusionEditor}
                        placeholder="Enter your text here"
                        value={conclusion}
                        key={screenNumber} config={config}
                        tabIndex={1}
                        onBlur={(newContent) => setConclusion(newContent)} // This updates backgroundBrief on blur
                        onChange={(newContent) => { debouncedSetConclusion(newContent) }} // Updates state on every change
                      />
                    </div>
                  </Typography>
                </div>
              )}
              <hr />
              <div className="review-modal-footer" id="conclusionFooter">
                <button
                  className="button-styles"
                  onClick={() => handleViewReport(selectedReportData.report_id)}
                  style={{ background: "#efc71d" }}
                >
                  Preview
                </button>
                {!exp && (
                  <>
                    <button
                      className="button-styles"
                      onClick={handleSave}
                      style={{ background: "#efc71d" }}
                    >
                      Save
                    </button>
                    <button
                      className="button-styles"
                      onClick={handlePrev}
                      style={{ background: "#efc71d" }}
                    >
                      &#171; Prev
                    </button>
                    {/* {module !== "cmv" && (
                      <button
                        className="button-styles"
                        onClick={handleComplete}
                        disabled={isComplete}
                        style={{
                          background: isComplete ? "lightgrey" : "#efc71d",
                        }}
                      >
                        Complete
                      </button>
                    )} */}
                  </>
                )}
                <ExportSavedReportPDF
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  selectedState={selectedState}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  exeSummaryFindigs={exeSummaryFindigs}
                  exeSummaryKeyObservations={exeSummaryKeyObservations}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  imageUrlsByRow={imageUrlsByRow}
                  reportType="HSE"
                  isSaved={exp ? true : isSaved}
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  scorePercent={scorePercent}
                  otherDetails={otherDetails}
                  ReportUID={selectedReportData.report_id}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  name={name}
                  facilityInfo={facilityInfo}
                  introduction={introduction}
                  timeFrom={timeFrom}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
                <ExportExcel
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  selectedState={selectedState}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  exeSummaryFindigs={exeSummaryFindigs}
                  exeSummaryKeyObservations={exeSummaryKeyObservations}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  imageUrlsByRow={imageUrlsByRow}
                  isSaved={exp ? true : isSaved}
                  reportType="HSE"
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  otherDetails={otherDetails}
                  ReportUID={selectedReportData.report_id}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  facilityInfo={facilityInfo}
                  timeFrom={timeFrom}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  name={name}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
                <ExportWordDoc
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  selectedState={selectedState}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  exeSummaryFindigs={exeSummaryFindigs}
                  exeSummaryKeyObservations={exeSummaryKeyObservations}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  isSaved={exp ? true : isSaved}
                  otherDetails={otherDetails}
                  reportType="HSE"
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  ReportUID={selectedReportData.report_id}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  name={name}
                  facilityInfo={facilityInfo}
                  introduction={introduction}

                  timeFrom={timeFrom}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
              </div>
            </div>
          </div>
        </Modal>
        {openViewReport && (
          <ViewHseReportSectionBasedDetails
            selectedReportData={selectedReportDataView}
            setOpenViewReport={setOpenViewReport}
            reportHeader={reportHeader}
            screenNumber={0}
            completeReport={true}
          />
        )}
      </>
    );
  }


  // if (screenNumber === 1) {
  //   return (
  //     <div>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="modal-container">
  //           <div className="modal-header">
  //             <Typography variant="h5">
  //               {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //             </Typography>
  //             <button className="custom-close-button" onClick={handleClose}>
  //               &#10005; {/* Unicode for 'X' */}
  //             </button>
  //           </div>
  //           <div className="modal-content">
  //             <div className="modal-body">
  //               <div className="report-id">

  //                 Report ID: {`${selectedReportData.report_id}`}
  //               </div>
  //               <Typography variant="body1" component="div">
  //                 <div className="select-container">
  //                   <CreatableSelect
  //                     placeholder="Organization"
  //                     options={orgList.map((e) => ({
  //                       label: e.org_name,
  //                       value: e.id,
  //                     }))}
  //                     onChange={handleOrganizationSelection}
  //                     value={selectedOrganization}
  //                     isSearchable
  //                     isClearable
  //                     isDisabled
  //                   />
  //                   <CreatableSelect
  //                     placeholder="Site"
  //                     options={siteOptions}
  //                     onChange={handleSiteSelection}
  //                     value={selectedSite}
  //                     isSearchable
  //                     isClearable
  //                     // isDisabled={!selectedOrganization}
  //                     isDisabled
  //                   />

  //                 </div>
  //                 <div className="flex-container-start-end-date">
  //                   <div className="to-date-from-date">
  //                     <DatePicker
  //                       selected={new Date(startDate)}
  //                       onChange={(e) => handleStartEndDate(e, "start-date")}
  //                       className="class-for-date-pickers"
  //                       placeholderText="Audit Start Date"
  //                       dateFormat="dd-MM-yyyy"
  //                       utcOffset={0}
  //                       maxDate={new Date(endDate)}
  //                       todayButton={"Today"}
  //                     />
  //                   </div>
  //                   <div className="to-date-from-date">
  //                     <DatePicker
  //                       selected={new Date(endDate)}
  //                       onChange={(e) => handleStartEndDate(e, "end-date")}
  //                       className="class-for-date-pickers"
  //                       placeholderText="Audit End Date"
  //                       dateFormat="dd-MM-yyyy"
  //                       utcOffset={0}
  //                       minDate={new Date(startDate)}
  //                       todayButton={"Today"}
  //                     />
  //                   </div>
  //                 </div>
  //                 {/* from and to time */}
  //                 <div className="flex-container-start-end-date">
  //                   <div className="to-date-from-date">
  //                     <label>Time of Audit From:</label>
  //                     <input
  //                       type="time"
  //                       value={timeFrom}
  //                       onChange={(e) => setTimeFrom(e.target.value)}
  //                       className="class-for-time-pickers"
  //                     />

  //                   </div>
  //                   <div className="to-date-from-date">
  //                     <label>Time of Audit To:</label>
  //                     <input
  //                       type="time"
  //                       value={timeTo}
  //                       onChange={(e) => setTimeTo(e.target.value)}
  //                       className="class-for-time-pickers"
  //                     />
  //                   </div>
  //                 </div>
  //                 {/* brief property Descriptionand  Number of Floors  */}

  //                 <div className="select-container " style={{ marginTop: "10px" }}>
  //                   {/* Brief Property Description */}
  //                   <input
  //                     type="text"
  //                     placeholder="Brief Property Description"
  //                     value={briefPropertyDescription}
  //                     onChange={(e) => setBriefPropertyDescription(e.target.value)}
  //                     className="class-for-selects" // reuse your select input styles if needed
  //                   />

  //                   {/* Number of Floors */}
  //                   <input
  //                     type="text"
  //                     placeholder="Number of Floors"
  //                     value={numOfFloors}
  //                     onChange={(e) => setNumOfFloors(e.target.value)}
  //                     className="class-for-selects" // reuse same CSS for consistent layout
  //                   />
  //                 </div>
  //                 {/* average staff footfall and num of objection certificate */}
  //                 <div className="select-container " style={{ marginTop: "10px" }}>
  //                   <input
  //                     type="text"
  //                     placeholder="Average Staff Footfall "
  //                     value={avgStaffFootfall}
  //                     onChange={(e) => setAvgStaffFootfall(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                   <input
  //                     type="text"
  //                     placeholder="No Objection Certificate: "
  //                     value={noObjectionCertificate}
  //                     onChange={(e) => setNoObjectionCertificate(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                 </div>
  //                 {/* national building code and coordinationg person div  */}
  //                 <div className="select-container " style={{ marginTop: "10px" }}>
  //                   <input
  //                     type="text"
  //                     placeholder="National Building Code Category "
  //                     value={nationalBuildingCodeCategory}
  //                     onChange={(e) => setNationalBuildingCodeCategory(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                   <input
  //                     type="text"
  //                     placeholder="Coordinating Person – Client Side "
  //                     value={coordinationgPersonClientside}
  //                     onChange={(e) => setCoordinationgPersonClientside(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                 </div>

  //                 {/* reported prepared by, reviewd by */}
  //                 <div className="select-container " style={{ marginTop: "10px" }}>
  //                   <input
  //                     type="text"
  //                     placeholder="Report Prepared By "
  //                     value={reportPreparedBy}
  //                     onChange={(e) => setReportPreparedBy(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                   <input
  //                     type="text"
  //                     placeholder="Report Reviewed By "
  //                     value={reportReviewedBy}
  //                     onChange={(e) => setReportReviewedBy(e.target.value)}
  //                     className="class-for-selects"
  //                   />
  //                 </div>










  //                 {/* <div style={{ gap: "10px" }} className="flex-container">
  //                   <Select
  //                     className="select"
  //                     placeholder="Sector"
  //                     options={sectors()}
  //                     onChange={(e) => handleChangeSector(e)}
  //                     // isMulti={true}
  //                     value={selectedSector}
  //                     isClearable
  //                     defaultValue={selectedSector}
  //                   />
  //                   <Select
  //                     className="select"
  //                     placeholder="Parameter"
  //                     options={paramsOption()}
  //                     onChange={(e) => handleChangeParam(e)}
  //                     // isMulti={true}
  //                     value={selectedParam}
  //                     isClearable
  //                     isMulti
  //                     styles={{
  //                       control: (provided) => ({
  //                         ...provided,
  //                         maxHeight: '80px',  // Limit height of selected values
  //                         overflowY: 'auto',  // Enable scrolling
  //                         display: 'flex',
  //                         flexWrap: 'wrap',
  //                       }),
  //                       valueContainer: (provided) => ({
  //                         ...provided,
  //                         maxHeight: '80px',  // Keep value container from expanding
  //                         overflowY: 'auto',  // Add scrollbar when needed
  //                       }),
  //                     }}
  //                   />
  //                   <Select
  //                     className="select"
  //                     placeholder="Area"
  //                     options={areaList.map((area) => ({
  //                       label: area,
  //                       value: area,
  //                     }))}
  //                     onChange={(e) => handleChangeArea(e)}
  //                     isMulti={true}

  //                        value={selectedArea.map((area) => ({
  //                       label: area,
  //                       value: area,
  //                     }))}
  //                     styles={{
  //                       control: (provided) => ({
  //                         ...provided,
  //                         maxHeight: '80px',  // Limit height of selected values
  //                         overflowY: 'auto',  // Enable scrolling
  //                         display: 'flex',
  //                         flexWrap: 'wrap',
  //                       }),
  //                       valueContainer: (provided) => ({
  //                         ...provided,
  //                         maxHeight: '80px',  // Keep value container from expanding
  //                         overflowY: 'auto',  // Add scrollbar when needed
  //                       }),
  //                     }}
  //                   />
  //                 </div> */}
  //               </Typography>
  //               <div className="observation-and-global-search">
  //                 <div className="observations-container">
  //                   {/* <div
  //                     style={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                     }}
  //                   >
  //                     <input
  //                       type="text"
  //                       placeholder="Search Observations"
  //                       value={searchTerm}
  //                       onChange={(e) => setSearchTerm(e.target.value)}
  //                       className="search-input"
  //                     />
  //                     <div>
  //                       <button
  //                         onClick={handleOpenDrawer}
  //                         className="search-bar-button"
  //                       >
  //                         <span className="search-icon">&#128269;</span>{" "}

  //                         <span className="search-text">
  //                           All Observations
  //                         </span>
  //                       </button>

  //                       <ObservationsDrawer
  //                         isOpen={isDrawerOpen}
  //                         onClose={handleCloseDrawer}
  //                         groupedData={groupedData}
  //                         globalSearchTerm={globalSearchTerm}
  //                         setGlobalSearchTerm={setGlobalSearchTerm}
  //                       />
  //                     </div>
  //                   </div> */}

  //                   {(AllObservations?.length ?? 0) > 0 && (selectedArea?.length ?? 0) > 0 ? (
  //                     <div className="ao-observations-list">
  //                       {Object.entries(groupedByTableType || {}).map(([table_type, areas]) => (
  //                         <div key={table_type} className="table-group">
  //                           <h4>
  //                             Parameter : <u>{table_type}</u>
  //                           </h4>

  //                           {Object.entries(areas || {}).map(([area, observationsInArea], index) => (
  //                             <Accordion
  //                               key={`${table_type}-${area}`}
  //                               expanded={expanded === `${table_type}-${area}`}
  //                               onChange={handleChangeAccordion(`${table_type}-${area}`)}
  //                             >
  //                               <AccordionSummary
  //                                 expandIcon={<ExpandMoreIcon />}
  //                                 aria-controls={`ao-${index}-content`}
  //                                 id={`ao-${index}-header`}
  //                               >
  //                                 <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
  //                                   Area: {area}
  //                                 </Typography>
  //                                 <Typography component="span" sx={{ color: "text.secondary" }}>
  //                                   {(observationsInArea || []).filter((obs) => obs.is_selected === 1).length} out of{" "}
  //                                   {(observationsInArea || []).length} observations selected
  //                                 </Typography>
  //                               </AccordionSummary>

  //                               <AccordionDetails>
  //                                 {(observationsInArea || []).map((observation, obsIndex) => (
  //                                   <div key={obsIndex} className="observation-item-checkbox">
  //                                     <input
  //                                       type="checkbox"
  //                                       checked={observation.is_selected === 1}
  //                                       onChange={() =>
  //                                         handleObservationSelection(
  //                                           observation,
  //                                           (AllObservations || []).indexOf(observation)
  //                                         )
  //                                       }
  //                                     />
  //                                     <span>
  //                                       {observation.observation} 
  //                                       {/* (<span style={{ fontWeight: "bold" }}>{observation.category}</span>) */}
  //                                     </span>
  //                                     {observation.isNote && <span className="note-label">(Note)</span>}
  //                                   </div>
  //                                 ))}
  //                               </AccordionDetails>
  //                             </Accordion>
  //                           ))}
  //                         </div>
  //                       ))}
  //                     </div>
  //                   ) : (
  //                     <div className="no-observations">No observations available.</div>
  //                   )}


  //                 </div>
  //               </div>
  //               <div className="get-recommendations-container">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleProceed}
  //                   disabled={
  //                     // selectedObservations.length === 0 ||
  //                     !selectedOrganization ||
  //                     !selectedSite ||
  //                     !startDate ||
  //                     !endDate
  //                   }
  //                   style={{
  //                     background:
  //                       // selectedObservations.length === 0 ||
  //                       !selectedOrganization ||
  //                         !selectedSite ||
  //                         !startDate ||
  //                         !endDate
  //                         ? "lightgrey"
  //                         : "#efc71d",
  //                   }}
  //                 >
  //                   Proceed
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </div>
  //   );
  // } else if (screenNumber === 2) {
  //   return (
  //     <div>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">
  //                     AUDIT OBJECTIVE
  //                   </div>
  //                   <JoditEditor
  //                     ref={editor}
  //                     placeholder="Enter your text here"
  //                     value={backgroundBrief}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setBackgroundBrief(newContent)} // This updates backgroundBrief on blur

  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={() => handleNext()}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </div>
  //   );
  // } else if (screenNumber === 3) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">
  //                     UNDERSTANDING OF THE REVIEW REPORT &#8208; CONTENTS.
  //                   </div>
  //                   <JoditEditor
  //                     // ref={editor}
  //                     ref={contentEditor}
  //                     placeholder="Enter your text here"
  //                     value={contents}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setContents(newContent)} // still update on blur
  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </>
  //   );
  // } else if (screenNumber === 4) {
  //   return (
  //     <>
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">Saved Report</Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">INTRODUCTION</div>
  //                   <JoditEditor
  //                     // ref={editor}
  //                     ref={introductionEditor}
  //                     placeholder="Enter your text here"
  //                     value={introduction}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setIntroduction(newContent)} // still update on blur
  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // } else if (screenNumber === 5) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">EXECUTIVE SUMMARY</div>
  //                   <br />
  //                   <JoditEditor
  //                     // ref={editor}
  //                     ref={exeSummaryEditor}
  //                     placeholder="Enter your text here"
  //                     value={exeSummary}
  //                     key={screenNumber} config={config}

  //                     tabIndex={1}
  //                     onBlur={(newContent) => setExeSummary(newContent)} // This updates backgroundBrief on blur
  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </>
  //   );
  // } else if (screenNumber === 6) {
  //   return (
  //     <Modal open={true} onClose={handleClose}>
  //       <div className="review-modal-container">
  //         <div className="review-modal-content">
  //           <div className="review-modal-header">
  //             <Typography variant="h5">Saved Report</Typography>
  //             <button className="custom-close-button" onClick={handleClose}>
  //               &#10005; {/* Unicode for 'X' */}
  //             </button>
  //           </div>
  //           <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //             <Typography variant="body1" component="div">
  //               <div className="sub-headings">ACADEMIC INFORMATION</div>
  //               <br />
  //               <TableContainer component={Paper}>
  //                 <Table>
  //                   <TableHead>
  //                     <TableRow>
  //                       <TableCell>FACILITY INFORMATION</TableCell>
  //                       <TableCell>COMMENTS & NOTES</TableCell>
  //                     </TableRow>
  //                   </TableHead>
  //                   <TableBody>
  //                     {facilityInfo &&
  //                       Object.entries(facilityInfo).map(
  //                         ([key, value], index) => (
  //                           <TableRow key={index}>
  //                             <TableCell>{key}:</TableCell>
  //                             <TableCell style={{ display: "flex" }}>
  //                               {/* <textarea
  //                                */}
  //                               <TextField
  //                                 value={value}
  //                                 onChange={(e) =>
  //                                   handleChangeFacilityInfo({
  //                                     target: {
  //                                       name: key,
  //                                       value: e.target.value,
  //                                     },
  //                                   })
  //                                 }
  //                                 placeholder="Enter your text here"
  //                                 style={{ width: "100%", minHeight: "80px" }}
  //                               />
  //                               <IconButton
  //                                 onClick={() => handleRemoveField(key)}
  //                                 style={{ color: "red", marginLeft: "10px" }}
  //                                 aria-label="delete"
  //                               >
  //                                 <DeleteIcon />
  //                               </IconButton>
  //                             </TableCell>
  //                           </TableRow>
  //                         )
  //                       )}

  //                     <TableRow>
  //                       <TableCell>
  //                         <TextField
  //                           label="New Field Name"
  //                           value={newKey}
  //                           onChange={handleNewKeyChange}
  //                           variant="outlined"
  //                           style={{ width: "100%" }}
  //                         />
  //                       </TableCell>
  //                       <TableCell>
  //                         <div
  //                           style={{ display: "flex", alignItems: "center" }}
  //                         >
  //                           <TextField
  //                             value={newValue}
  //                             onChange={(e) =>
  //                               handleNewValueChange({
  //                                 target: { value: e.target.value },
  //                               })
  //                             }
  //                             placeholder="Enter your text here"
  //                             style={{ width: "100%", minHeight: "80px" }}
  //                           />
  //                           <button
  //                             onClick={handleAddNewField}
  //                             className="button-styles"
  //                             style={{
  //                               background: "#efc71d",
  //                               marginLeft: "10px",
  //                             }}
  //                           >
  //                             Add
  //                           </button>
  //                         </div>
  //                       </TableCell>

  //                     </TableRow>
  //                   </TableBody>
  //                 </Table>
  //               </TableContainer>
  //             </Typography>
  //           </div>
  //           <hr />
  //           <div className="review-modal-footer">
  //             <button
  //               className="button-styles"
  //               onClick={handleSave}
  //               style={{ background: "#efc71d" }}
  //             >
  //               Save
  //             </button>
  //             <button
  //               className="button-styles"
  //               onClick={handlePrev}
  //               style={{ background: "#efc71d" }}
  //             >
  //               &#171; Prev
  //             </button>
  //             <button
  //               className="button-styles"
  //               color="primary"
  //               onClick={handleNext}
  //               style={{ background: "#efc71d" }}
  //             >
  //               Next &#187;
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     </Modal>
  //   );
  // } else if (screenNumber === 7) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">CRITICAL OBSERVATIONS</div>
  //                   <br />
  //                   <div className="critical-observations-div">
  //                     {criticalObservations.length === 0 ? (
  //                       <div className="no-observations">
  //                         <em>No critical observations</em>
  //                       </div>
  //                     ) : (
  //                       criticalObservations.map((observation, index) => (
  //                         <div key={index} className="observation-item">
  //                           <textarea
  //                             onChange={(e) => handleObservationEdit(index, e)}
  //                             style={{ width: "100%", fontFamily: "inherit" }}
  //                             value={observation.observation}
  //                           />
  //                           &nbsp;
  //                           <CancelIcon
  //                             onClick={() => removeItem(index)}
  //                             className="cancel-icon"
  //                           >
  //                             &#10005;
  //                           </CancelIcon>
  //                         </div>
  //                       ))
  //                     )}
  //                   </div>
  //                   <br />
  //                   <div className="custom-text-box">
  //                     <TextField
  //                       id="outlined-multiline-flexible"
  //                       label="Add Other Critical Observations"
  //                       placeholder="Other Observations. . ."
  //                       multiline
  //                       onChange={(e) => handleChange(e, "other details")}
  //                       value={otherDetails}
  //                       style={{ width: "100%", fontFamily: "inherit", backgroundColor: "white" }}
  //                     />
  //                     <IoAddCircle size={30} className="IoAddCircle" style={{ cursor: "pointer" }} onClick={addObservation} />
  //                   </div>
  //                 </div>
  //               </Typography>
  //             </div>

  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 disabled={disableSaveNext}
  //                 style={{
  //                   background: disableSaveNext ? "lightgrey" : "#efc71d",
  //                 }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 disabled={disableSaveNext}
  //                 style={{
  //                   background: disableSaveNext ? "lightgrey" : "#efc71d",
  //                 }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </>
  //   );
  // } else if (screenNumber === 8) {
  //   return (
  //     <>
  //       <VariantsModal
  //         data={observationVariants}
  //         open={openVairantModal}
  //         handleClose={closeVariantModal}
  //         handleConfirmSelection={handleConfirmSelection}
  //       />
  //       <ImageViewerModal
  //         imageUrl={selectedImage}
  //         onClose={() => setSelectedImage(null)}
  //       />
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">Saved Report</Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div style={{ fontWeight: "600" }} className="sub-headings">
  //               CRITICAL HSE OBSERVATIONS, PHOTOS & RECOMMENDATIONS
  //             </div>
  //             <div
  //               style={{ overflowY: "scroll" }}
  //               className="review-modal-body"
  //             >
  //                                                <div>
  //                   {tableTypes.length > 0 && (
  //                    <CreatableSelect
  //                   placeholder="Select or type table type..."
  //                   value={selectedTableType}
  //                   onChange={handleTableTypeChange}
  //                   options={tableTypes.map((type) => ({ label: type, value: type }))}
  //                   isClearable
  //                   isMulti
  //                   formatCreateLabel={(inputValue) => `+ Create "${inputValue}"`}
  //                 />

  //                   )}
  //                 </div>
  //               <div className="table-container">
  //                 {Object.keys(safeGroupedObservations).map((tableType) => (
  //                   <div key={tableType}>
  //                     <Typography style={{ fontWeight: "500" }} gutterBottom>
  //                       {tableType}
  //                     </Typography>
  //                     <TableContainer
  //                       component={Paper}
  //                       className="table-scroll"
  //                     >
  //                       <Table>
  //                         <TableHead>
  //                           <TableRow>
  //                             <TableCell>Sr. No.</TableCell>
  //                             <TableCell>Areas</TableCell>
  //                             <TableCell>Check Point</TableCell>
  //                             <TableCell>Observation</TableCell>
  //                             <TableCell>Criticality</TableCell>
  //                             <TableCell>Recommendation</TableCell>
  //                             <TableCell>IS Reference</TableCell>
  //                             <TableCell>Score</TableCell>
  //                             <TableCell>System Implementation</TableCell>
  //                             <TableCell>Compliance Check</TableCell>
  //                             <TableCell>Photo Evidences</TableCell>
  //                             <TableCell>Actions</TableCell>
  //                           </TableRow>
  //                         </TableHead>
  //                         <TableBody>
  //                           {safeGroupedObservations[tableType].map(
  //                             (observation, index) => (
  //                               <TableRow
  //                                 key={`${observation.table_type}-${observation.area
  //                                   }-${observation.sr_no || observation.id}`}
  //                                 className={(() => {
  //                                   const foundObservation = selectedObservations.find(
  //                                     (obs) =>
  //                                       obs.table_type === observation.table_type &&
  //                                       obs.area === observation.area &&
  //                                       (obs.sr_no === observation.sr_no || obs.id === observation.id)
  //                                   );

  //                                   // If editing, make all other rows 'odd-row' except the one being edited
  //                                   if (isEditing && foundObservation) {
  //                                     // Check if the current row is being edited
  //                                     if (
  //                                       currentEditedRow === foundObservation.sr_no ||
  //                                       currentEditedRow === foundObservation.id
  //                                     ) {
  //                                       return "even-row"; // Keep the current editing row as even
  //                                     } else {
  //                                       return "odd-row"; // All other rows become odd
  //                                     }
  //                                   }

  //                                   // Default: all rows are even when not editing
  //                                   return "even-row";
  //                                 })()}


  //                                 style={
  //                                   observation.variant === true
  //                                     ? { backgroundColor: "#f2f2f2" }
  //                                     : {}
  //                                 }
  //                               >
  //                                 <TableCell>{index + 1}</TableCell>
  //                                 {/* area */}
  //                                 <TableCell
  //                                   className="editable-cell"
  //                                   style={{ height: "100px" }}
  //                                 >
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{
  //                                       color:
  //                                         isEditing &&
  //                                           currentEditedRow !==
  //                                           observation.sr_no

  //                                           ? "grey"
  //                                           : "black",
  //                                     }}
  //                                   >
  //                                     <CreatableSelect
  //                                       styles={customSelectStylesCreatable}
  //                                       placeholder="Area"
  //                                       // options={areaOptions()}
  //                                       options={areaList.map((area) => ({ label: area, value: area }))}
  //                                       defaultValue={{
  //                                         label: observation.area,
  //                                         value: observation.area,
  //                                       }}
  //                                       isSearchable
  //                                       onChange={(e) =>
  //                                         handleCellEdit(
  //                                           e,
  //                                           index,
  //                                           "area",
  //                                           observation.area,
  //                                           observation
  //                                         )
  //                                       }
  //                                       isDisabled={
  //                                         isEditing &&
  //                                         currentEditedRow !==
  //                                         observation.sr_no


  //                                       }
  //                                     />
  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* checkpoints */}
  //                                 <TableCell
  //                                   className="editable-cell"
  //                                   style={{ height: "100px" }}
  //                                 >
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{

  //                                       marginRight: "10px"
  //                                     }}
  //                                   >

  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       value={observation.check_points}

  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "check_points",
  //                                           observation.check_points,
  //                                           { ...observation, check_points: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         if (newValue !== observation.check_points) {
  //                                           setSelectedObservations((prev) => {
  //                                             return prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, check_points: newValue }
  //                                                 : obs
  //                                             );
  //                                           });

  //                                         }
  //                                       }}


  //                                       onBlur={(e) => {
  //                                         handleCellEdit(e, index, "check_points", observation.check_points, observation);
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       placeholder="Check Point"
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />



  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "check_points",
  //                                             observation.check_points,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* observation */}
  //                                 <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{
  //                                       //     color:
  //                                       // (isEditing && currentEditedRow !== index) ||
  //                                       //   observation.variant === true
  //                                       //   ? "grey"
  //                                       //   : "black",
  //                                       marginRight: "10px",
  //                                     }}
  //                                   >
  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       placeholder="Observation"
  //                                       value={
  //                                         observation.observation
  //                                           ? observation.observation.replace(/\s+/g, " ").trim()
  //                                           : ""
  //                                       }
  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "observation",
  //                                           observation.observation,
  //                                           { ...observation, observation: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, observation: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }}
  //                                       onBlur={(e) => {
  //                                         const latestValue = e.target.value;

  //                                         handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: latestValue } },
  //                                           index,
  //                                           "observation",
  //                                           observation.observation,
  //                                           { ...observation, observation: latestValue }
  //                                         );
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />

  //                                     <DialogBox dialog={dialog} handleDialogBox={handleDialogBoxObservation} />
  //                                     {/* Optional: Show dialog if needed */}
  //                                     {/* <DialogBox dialog={dialog} handleDialogBox={handleDialogBoxObservation} /> */}
  //                                   </div>

  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "observation",
  //                                             observation.observation,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>

  //                                 {/* criticality */}

  //                                 <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                   <Select
  //                                     styles={{
  //                                       ...customSelectStylesCreatable,
  //                                     }}
  //                                     placeholder="Criticality"
  //                                     options={criticalityOptions.length > 0 ? criticalityOptions : []}
  //                                     noOptionsMessage={() => "No options"}
  //                                     value={
  //                                       observation.criticality
  //                                         ? { label: observation.criticality, value: observation.criticality }
  //                                         : null
  //                                     }
  //                                     isSearchable
  //                                     onChange={(selectedOption) => {
  //                                       if (currentEditedRow !== -1 && currentEditedRow !== observation.sr_no) {
  //                                         toast.warning("Please save the current row before editing another.");
  //                                         return;
  //                                       }

  //                                       if (selectedOption) {
  //                                         const newValue = selectedOption.value;

  //                                         // Pass updated observation object with new criticality to handleCellEdit
  //                                         const proceed = handleCellEdit(
  //                                           { value: newValue }, // mimic the select object
  //                                           index,
  //                                           "criticality",
  //                                           observation.criticality,
  //                                           { ...observation, criticality: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         // Update selectedObservations
  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, criticality: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }
  //                                     }}
  //                                     isDisabled={
  //                                       (isEditing && currentEditedRow !== observation.sr_no) ||
  //                                       observation.variant === true
  //                                     }
  //                                     menuPlacement="auto"
  //                                   />

  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                       onClick={(e) => {
  //                                         e.stopPropagation();
  //                                         if (
  //                                           (isEditing && currentEditedRow !== observation.sr_no) ||
  //                                           observation.variant === true
  //                                         ) {
  //                                           toast.warning(
  //                                             "Please save changes in the currently edited row before editing another row."
  //                                           );
  //                                         }
  //                                       }}
  //                                     />
  //                                   )}
  //                                 </TableCell>

  //                                 {/* recommendations */}
  //                                 <TableCell className="editable-cell">
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{
  //                                       //     color:
  //                                       // (isEditing && currentEditedRow !== index) ||
  //                                       //   observation.variant === true
  //                                       //   ? "grey"
  //                                       //   : "black",
  //                                       marginRight: "10px"
  //                                     }}
  //                                   >

  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       value={observation.recommendations}
  //                                       placeholder="Recommendations"
  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "recommendations",
  //                                           observation.recommendations,
  //                                           { ...observation, recommendations: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, recommendations: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }}
  //                                       onBlur={(e) => {
  //                                         const latestValue = e.target.value;

  //                                         handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: latestValue } },
  //                                           index,
  //                                           "recommendations",
  //                                           observation.recommendations,
  //                                           { ...observation, recommendations: latestValue }
  //                                         );
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />


  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "recommendations",
  //                                             observation.recommendations,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* isreference */}
  //                                 <TableCell
  //                                   className="editable-cell"
  //                                   style={{ height: "100px" }}
  //                                 >
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{
  //                                       //    color:
  //                                       // (isEditing && currentEditedRow !== index) ||
  //                                       //   observation.variant === true
  //                                       //   ? "grey"
  //                                       //   : "black",
  //                                       marginRight: "10px"
  //                                     }}
  //                                   >

  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       value={observation.is_reference}
  //                                       placeholder="IS Reference"
  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "is_reference",
  //                                           observation.is_reference,
  //                                           { ...observation, is_reference: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, is_reference: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }}
  //                                       onBlur={(e) => {
  //                                         const latestValue = e.target.value;

  //                                         handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: latestValue } },
  //                                           index,
  //                                           "is_reference",
  //                                           observation.is_reference,
  //                                           { ...observation, is_reference: latestValue }
  //                                         );
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />

  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "is_reference",
  //                                             observation.is_reference,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* scores */}
  //                                 <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{
  //                                       //    color:
  //                                       // (isEditing && currentEditedRow !== index) ||
  //                                       //   observation.variant === true
  //                                       //   ? "grey"
  //                                       //   : "black",
  //                                       marginRight: "10px",
  //                                     }}
  //                                   >
  //                                     <TextField
  //                                       type="number"
  //                                       value={observation.score ?? ""}
  //                                       placeholder="Score"
  //                                       sx={{ width: "80px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         inputProps: {
  //                                           min: 0,
  //                                           max: 5,
  //                                           step: 1,
  //                                         },
  //                                         disabled:
  //                                           observation.variant === true ||
  //                                           (isEditing && currentEditedRow !== observation.sr_no),
  //                                       }}
  //                                       onChange={(e) =>
  //                                         handleScoreChange(
  //                                           e,
  //                                           observation.table_type,
  //                                           observation.sr_no || observation.id,
  //                                           index
  //                                         )
  //                                       }
  //                                       size="small"
  //                                       variant="outlined"
  //                                     />
  //                                   </div>

  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? setCurrentEditedRow(index)
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* system_implentation */}
  //                                 <TableCell className="editable-cell">
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{

  //                                       marginRight: "10px"
  //                                     }}
  //                                   >

  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       value={observation.system_implementation}
  //                                       placeholder="System Implementation"
  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "system_implementation",
  //                                           observation.system_implementation,
  //                                           { ...observation, system_implementation: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, system_implementation: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }}
  //                                       onBlur={(e) => {
  //                                         const latestValue = e.target.value;

  //                                         handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: latestValue } },
  //                                           index,
  //                                           "system_implementation",
  //                                           observation.system_implementation,
  //                                           { ...observation, system_implementation: latestValue }
  //                                         );
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />


  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "system_implementation",
  //                                             observation.system_implementation,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* compliance_check */}
  //                                 <TableCell className="editable-cell">
  //                                   <div
  //                                     className="cell-content"
  //                                     style={{

  //                                       marginRight: "10px"
  //                                     }}
  //                                   >

  //                                     <TextField
  //                                       id={`outlined-textarea-${index}`}
  //                                       value={observation.compliance_check}
  //                                       placeholder="Compliance Check"
  //                                       onChange={(e) => {
  //                                         const newValue = e.target.value;

  //                                         const proceed = handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: newValue } },
  //                                           index,
  //                                           "compliance_check",
  //                                           observation.compliance_check,
  //                                           { ...observation, compliance_check: newValue }
  //                                         );

  //                                         if (!proceed) return;

  //                                         setSelectedObservations((prev) =>
  //                                           prev.map((obs) =>
  //                                             (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                               ? { ...obs, compliance_check: newValue, hasEdited: true }
  //                                               : obs
  //                                           )
  //                                         );
  //                                       }}
  //                                       onBlur={(e) => {
  //                                         const latestValue = e.target.value;

  //                                         handleCellEdit(
  //                                           { ...e, target: { ...e.target, textContent: latestValue } },
  //                                           index,
  //                                           "compliance_check",
  //                                           observation.compliance_check,
  //                                           { ...observation, compliance_check: latestValue }
  //                                         );
  //                                       }}
  //                                       sx={{ width: "200px" }}
  //                                       InputProps={{
  //                                         sx: { fontSize: "10px" },
  //                                         disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                       }}
  //                                       variant="outlined"
  //                                       size="small"
  //                                       fullWidth
  //                                       multiline
  //                                       minRows={1.5}
  //                                       maxRows={10}
  //                                     />


  //                                   </div>
  //                                   {!observation.variant && (
  //                                     <EditOutlinedIcon
  //                                       onClick={(e) =>
  //                                         isEditing
  //                                           ? handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "compliance_check",
  //                                             observation.compliance_check,
  //                                             observation
  //                                           )
  //                                           : null
  //                                       }
  //                                       className="edit-icon"
  //                                       fontSize="small"
  //                                     />
  //                                   )}
  //                                 </TableCell>
  //                                 {/* image upload */}

  //                                 <TableCell>
  //                                   <div
  //                                     style={{
  //                                       display: "flex",
  //                                       alignItems: "center",
  //                                       justifyContent: "center",
  //                                       gap: "15px",
  //                                     }}
  //                                   >
  //                                     {/* Upload Icon with input */}
  //                                     <div
  //                                       style={{
  //                                         display: "flex",
  //                                         alignItems: "center",
  //                                         justifyContent: "center",
  //                                         padding: "6px 0",
  //                                         border: "1px solid grey",
  //                                         borderRadius: "5px",
  //                                         width: "100px",
  //                                         cursor:
  //                                           isEditing &&
  //                                             currentEditedRow !== (observation.sr_no || observation.id)
  //                                             ? "not-allowed"
  //                                             : "pointer",
  //                                         position: "relative",
  //                                         background:
  //                                           isEditing &&
  //                                             currentEditedRow !== (observation.sr_no || observation.id)
  //                                             ? "#f0f0f0"
  //                                             : "white",
  //                                         opacity:
  //                                           isEditing &&
  //                                             currentEditedRow !== (observation.sr_no || observation.id)
  //                                             ? 0.6
  //                                             : 1,
  //                                       }}
  //                                     >
  //                                       <CloudUploadOutlinedIcon fontSize="small" />
  //                                       <input
  //                                         type="file"
  //                                         accept="image/*"
  //                                         multiple
  //                                         onChange={(e) =>
  //                                           handleImageUpload(
  //                                             observation.table_type,
  //                                             observation.sr_no || observation.id,
  //                                             observation.area,
  //                                             e.target.files
  //                                           )
  //                                         }
  //                                         style={{
  //                                           position: "absolute",
  //                                           width: "100%",
  //                                           height: "100%",
  //                                           top: 0,
  //                                           left: 0,
  //                                           opacity: 0,
  //                                           cursor:
  //                                             isEditing &&
  //                                               currentEditedRow !== (observation.sr_no || observation.id)
  //                                               ? "not-allowed"
  //                                               : "pointer",
  //                                         }}
  //                                         disabled={
  //                                           isEditing &&
  //                                           currentEditedRow !== (observation.sr_no || observation.id)
  //                                         }
  //                                       />
  //                                     </div>

  //                                     {/* View Uploaded Images Icon */}
  //                                     <div
  //                                       style={{
  //                                         display: "flex",
  //                                         alignItems: "center",
  //                                         justifyContent: "center",
  //                                         padding: "6px 0",
  //                                         border: "1px solid grey",
  //                                         borderRadius: "5px",
  //                                         width: "100px",
  //                                         cursor: "pointer",
  //                                       }}
  //                                       onClick={() =>
  //                                         handleOpenImageDialog(observation.sr_no || observation.id, observation)
  //                                       }
  //                                     >
  //                                       <InsertPhotoOutlinedIcon fontSize="small" />
  //                                     </div>
  //                                   </div>

  //                                   {/* Image Preview Dialog */}
  //                                   {selectedObservation?.image &&
  //                                     selectedObservation.image.sr_no === observation.sr_no && (
  //                                       <Dialog onClose={handleCloseImageDialog} open={openDialog} maxWidth={false}>
  //                                         <DialogTitle>Uploaded Images</DialogTitle>
  //                                         <DialogContent dividers>
  //                                           {selectedObservation.image.imageUrls?.length > 0 ? (
  //                                             <div
  //                                               style={{
  //                                                 display: "grid",
  //                                                 gridTemplateColumns: "repeat(4, 1fr)",
  //                                                 gap: "10px",
  //                                               }}
  //                                             >
  //                                               {selectedObservation.image.imageUrls.map(
  //                                                 (imageUrl, imgIndex) => (
  //                                                   <div style={{ display: "flex" }} key={imgIndex}>
  //                                                     <img
  //                                                       src={imageUrl}
  //                                                       alt={`Image ${imgIndex + 1}`}
  //                                                       onClick={() => setSelectedImage(imageUrl)}
  //                                                       style={{
  //                                                         cursor: "pointer",
  //                                                         width: "200px",
  //                                                         height: "200px",
  //                                                         objectFit: "cover",
  //                                                         borderRadius: "4px",
  //                                                       }}
  //                                                     />
  //                                                     <CancelIcon
  //                                                       className="cancel-icon"
  //                                                       onClick={() =>
  //                                                         handleRemoveImage(
  //                                                           observation.table_type,
  //                                                           observation.area,
  //                                                           observation.sr_no || observation.id,
  //                                                           imgIndex
  //                                                         )
  //                                                       }
  //                                                       style={{ cursor: "pointer", marginLeft: 5 }}
  //                                                     />
  //                                                   </div>
  //                                                 )
  //                                               )}
  //                                             </div>
  //                                           ) : (
  //                                             <Typography gutterBottom>No File Uploaded</Typography>
  //                                           )}
  //                                         </DialogContent>
  //                                         <DialogActions>
  //                                           <Button onClick={handleCloseImageDialog}>Close</Button>
  //                                         </DialogActions>
  //                                       </Dialog>
  //                                     )}
  //                                 </TableCell>
  //                                 {/* Info */}
  //                                 <TableCell className="table-actions">
  //                                   <div
  //                                     style={{
  //                                       display: "flex",
  //                                       justifyContent: "center",
  //                                     }}
  //                                   >
  //                                     <InfoIcon
  //                                       onClick={() =>
  //                                         getObservationVariants(observation.observation, observation.sr_no || observation.id)
  //                                       }
  //                                       style={{
  //                                         pointerEvents: (() => {
  //                                           const matchingObservation = selectedObservations.find(
  //                                             (obs) =>
  //                                               obs.table_type === observation.table_type &&
  //                                               obs.area === observation.area &&
  //                                               (obs.sr_no === observation.sr_no || obs.id === observation.id)
  //                                           );

  //                                           return isEditing &&
  //                                             (!matchingObservation ||
  //                                               (matchingObservation.sr_no !== observation.sr_no &&
  //                                                 matchingObservation.id !== observation.id))
  //                                             ? "none"
  //                                             : "auto";
  //                                         })(),
  //                                       }}
  //                                     />
  //                                     {/* <PlaylistAddCircleIcon
  //                                       onClick={() =>
  //                                         handleDuplicateRow(
  //                                           observation.table_type,
  //                                           observation.area,
  //                                           observation.sr_no || observation.id
  //                                         )
  //                                       }
  //                                       style={{
  //                                         pointerEvents: (() => {
  //                                           const matchingObservation = selectedObservations.find(
  //                                             (obs) =>
  //                                               obs.table_type === observation.table_type &&
  //                                               obs.area === observation.area &&
  //                                               (obs.sr_no === observation.sr_no || obs.id === observation.id)
  //                                           );

  //                                           return isEditing &&
  //                                             (!matchingObservation ||
  //                                               (matchingObservation.sr_no !== observation.sr_no &&
  //                                                 matchingObservation.id !== observation.id))
  //                                             ? "none"
  //                                             : "auto";
  //                                         })(),
  //                                       }}
  //                                     /> */}
  //                                     <DeleteForeverIcon
  //                                       onClick={() =>
  //                                         handleDeleteRow(
  //                                           observation.table_type,
  //                                           observation.area,
  //                                           observation.sr_no || observation.id
  //                                         )
  //                                       }
  //                                       style={{
  //                                         pointerEvents: (() => {
  //                                           const matchingObservation = selectedObservations.find(
  //                                             (obs) =>
  //                                               obs.table_type === observation.table_type &&
  //                                               obs.area === observation.area &&
  //                                               (obs.sr_no === observation.sr_no || obs.id === observation.id)
  //                                           );

  //                                           return isEditing &&
  //                                             (!matchingObservation ||
  //                                               (matchingObservation.sr_no !== observation.sr_no &&
  //                                                 matchingObservation.id !== observation.id))
  //                                             ? "none"
  //                                             : "auto";
  //                                         })(),
  //                                       }}
  //                                     />
  //                                   </div>
  //                                 </TableCell>

  //                               </TableRow>
  //                             )
  //                           )}
  //                           {renderNewRowInputs(tableType)}
  //                         </TableBody>
  //                       </Table>
  //                     </TableContainer>
  //                   </div>
  //                 ))}
  //               </div>
  //             </div>
  //             <hr />
  //             <div
  //               style={{ marginBottom: "10px" }}
  //               className="review-modal-footer"
  //             >
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={() => handleNext()}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // } else if (screenNumber === 9) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">GLOBAL BEST PRACTICES</div>
  //                   <JoditEditor
  //                     // ref={editor}
  //                     ref={bestPracticeEditor}
  //                     placeholder="Enter your text here"
  //                     value={bestPractice}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setbestPractice(newContent)} // This updates backgroundBrief on blur
  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // } else if (screenNumber === 10) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">Charts</Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <div
  //                 id="chart-container-for-report"
  //                 className="chart-container-for-report"
  //                 ref={chartContainerRef}
  //               >
  //                 <div className="total-serverity-div-for-report">
  //                   <div className="severity-item-for-report">
  //                     Total Observations
  //                     <br />
  //                     <span>{data.length}</span>
  //                     <hr />
  //                   </div>
  //                   <div
  //                     style={{ color: "#FF0000" }}
  //                     className="severity-item-for-report"
  //                   >
  //                     High Severity Observations
  //                     <br />
  //                     <span>
  //                       {data.filter((e) => e.criticality === "High").length}
  //                     </span>
  //                     <hr />
  //                   </div>
  //                   <div
  //                     style={{ color: "#006400" }}
  //                     className="severity-item-for-report"
  //                   >
  //                     Medium Severity Observations
  //                     <br />
  //                     <span>
  //                       {data.filter((e) => e.criticality === "Medium").length}
  //                     </span>
  //                     <hr />
  //                   </div>
  //                   <div
  //                     style={{ color: "#005cdb" }}
  //                     className="severity-item-for-report"
  //                   >
  //                     Low Severity Observations
  //                     <br />
  //                     <span>
  //                       {data.filter((e) => e.criticality === "Low").length}
  //                     </span>
  //                     <hr />
  //                   </div>
  //                 </div>
  //                 <div className="area-chart-for-report">
  //                   Area Chart
  //                   <Chart
  //                     options={barOptions}
  //                     series={[
  //                       {
  //                         name: "",
  //                         data: counts,
  //                       },
  //                     ]}
  //                     type="bar"
  //                     height={300}
  //                   />
  //                 </div>
  //                 <div className="severity-chart-for-report">
  //                   Severity Chart
  //                   <Chart
  //                     options={severityChartOptions}
  //                     series={seriesData}
  //                     type="bar"
  //                     height={300}
  //                   />
  //                 </div>
  //                 <div className="pie-chart-for-report">
  //                   Audit Score
  //                   <Chart
  //                     options={pieOptions}
  //                     series={[scorePercent, 100 - scorePercent]}
  //                     type="pie"
  //                     style={{ width: "100%" }}
  //                     height={250}
  //                   />
  //                 </div>
  //               </div>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 disabled={waitForCharts}
  //                 style={{
  //                   background: waitForCharts ? "lightgrey" : "#efc71d",
  //                 }}
  //               >
  //                 {waitForCharts ? "Wait..." : "Save"}
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 disabled={waitForCharts}
  //                 style={{
  //                   background: waitForCharts ? "lightgrey" : "#efc71d",
  //                 }}
  //               >
  //                 &#171; {waitForCharts ? "Wait..." : "Prev"}
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 disabled={waitForCharts}
  //                 style={{
  //                   background: waitForCharts ? "lightgrey" : "#efc71d",
  //                 }}
  //               >
  //                 {waitForCharts ? "Wait..." : "Next"} &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // } else if (screenNumber === 11) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">THE WAY FORWARD</div>
  //                   <JoditEditor
  //                     // ref={editor}
  //                     ref={wayForwardPlanEditor}
  //                     placeholder="Enter your text here"
  //                     value={theWayForward}
  //                     tabIndex={1}
  //                     config={{
  //                       ...config,
  //                       readonly: false,
  //                       toolbarSticky: false,
  //                       askBeforePasteFromHTML: false,
  //                       askBeforePasteFromWord: false,
  //                       processPasteFromWord: true,
  //                       defaultActionOnPaste: 'insert_clear_html',  // Clears formatting but keeps structure
  //                       pasteHTMLAction: 'insert_clear_html',
  //                       disablePlugins: ['pasteDialog', 'paste'],
  //                       cleanHTML: {
  //                         removeStyles: true,
  //                         removeClasses: true,
  //                       },
  //                       pasteFromClipboard: true,

  //                     }}
  //                     onBlur={(newContent) => setTheWayForward(newContent)} // This updates backgroundBrief on blur
  //                     // onChange={(newContent) => { setTheWayForward(newContent) }} // Updates state on every change
  //                     onChange={(newContent) => { debouncedSetTheWayForward(newContent) }}
  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // }
  // else if (screenNumber === 12) {
  //   return (
  //     <div>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">
  //                     Classification Of Audit Observations
  //                   </div>
  //                   <JoditEditor
  //                     ref={classificationEditor}
  //                     placeholder="Enter your text here"
  //                     value={classificationOfAuditObservations}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setClassificationOfAuditObservations(newContent)}

  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={() => handleNext()}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </div>
  //   );
  // }
  // else if (screenNumber === 13) {
  //   return (
  //     <div>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">
  //                     AUDIT SCORE ANALYSIS
  //                   </div>
  //                   <JoditEditor
  //                     ref={auditScoreEditor}
  //                     placeholder="Enter your text here"
  //                     value={auditScoreAnalysis}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setAuditScoreAnalysis(newContent)} // This updates backgroundBrief on blur

  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={() => handleNext()}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </div>
  //   );
  // }
  // else if (screenNumber === 14) {
  //   return (
  //     <div>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {module === "cmv" ? " HSE CMV Report" : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div>
  //                   <div className="sub-headings">
  //                     IMPROVEMENT OPPORTUNITY AREAS
  //                   </div>
  //                   <JoditEditor
  //                     ref={improvementEditor}
  //                     placeholder="Enter your text here"
  //                     value={improvementOpportunityAreas}
  //                     key={screenNumber} config={config}
  //                     tabIndex={1}
  //                     onBlur={(newContent) => setImprovementOpportunityAreas(newContent)} // This updates backgroundBrief on blur

  //                   />
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={() => handleNext()}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //       {/* {showConfirmationModal && (
  //         <ConfirmationModal
  //           setShowConfirmationModal={setShowConfirmationModal}
  //           handleCloseWithoutSaving={handleCloseWithoutSaving}
  //         />
  //       )} */}
  //     </div>
  //   );
  // }
  // else if (screenNumber === 15) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h6">
  //                 {module === "cmv" ? " Electrical CMV Report" : "SAVED REPORT"} : OVERALL ASSESSMENT INDICATOR
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">


  //                 <div className="review-modal-body" style={{ display: "flex", gap: "10px", overflowY: "auto", marginBottom: "20px" }}>

  //                   {/* Left Risk Section */}
  //                   <div
  //                     style={{
  //                       width: "200px",
  //                       padding: "10px",
  //                       borderRadius: "4px",
  //                       backgroundColor: currentRisk.color,
  //                       color: "#000",
  //                       flexShrink: 0,
  //                       display: "flex",
  //                       justifyContent: "center",
  //                       alignItems: "center",
  //                     }}
  //                   >
  //                     {/* <h4>{currentRisk.risk}</h4>
  //                     <p>{currentRisk.interpretation}</p> */}
  //                     {/* <p>{Math.floor((cumulativeScore / 10) * 100)}%</p> */}
  //                     <p>30%</p>
  //                   </div>

  //                   {/* Right JoditEditor */}
  //                   <div style={{ flexGrow: 1 }}>
  //                     <JoditEditor
  //                       ref={overallAssessmentEditor}
  //                       placeholder="Enter your text here"
  //                       value={overallAssessmentIndicator}
  //                       key={screenNumber} config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setOverallAssessmentIndicator(newContent)} // This updates backgroundBrief on blur

  //                     />
  //                   </div>
  //                 </div>
  //                 <div style={{ marginBottom: "20px", overflowX: "auto" }}>
  //                   <h3 style={{ color: "#307260", fontFamily: "Montserrat" }}>Risk Legend</h3>
  //                   <p style={{ fontFamily: "Montserrat" }}>The above image is per display purpose only</p>
  //                   <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Montserrat" }}>
  //                     <thead>
  //                       <tr style={{ backgroundColor: "#307260", color: "#efc71d" }}>
  //                         <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Score Range</th>
  //                         <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Risk Level</th>
  //                         <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Interpretation</th>
  //                       </tr>
  //                     </thead>
  //                     <tbody>
  //                       {riskLevels.map((level, index) => (
  //                         <tr key={index}>
  //                           <td style={{ textAlign: "center", color: level.color, border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
  //                             {level.range}
  //                           </td>
  //                           <td style={{ textAlign: "center", backgroundColor: level.color, color: "#000", border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
  //                             {level.risk}
  //                           </td>
  //                           <td style={{ border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
  //                             {level.interpretation}
  //                           </td>
  //                         </tr>
  //                       ))}
  //                     </tbody>
  //                   </table>
  //                 </div>
  //               </Typography>

  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // }
  // else if (screenNumber === 16) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h6">
  //                 {module === "cmv" ? " Electrical CMV Report" : "SAVED REPORT"} : AUDIT Description
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div className="review-table-wrapper" style={{ borderRadius: "5px" }}>
  //                   <table style={{ width: "100%", fontFamily: "montserrat", borderCollapse: "collapse", borderRadius: "5px" }}>
  //                     <tbody>
  //                       <tr>
  //                         <td>Client</td>
  //                         <td>{selectedOrganization.label}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Location</td>
  //                         <td>{selectedSite.label}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Date of Site Visit</td>
  //                         <td>{new Date(startDate).getDate()}-{new Date(startDate).getMonth() + 1
  //                         }-{new Date(startDate).getFullYear()}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Study</td>
  //                         <td>Electrical Audit</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Time of Audit (From & To)</td>
  //                         {/* <td>{timeFrom.slice(0, 5)} to {timeTo.slice(0, 5)}</td> */}
  //                         <td>
  //                           {timeFrom && timeTo
  //                             ? `${timeFrom.slice(0, 5)} to ${timeTo.slice(0, 5)}`
  //                             : timeFrom
  //                               ? `${timeFrom.slice(0, 5)} to N/A`
  //                               : timeTo
  //                                 ? `N/A to ${timeTo.slice(0, 5)}`
  //                                 : "N/A"}
  //                         </td>

  //                       </tr>
  //                       <tr>
  //                         <td>Brief Property Description</td>
  //                         <td>{briefPropertyDescription}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Number of floors</td>
  //                         <td>{numOfFloors}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Average Staff Footfall</td>
  //                         <td>{avgStaffFootfall}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>No Objection Certificate</td>
  //                         <td>{noObjectionCertificate}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>National Building Code Category</td>
  //                         <td>{nationalBuildingCodeCategory}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Coordinating Person – Client Side</td>
  //                         <td>{coordinationgPersonClientside}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Report Prepared By</td>
  //                         <td>{reportPreparedBy}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Report Reviewed By</td>
  //                         <td>{reportReviewedBy}</td>
  //                       </tr>
  //                       <tr>
  //                         <td>Date of Submission of Report</td>
  //                         <td>{new Date(endDate).getDate()}-{new Date(endDate).getMonth() + 1
  //                         }-{new Date(endDate).getFullYear()}</td>
  //                       </tr>
  //                     </tbody>
  //                   </table>
  //                 </div>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // }
  // else if (screenNumber === 17) {
  //   return (
  //     <>
  //       {loading ? <Loader /> : null}
  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div
  //             className={exp ? "export-modal-content" : "review-modal-content"}
  //           >
  //             <div className="review-modal-header">
  //               <Typography variant="h5">
  //                 {exp
  //                   ? "Export Report"
  //                   : module === "cmv"
  //                     ? "HSE CMV Report"
  //                     : "Saved Report"}
  //               </Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             {!exp && (
  //               <div
  //                 className="review-modal-body"
  //                 style={{ overflowY: "auto" }}
  //               >
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">CONCLUSION</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={conclusionEditor}
  //                       placeholder="Enter your text here"
  //                       value={conclusion}
  //                       key={screenNumber} config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setConclusion(newContent)} // This updates backgroundBrief on blur
  //                       onChange={(newContent) => { debouncedSetConclusion(newContent) }} // Updates state on every change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //             )}
  //             <hr />
  //             <div className="review-modal-footer" id="conclusionFooter">
  //               {!exp && (
  //                 <>
  //                   <button
  //                     className="button-styles"
  //                     onClick={handleSave}
  //                     style={{ background: "#efc71d" }}
  //                   >
  //                     Save
  //                   </button>
  //                   <button
  //                     className="button-styles"
  //                     onClick={handlePrev}
  //                     style={{ background: "#efc71d" }}
  //                   >
  //                     &#171; Prev
  //                   </button>
  //                   {module !== "cmv" && (
  //                     <button
  //                       className="button-styles"
  //                       onClick={handleComplete}
  //                       disabled={isComplete}
  //                       style={{
  //                         background: isComplete ? "lightgrey" : "#efc71d",
  //                       }}
  //                     >
  //                       Complete
  //                     </button>
  //                   )}
  //                 </>
  //               )}
  //               <ExportSavedReportPDF
  //                 selectedOrganization={selectedOrganization}
  //                 selectedSite={selectedSite}
  //                 backgroundBrief={backgroundBrief}
  //                 contents={contents}
  //                 exeSummary={exeSummary}
  //                 criticalObservations={criticalObservations}
  //                 selectedObservations={selectedObservations}
  //                 selectedArea={selectedArea}
  //                 selectedCategory={selectedCategory}
  //                 recommendations={recommendations}
  //                 conclusion={conclusion}
  //                 selectedDateTime={selectedDateTime.split("T")[0]}
  //                 imageUrlsByRow={imageUrlsByRow}
  //                 reportType="HSE"
  //                 isSaved={exp ? true : isSaved}
  //                 chartImage={chartImage}
  //                 otherDetails={otherDetails}
  //                 ReportUID={selectedReportData.report_id}
  //                 startDate={new Date(startDate)}
  //                 endDate={new Date(endDate)}
  //                 bestPractice={bestPractice}
  //                 theWayForward={theWayForward}
  //                 name={name}
  //                 facilityInfo={facilityInfo}
  //                 introduction={introduction}
  //               />
  //               <ExportExcel
  //                 selectedOrganization={selectedOrganization}
  //                 selectedSite={selectedSite}
  //                 backgroundBrief={backgroundBrief}
  //                 contents={contents}
  //                 exeSummary={exeSummary}
  //                 criticalObservations={criticalObservations}
  //                 selectedObservations={selectedObservations}
  //                 selectedArea={selectedArea}
  //                 selectedCategory={selectedCategory}
  //                 recommendations={recommendations}
  //                 conclusion={conclusion}
  //                 selectedDateTime={selectedDateTime.split("T")[0]}
  //                 imageUrlsByRow={imageUrlsByRow}
  //                 isSaved={exp ? true : isSaved}
  //                 reportType="HSE"
  //                 otherDetails={otherDetails}
  //                 ReportUID={selectedReportData.report_id}
  //                 bestPractice={bestPractice}
  //                 theWayForward={theWayForward}
  //                 facilityInfo={facilityInfo}
  //               />
  //               <ExportWordDoc
  //                 selectedOrganization={selectedOrganization}
  //                 selectedSite={selectedSite}
  //                 backgroundBrief={backgroundBrief}
  //                 contents={contents}
  //                 exeSummary={exeSummary}
  //                 criticalObservations={criticalObservations}
  //                 selectedObservations={selectedObservations}
  //                 selectedArea={selectedArea}
  //                 selectedCategory={selectedCategory}
  //                 recommendations={recommendations}
  //                 conclusion={conclusion}
  //                 selectedDateTime={selectedDateTime.split("T")[0]}
  //                 isSaved={exp ? true : isSaved}
  //                 otherDetails={otherDetails}
  //                 reportType="HSE"
  //                 chartImage={chartImage}
  //                 ReportUID={selectedReportData.report_id}
  //                 bestPractice={bestPractice}
  //                 theWayForward={theWayForward}
  //                 startDate={new Date(startDate)}
  //                 endDate={new Date(endDate)}
  //                 name={name}
  //                 facilityInfo={facilityInfo}
  //                 introduction={introduction}
  //               />
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>
  //     </>
  //   );
  // }
};

export default HseSavedReportModal;
